// src/ui/StormcloudVideoPlayer.tsx
import React, { useEffect, useRef, useMemo, useCallback } from "react";

// src/sdk/hlsAdPlayer.ts
import Hls from "hls.js";
var MAX_VAST_WRAPPER_DEPTH = 5;
function createHlsAdPlayer(contentVideo, options) {
  let adPlaying = false;
  let originalMutedState = false;
  let originalVolume = Math.max(0, Math.min(1, contentVideo.volume || 1));
  const listeners = /* @__PURE__ */ new Map();
  const licenseKey = options?.licenseKey;
  const mainHlsInstance = options?.mainHlsInstance;
  let adVideoElement;
  let adHls;
  let adContainerEl;
  let currentAd;
  let podAds = [];
  let podIndex = 0;
  let sessionId;
  const preloadedAds = /* @__PURE__ */ new Map();
  const preloadingAds = /* @__PURE__ */ new Map();
  let destroyed = false;
  let pendingTimeouts = [];
  let trackingFired = {
    impression: false,
    start: false,
    firstQuartile: false,
    midpoint: false,
    thirdQuartile: false,
    complete: false
  };
  function emit(event, payload) {
    const set = listeners.get(event);
    if (!set) return;
    for (const fn of Array.from(set)) {
      try {
        fn(payload);
      } catch (error) {
        console.warn(
          `[HlsAdPlayer] Error in event listener for ${event}:`,
          error
        );
      }
    }
  }
  function generateSessionId() {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  function fireTrackingPixels(urls) {
    if (!urls || urls.length === 0) return;
    urls.forEach((url) => {
      try {
        let trackingUrl = url;
        if (sessionId) {
          trackingUrl = `${trackingUrl}${trackingUrl.includes("?") ? "&" : "?"}session_id=${sessionId}`;
        }
        if (licenseKey) {
          trackingUrl = `${trackingUrl}${trackingUrl.includes("?") ? "&" : "?"}license_key=${licenseKey}`;
        }
        const img = new Image(1, 1);
        img.src = trackingUrl;
        console.log(`[HlsAdPlayer] Fired tracking pixel: ${trackingUrl}`);
      } catch (error) {
        console.warn(`[HlsAdPlayer] Error firing tracking pixel:`, error);
      }
    });
  }
  function getMainStreamQuality() {
    if (!mainHlsInstance || !mainHlsInstance.levels) {
      return null;
    }
    const currentLevel = mainHlsInstance.currentLevel;
    if (currentLevel === -1 || !mainHlsInstance.levels[currentLevel]) {
      const autoLevel = mainHlsInstance.loadLevel;
      if (autoLevel !== -1 && mainHlsInstance.levels[autoLevel]) {
        const level2 = mainHlsInstance.levels[autoLevel];
        return {
          width: level2.width || 1920,
          height: level2.height || 1080,
          bitrate: level2.bitrate || 5e6
        };
      }
      return null;
    }
    const level = mainHlsInstance.levels[currentLevel];
    return {
      width: level.width || 1920,
      height: level.height || 1080,
      bitrate: level.bitrate || 5e6
    };
  }
  function selectBestMediaFile(mediaFiles) {
    if (mediaFiles.length === 0) {
      throw new Error("No media files available");
    }
    const firstFile = mediaFiles[0];
    if (!firstFile) {
      throw new Error("No media files available");
    }
    if (mediaFiles.length === 1) {
      return firstFile;
    }
    const mainQuality = getMainStreamQuality();
    if (!mainQuality) {
      console.log(
        "[HlsAdPlayer] No main stream quality info, using first media file"
      );
      return firstFile;
    }
    console.log("[HlsAdPlayer] Main stream quality:", mainQuality);
    const scoredFiles = mediaFiles.map((file) => {
      const widthDiff = Math.abs(file.width - mainQuality.width);
      const heightDiff = Math.abs(file.height - mainQuality.height);
      const resolutionDiff = widthDiff + heightDiff;
      const fileBitrate = (file.bitrate || 5e3) * 1e3;
      const bitrateDiff = Math.abs(fileBitrate - mainQuality.bitrate);
      const score = resolutionDiff * 2 + bitrateDiff / 1e3;
      return { file, score, resolutionDiff, bitrateDiff };
    });
    scoredFiles.sort((a, b) => a.score - b.score);
    const bestMatch = scoredFiles[0];
    if (!bestMatch) {
      console.log("[HlsAdPlayer] No best match found, using first media file");
      return firstFile;
    }
    console.log("[HlsAdPlayer] Selected media file:", {
      url: bestMatch.file.url,
      resolution: `${bestMatch.file.width}x${bestMatch.file.height}`,
      bitrate: bestMatch.file.bitrate,
      score: bestMatch.score,
      resolutionDiff: bestMatch.resolutionDiff,
      bitrateDiff: bestMatch.bitrateDiff
    });
    return bestMatch.file;
  }
  function isHlsMediaFile(mediaFile) {
    const type = mediaFile.type.toLowerCase();
    const url = mediaFile.url.toLowerCase();
    return type === "application/x-mpegurl" || type === "application/vnd.apple.mpegurl" || type.includes("mpegurl") || url.includes(".m3u8");
  }
  function isProgressiveMediaFile(mediaFile) {
    const type = mediaFile.type.toLowerCase();
    const url = mediaFile.url.toLowerCase();
    return type.startsWith("video/") || url.endsWith(".mp4") || url.includes(".mp4?");
  }
  function createEmptyTrackingUrls() {
    return {
      impression: [],
      start: [],
      firstQuartile: [],
      midpoint: [],
      thirdQuartile: [],
      complete: [],
      mute: [],
      unmute: [],
      pause: [],
      resume: [],
      fullscreen: [],
      exitFullscreen: [],
      skip: [],
      error: []
    };
  }
  function extractTrackingUrls(scope) {
    const trackingUrls = createEmptyTrackingUrls();
    scope.querySelectorAll("Impression").forEach((el) => {
      const url = el.textContent?.trim();
      if (url) trackingUrls.impression.push(url);
    });
    scope.querySelectorAll("Tracking").forEach((el) => {
      const event = el.getAttribute("event");
      const url = el.textContent?.trim();
      if (event && url) {
        const eventKey = event;
        if (trackingUrls[eventKey]) {
          trackingUrls[eventKey].push(url);
        }
      }
    });
    scope.querySelectorAll("Error").forEach((el) => {
      const url = el.textContent?.trim();
      if (url) trackingUrls.error.push(url);
    });
    return trackingUrls;
  }
  function mergeTrackingUrls(target, source) {
    Object.keys(target).forEach((key) => {
      if (source[key] && source[key].length > 0) {
        target[key] = target[key].concat(source[key]);
      }
    });
    return target;
  }
  function parseVastXml(xmlString) {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, "text/xml");
      const parserError = xmlDoc.querySelector("parsererror");
      if (parserError) {
        console.error(
          "[HlsAdPlayer] XML parsing error (malformed VAST XML):",
          parserError.textContent
        );
        return { type: "empty" };
      }
      const adElement = xmlDoc.querySelector("Ad");
      if (!adElement) {
        console.warn("[HlsAdPlayer] No Ad element found in VAST XML");
        return { type: "empty" };
      }
      const adId = adElement.getAttribute("id") || "unknown";
      const title = xmlDoc.querySelector("AdTitle")?.textContent || "Ad";
      const clickThrough = xmlDoc.querySelector("ClickThrough")?.textContent?.trim();
      const wrapperUri = xmlDoc.querySelector("VASTAdTagURI")?.textContent?.trim();
      if (wrapperUri) {
        console.log(
          `[HlsAdPlayer] VAST wrapper detected, following VASTAdTagURI: ${wrapperUri}`
        );
        return {
          type: "wrapper",
          vastAdTagUri: wrapperUri,
          trackingUrls: extractTrackingUrls(xmlDoc),
          clickThrough
        };
      }
      const isNoAdAvailable = adId === "empty" || title.toLowerCase().includes("no ad available") || title.toLowerCase() === "no ad available";
      const durationText = xmlDoc.querySelector("Duration")?.textContent || "00:00:30";
      const durationParts = durationText.split(":");
      const duration = parseInt(durationParts[0] || "0", 10) * 3600 + parseInt(durationParts[1] || "0", 10) * 60 + parseInt(durationParts[2] || "0", 10);
      const mediaFileElements = xmlDoc.querySelectorAll("MediaFile");
      const mediaFiles = [];
      console.log(
        `[HlsAdPlayer] Found ${mediaFileElements.length} MediaFile element(s) in VAST XML`
      );
      mediaFileElements.forEach((mf, index) => {
        const type = mf.getAttribute("type") || "";
        const url = mf.textContent?.trim() || "";
        const width = mf.getAttribute("width") || "";
        const height = mf.getAttribute("height") || "";
        console.log(
          `[HlsAdPlayer] MediaFile ${index}: type="${type}", url="${url}", width="${width}", height="${height}"`
        );
        const mediaFile = {
          url,
          type,
          width: parseInt(width || "1920", 10),
          height: parseInt(height || "1080", 10),
          bitrate: void 0
        };
        if (!url) {
          console.warn(`[HlsAdPlayer] MediaFile ${index} has empty URL`);
          return;
        }
        const bitrateAttr = mf.getAttribute("bitrate");
        const bitrateValue = bitrateAttr ? parseInt(bitrateAttr, 10) : void 0;
        mediaFile.bitrate = bitrateValue && bitrateValue > 0 ? bitrateValue : void 0;
        if (isHlsMediaFile(mediaFile) || isProgressiveMediaFile(mediaFile)) {
          mediaFiles.push(mediaFile);
          console.log(
            `[HlsAdPlayer] Added ${isHlsMediaFile(mediaFile) ? "HLS" : "progressive"} MediaFile: ${url}`
          );
        } else {
          console.log(
            `[HlsAdPlayer] MediaFile ${index} ignored (type="${type}" is not supported)`
          );
        }
      });
      if (mediaFiles.length === 0) {
        if (isNoAdAvailable) {
          console.warn(
            "[HlsAdPlayer] No ads available (VAST response indicates no ads)"
          );
        } else {
          console.warn("[HlsAdPlayer] No supported media files found in VAST XML");
        }
        return { type: "empty" };
      }
      return {
        type: "inline",
        ad: {
          id: adId,
          title,
          duration,
          mediaFiles,
          trackingUrls: extractTrackingUrls(xmlDoc),
          clickThrough
        }
      };
    } catch (error) {
      console.error("[HlsAdPlayer] Error parsing VAST XML:", error);
      return { type: "empty" };
    }
  }
  function parseAdElement(adElement) {
    try {
      const adId = adElement.getAttribute("id") || "unknown";
      const title = adElement.querySelector("AdTitle")?.textContent || "Ad";
      const clickThrough = adElement.querySelector("ClickThrough")?.textContent?.trim();
      const wrapperUri = adElement.querySelector("VASTAdTagURI")?.textContent?.trim();
      if (wrapperUri) {
        return {
          type: "wrapper",
          vastAdTagUri: wrapperUri,
          trackingUrls: extractTrackingUrls(adElement),
          clickThrough
        };
      }
      const isNoAdAvailable = adId === "empty" || title.toLowerCase().includes("no ad available") || title.toLowerCase() === "no ad available";
      const durationText = adElement.querySelector("Duration")?.textContent || "00:00:30";
      const durationParts = durationText.split(":");
      const duration = parseInt(durationParts[0] || "0", 10) * 3600 + parseInt(durationParts[1] || "0", 10) * 60 + parseInt(durationParts[2] || "0", 10);
      const mediaFileElements = adElement.querySelectorAll("MediaFile");
      const mediaFiles = [];
      mediaFileElements.forEach((mf) => {
        const type = mf.getAttribute("type") || "";
        const url = mf.textContent?.trim() || "";
        const width = mf.getAttribute("width") || "";
        const height = mf.getAttribute("height") || "";
        if (!url) return;
        const bitrateAttr = mf.getAttribute("bitrate");
        const bitrateValue = bitrateAttr ? parseInt(bitrateAttr, 10) : void 0;
        const mediaFile = {
          url,
          type,
          width: parseInt(width || "1920", 10),
          height: parseInt(height || "1080", 10),
          bitrate: bitrateValue && bitrateValue > 0 ? bitrateValue : void 0
        };
        if (isHlsMediaFile(mediaFile) || isProgressiveMediaFile(mediaFile)) {
          mediaFiles.push(mediaFile);
        }
      });
      if (mediaFiles.length === 0) {
        if (isNoAdAvailable) {
          console.warn(
            "[HlsAdPlayer] Pod <Ad> indicates no ad available"
          );
        } else {
          console.warn("[HlsAdPlayer] No supported media files in pod <Ad>");
        }
        return { type: "empty" };
      }
      return {
        type: "inline",
        ad: {
          id: adId,
          title,
          duration,
          mediaFiles,
          trackingUrls: extractTrackingUrls(adElement),
          clickThrough
        }
      };
    } catch (error) {
      console.error("[HlsAdPlayer] Error parsing pod <Ad> element:", error);
      return { type: "empty" };
    }
  }
  async function fetchVastXml(vastTagUrl) {
    const response = await fetch(vastTagUrl, {
      mode: "cors",
      credentials: "include",
      headers: {
        "Accept": "application/xml, text/xml, */*"
      },
      referrerPolicy: "no-referrer-when-downgrade"
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch VAST: ${response.statusText}`);
    }
    return response.text();
  }
  async function fetchAndParseVastAd(vastTagUrl, depth = 0, accumulatedTracking = createEmptyTrackingUrls()) {
    const vastXml = await fetchVastXml(vastTagUrl);
    console.log("[HlsAdPlayer] VAST XML received");
    console.log(
      "[HlsAdPlayer] VAST XML content (first 2000 chars):",
      vastXml.substring(0, 2e3)
    );
    const parsed = parseVastXml(vastXml);
    if (parsed.type === "empty") {
      return null;
    }
    if (parsed.type === "wrapper") {
      if (depth >= MAX_VAST_WRAPPER_DEPTH) {
        console.warn(
          `[HlsAdPlayer] VAST wrapper depth limit (${MAX_VAST_WRAPPER_DEPTH}) reached, aborting redirect chain`
        );
        return null;
      }
      mergeTrackingUrls(accumulatedTracking, parsed.trackingUrls);
      return fetchAndParseVastAd(
        parsed.vastAdTagUri,
        depth + 1,
        accumulatedTracking
      );
    }
    mergeTrackingUrls(parsed.ad.trackingUrls, accumulatedTracking);
    return parsed.ad;
  }
  async function fetchAndParsePod(vastTagUrl) {
    const vastXml = await fetchVastXml(vastTagUrl);
    console.log("[HlsAdPlayer] Pod VAST XML received");
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(vastXml, "text/xml");
    if (xmlDoc.querySelector("parsererror")) {
      console.error("[HlsAdPlayer] Pod VAST XML parsing error (malformed)");
      return [];
    }
    const adElements = Array.from(xmlDoc.querySelectorAll("Ad"));
    if (adElements.length === 0) {
      console.warn("[HlsAdPlayer] No <Ad> elements found in pod VAST response");
      return [];
    }
    adElements.sort((a, b) => {
      const aSeq = parseInt(a.getAttribute("sequence") || "", 10);
      const bSeq = parseInt(b.getAttribute("sequence") || "", 10);
      const aVal = Number.isFinite(aSeq) ? aSeq : Number.MAX_SAFE_INTEGER;
      const bVal = Number.isFinite(bSeq) ? bSeq : Number.MAX_SAFE_INTEGER;
      return aVal - bVal;
    });
    const ads = [];
    for (const adElement of adElements) {
      const parsed = parseAdElement(adElement);
      if (parsed.type === "inline") {
        ads.push(parsed.ad);
      } else if (parsed.type === "wrapper") {
        try {
          const resolved = await fetchAndParseVastAd(
            parsed.vastAdTagUri,
            1,
            parsed.trackingUrls
          );
          if (resolved) {
            ads.push(resolved);
          }
        } catch (error) {
          console.warn(
            "[HlsAdPlayer] Failed to resolve wrapper <Ad> in pod:",
            error
          );
        }
      }
    }
    console.log(`[HlsAdPlayer] Parsed ${ads.length} ad(s) from pod VAST response`);
    return ads;
  }
  function createAdVideoElement() {
    const video = document.createElement("video");
    video.style.position = "absolute";
    video.style.left = "0";
    video.style.top = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "contain";
    video.style.backgroundColor = "#000";
    video.playsInline = true;
    video.muted = false;
    video.volume = 1;
    console.log(
      `[HlsAdPlayer] Created ad video element with volume ${video.volume}`
    );
    return video;
  }
  function setupAdEventListeners() {
    if (!adVideoElement || !currentAd) return;
    adVideoElement.addEventListener("timeupdate", () => {
      if (!currentAd || !adVideoElement) return;
      const progress = adVideoElement.currentTime / currentAd.duration;
      if (progress >= 0.25 && !trackingFired.firstQuartile) {
        trackingFired.firstQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.firstQuartile);
      }
      if (progress >= 0.5 && !trackingFired.midpoint) {
        trackingFired.midpoint = true;
        fireTrackingPixels(currentAd.trackingUrls.midpoint);
      }
      if (progress >= 0.75 && !trackingFired.thirdQuartile) {
        trackingFired.thirdQuartile = true;
        fireTrackingPixels(currentAd.trackingUrls.thirdQuartile);
      }
    });
    adVideoElement.addEventListener("playing", () => {
      if (!currentAd || trackingFired.start) return;
      trackingFired.start = true;
      fireTrackingPixels(currentAd.trackingUrls.start);
      console.log("[HlsAdPlayer] Ad started playing");
    });
    adVideoElement.addEventListener("ended", () => {
      if (!currentAd || trackingFired.complete) return;
      trackingFired.complete = true;
      fireTrackingPixels(currentAd.trackingUrls.complete);
      console.log("[HlsAdPlayer] Ad completed");
      if (podIndex < podAds.length - 1 && advanceToNextPodAd()) {
        return;
      }
      handleAdComplete();
    });
    adVideoElement.addEventListener("error", (e) => {
      if (!adPlaying) return;
      console.error("[HlsAdPlayer] Ad video error:", e);
      if (currentAd) {
        fireTrackingPixels(currentAd.trackingUrls.error);
      }
      if (podIndex < podAds.length - 1 && advanceToNextPodAd()) {
        return;
      }
      handleAdError();
    });
    adVideoElement.addEventListener("volumechange", () => {
      if (!currentAd) return;
      if (adVideoElement.muted) {
        fireTrackingPixels(currentAd.trackingUrls.mute);
      } else {
        fireTrackingPixels(currentAd.trackingUrls.unmute);
      }
    });
    adVideoElement.addEventListener("pause", () => {
      if (currentAd && !adVideoElement.ended) {
        fireTrackingPixels(currentAd.trackingUrls.pause);
      }
    });
    adVideoElement.addEventListener("play", () => {
      if (currentAd && adVideoElement.currentTime > 0) {
        fireTrackingPixels(currentAd.trackingUrls.resume);
      }
    });
  }
  function setAdPlayingFlag(isPlaying) {
    if (isPlaying) {
      contentVideo.dataset.stormcloudAdPlaying = "true";
    } else {
      delete contentVideo.dataset.stormcloudAdPlaying;
    }
  }
  function resetQuartileTracking(preserveImpression) {
    trackingFired = {
      impression: preserveImpression ? trackingFired.impression : false,
      start: false,
      firstQuartile: false,
      midpoint: false,
      thirdQuartile: false,
      complete: false
    };
  }
  function loadCurrentAdMedia() {
    if (!adVideoElement || !currentAd) {
      throw new Error("No ad video element or ad to load");
    }
    const mediaFile = selectBestMediaFile(currentAd.mediaFiles);
    if (!mediaFile) {
      throw new Error("No media file available for ad");
    }
    const isHlsAd = isHlsMediaFile(mediaFile);
    console.log(
      `[HlsAdPlayer] Loading ${isHlsAd ? "HLS" : "progressive"} ad (pod ${podIndex + 1}/${podAds.length || 1}) from: ${mediaFile.url}`
    );
    if (isHlsAd && Hls.isSupported()) {
      if (adHls) {
        adHls.destroy();
      }
      adHls = new Hls({
        enableWorker: true,
        lowLatencyMode: false
      });
      adHls.loadSource(mediaFile.url);
      adHls.attachMedia(adVideoElement);
      adHls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("[HlsAdPlayer] HLS manifest parsed, starting playback");
        adVideoElement.play().catch((error) => {
          console.error("[HlsAdPlayer] Error starting ad playback:", error);
          handleAdError();
        });
      });
      adHls.on(Hls.Events.ERROR, (_event, data) => {
        console.error("[HlsAdPlayer] HLS error:", data);
        if (data.fatal) {
          handleAdError();
        }
      });
    } else if (isHlsAd && adVideoElement.canPlayType("application/vnd.apple.mpegurl")) {
      adVideoElement.src = mediaFile.url;
      adVideoElement.play().catch((error) => {
        console.error("[HlsAdPlayer] Error starting ad playback:", error);
        handleAdError();
      });
    } else if (!isHlsAd && isProgressiveMediaFile(mediaFile)) {
      if (adHls) {
        adHls.destroy();
        adHls = void 0;
      }
      adVideoElement.src = mediaFile.url;
      adVideoElement.load();
      adVideoElement.play().catch((error) => {
        console.error("[HlsAdPlayer] Error starting progressive ad playback:", error);
        handleAdError();
      });
    } else {
      throw new Error(`Unsupported ad media file type: ${mediaFile.type}`);
    }
  }
  function advanceToNextPodAd() {
    if (podIndex >= podAds.length - 1) {
      return false;
    }
    podIndex++;
    currentAd = podAds[podIndex];
    resetQuartileTracking(false);
    if (currentAd) {
      fireTrackingPixels(currentAd.trackingUrls.impression);
      trackingFired.impression = true;
      console.log(
        `[HlsAdPlayer] Advancing to next pod ad (${podIndex + 1}/${podAds.length}): ${currentAd.title}, duration: ${currentAd.duration}s`
      );
    }
    try {
      loadCurrentAdMedia();
      emit("pod_ad_started", { index: podIndex, total: podAds.length });
      return true;
    } catch (error) {
      console.error("[HlsAdPlayer] Failed to load next pod ad:", error);
      return false;
    }
  }
  function handleAdComplete() {
    console.log("[HlsAdPlayer] Handling ad completion");
    adPlaying = false;
    setAdPlayingFlag(false);
    contentVideo.muted = true;
    if (adContainerEl) {
      adContainerEl.style.display = "none";
      adContainerEl.style.pointerEvents = "none";
    }
    if (options?.continueLiveStreamDuringAds) {
      if (contentVideo.paused) {
        console.log("[HlsAdPlayer] Content video paused in live mode, resuming playback");
        contentVideo.play().catch(() => {
        });
      } else {
        console.log("[HlsAdPlayer] Content video already playing in live mode");
      }
    }
    emit("content_resume");
  }
  function handleAdError() {
    console.log("[HlsAdPlayer] Handling ad error");
    adPlaying = false;
    setAdPlayingFlag(false);
    contentVideo.muted = true;
    if (adContainerEl) {
      adContainerEl.style.display = "none";
      adContainerEl.style.pointerEvents = "none";
    }
    emit("ad_error");
  }
  return {
    initialize() {
      console.log("[HlsAdPlayer] Initializing");
      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "30";
        container.style.backgroundColor = "#000";
        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }
    },
    async requestAds(vastTagUrl) {
      console.log("[HlsAdPlayer] Requesting ads:", vastTagUrl);
      if (adPlaying) {
        console.warn(
          "[HlsAdPlayer] Cannot request new ads while an ad is playing"
        );
        return Promise.reject(new Error("Ad already playing"));
      }
      try {
        sessionId = generateSessionId();
        let ads = [];
        if (preloadedAds.has(vastTagUrl)) {
          const preloaded = preloadedAds.get(vastTagUrl);
          preloadedAds.delete(vastTagUrl);
          if (preloaded) {
            ads = [preloaded];
          }
          console.log(
            "[HlsAdPlayer] Using preloaded VAST response:",
            vastTagUrl
          );
        } else {
          ads = await fetchAndParsePod(vastTagUrl);
        }
        if (!ads.length) {
          console.warn("[HlsAdPlayer] No ads available from VAST response");
          emit("ad_error");
          return Promise.reject(new Error("No ads available from VAST response"));
        }
        podAds = ads;
        podIndex = 0;
        currentAd = ads[0];
        console.log(
          `[HlsAdPlayer] Pod parsed: ${ads.length} ad(s), first="${currentAd?.title}", duration=${currentAd?.duration}s`
        );
        if (currentAd) {
          fireTrackingPixels(currentAd.trackingUrls.impression);
          trackingFired.impression = true;
        }
        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error requesting ads:", error);
        emit("ad_error");
        return Promise.reject(error);
      }
    },
    async play() {
      if (!currentAd) {
        console.warn(
          "[HlsAdPlayer] Cannot play: No ad loaded (no ads available)"
        );
        return Promise.reject(new Error("No ad loaded"));
      }
      console.log("[HlsAdPlayer] Starting ad playback");
      try {
        if (!adVideoElement) {
          adVideoElement = createAdVideoElement();
          adContainerEl?.appendChild(adVideoElement);
          setupAdEventListeners();
        }
        resetQuartileTracking(true);
        const contentVolume = contentVideo.volume;
        originalVolume = Math.max(
          0,
          Math.min(1, contentVolume || originalVolume)
        );
        if (!options?.continueLiveStreamDuringAds) {
          contentVideo.pause();
          console.log("[HlsAdPlayer] Content paused (VOD mode)");
        } else {
          console.log("[HlsAdPlayer] Content continues (Live mode)");
        }
        console.log("[HlsAdPlayer] FORCE MUTING main video");
        contentVideo.muted = true;
        adPlaying = true;
        setAdPlayingFlag(true);
        if (adVideoElement) {
          adVideoElement.volume = Math.max(0, Math.min(1, originalVolume));
          adVideoElement.muted = originalMutedState;
          console.log(
            `[HlsAdPlayer] Set ad video volume to ${adVideoElement.volume}, muted: ${adVideoElement.muted}, originalMutedState: ${originalMutedState}, contentVolume: ${contentVolume}`
          );
        }
        if (adContainerEl) {
          adContainerEl.style.display = "flex";
          adContainerEl.style.pointerEvents = "auto";
        }
        emit("content_pause");
        loadCurrentAdMedia();
        return Promise.resolve();
      } catch (error) {
        console.error("[HlsAdPlayer] Error playing ad:", error);
        handleAdError();
        return Promise.reject(error);
      }
    },
    pause() {
      if (!adPlaying || !adVideoElement) {
        return;
      }
      try {
        if (adVideoElement && !adVideoElement.paused) {
          adVideoElement.pause();
        }
      } catch (error) {
        console.warn("[HlsAdPlayer] Error pausing ad:", error);
      }
    },
    resume() {
      if (!adPlaying || !adVideoElement) {
        return;
      }
      try {
        if (adVideoElement && adVideoElement.paused) {
          adVideoElement.play().catch(() => {
          });
        }
      } catch (error) {
        console.warn("[HlsAdPlayer] Error resuming ad:", error);
      }
    },
    async stop() {
      console.log("[HlsAdPlayer] Stopping ad");
      adPlaying = false;
      setAdPlayingFlag(false);
      const previousMutedState = contentVideo.muted;
      contentVideo.muted = originalMutedState;
      contentVideo.volume = Math.max(0, Math.min(1, originalVolume));
      console.log(
        `[HlsAdPlayer] Restored mute state on stop: ${previousMutedState} -> ${originalMutedState}, volume: ${contentVideo.volume}`
      );
      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }
      contentVideo.style.visibility = "visible";
      contentVideo.style.opacity = "1";
      if (options?.continueLiveStreamDuringAds) {
        if (contentVideo.paused) {
          console.log("[HlsAdPlayer] Content video paused in live mode, resuming playback on stop");
          contentVideo.play().catch(() => {
          });
        }
      }
      if (adHls) {
        adHls.destroy();
        adHls = void 0;
      }
      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.src = "";
      }
      currentAd = void 0;
      podAds = [];
      podIndex = 0;
    },
    destroy() {
      console.log("[HlsAdPlayer] Destroying");
      destroyed = true;
      for (const timeoutId of pendingTimeouts) {
        clearTimeout(timeoutId);
      }
      pendingTimeouts = [];
      adPlaying = false;
      setAdPlayingFlag(false);
      contentVideo.muted = originalMutedState;
      contentVideo.volume = Math.max(0, Math.min(1, originalVolume));
      if (adHls) {
        adHls.destroy();
        adHls = void 0;
      }
      if (adVideoElement) {
        adVideoElement.pause();
        adVideoElement.src = "";
        adVideoElement.remove();
        adVideoElement = void 0;
      }
      if (adContainerEl?.parentElement) {
        adContainerEl.parentElement.removeChild(adContainerEl);
      }
      adContainerEl = void 0;
      currentAd = void 0;
      podAds = [];
      podIndex = 0;
      listeners.clear();
      preloadedAds.clear();
      preloadingAds.clear();
    },
    isAdPlaying() {
      return adPlaying;
    },
    resize(width, height) {
      console.log(`[HlsAdPlayer] Resizing to ${width}x${height}`);
      if (adContainerEl) {
        adContainerEl.style.width = `${width}px`;
        adContainerEl.style.height = `${height}px`;
      }
      if (adVideoElement) {
        adVideoElement.style.width = `${width}px`;
        adVideoElement.style.height = `${height}px`;
      }
    },
    on(event, listener) {
      if (!listeners.has(event)) listeners.set(event, /* @__PURE__ */ new Set());
      listeners.get(event).add(listener);
    },
    off(event, listener) {
      listeners.get(event)?.delete(listener);
    },
    updateOriginalMutedState(muted, volume) {
      const nextVolume = typeof volume === "number" && !Number.isNaN(volume) ? Math.max(0, Math.min(1, volume)) : originalVolume;
      console.log(
        `[HlsAdPlayer] updateOriginalMutedState called: { muted: ${originalMutedState} -> ${muted}, volume: ${originalVolume} -> ${nextVolume} }`
      );
      originalMutedState = muted;
      originalVolume = nextVolume;
    },
    getOriginalMutedState() {
      return originalMutedState;
    },
    getOriginalVolume() {
      return originalVolume;
    },
    setAdVolume(volume) {
      if (adVideoElement && adPlaying) {
        const clamped = Math.max(0, Math.min(1, volume));
        adVideoElement.volume = clamped;
        adVideoElement.muted = clamped === 0;
      }
    },
    getAdVolume() {
      if (adVideoElement && adPlaying) {
        return adVideoElement.volume;
      }
      return 1;
    },
    showPlaceholder() {
      contentVideo.style.opacity = "0";
      contentVideo.style.visibility = "hidden";
      if (!adContainerEl) {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "0";
        container.style.top = "0";
        container.style.right = "0";
        container.style.bottom = "0";
        container.style.display = "none";
        container.style.alignItems = "center";
        container.style.justifyContent = "center";
        container.style.pointerEvents = "none";
        container.style.zIndex = "10";
        container.style.backgroundColor = "#000";
        contentVideo.parentElement?.appendChild(container);
        adContainerEl = container;
      }
      if (adContainerEl) {
        adContainerEl.style.display = "flex";
        adContainerEl.style.pointerEvents = "auto";
      }
    },
    hidePlaceholder() {
      if (adContainerEl) {
        adContainerEl.style.display = "none";
        adContainerEl.style.pointerEvents = "none";
      }
      if (!adPlaying) {
        contentVideo.style.visibility = "visible";
        contentVideo.style.opacity = "1";
      }
    },
    getLoadedAdDuration() {
      return currentAd?.duration;
    },
    getLoadedAdId() {
      return currentAd?.id;
    },
    getPodAdCount() {
      return podAds.length;
    },
    getPodTotalDurationMs() {
      return podAds.reduce(
        (total, ad) => total + (ad.duration > 0 ? ad.duration * 1e3 : 0),
        0
      );
    }
  };
}

// src/sdk/pal.ts
var PAL_SDK_URL = "https://imasdk.googleapis.com/pal/sdkloader/pal.js";
var _nonceLoader;
var _palLoadPromise;
function loadPalSdk() {
  if (typeof window !== "undefined" && window.goog?.pal) {
    return Promise.resolve();
  }
  if (_palLoadPromise) return _palLoadPromise;
  _palLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-pal="true"]'
    );
    if (existing) {
      if (window.goog?.pal) {
        resolve();
        return;
      }
      const timeout = setTimeout(
        () => reject(new Error("PAL SDK load timeout")),
        1e4
      );
      existing.addEventListener("load", () => {
        clearTimeout(timeout);
        resolve();
      });
      existing.addEventListener("error", () => {
        clearTimeout(timeout);
        reject(new Error("PAL SDK load failed"));
      });
      return;
    }
    const script = document.createElement("script");
    script.src = PAL_SDK_URL;
    script.async = true;
    script.setAttribute("data-pal", "true");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("PAL SDK load failed"));
    document.head.appendChild(script);
  });
  return _palLoadPromise;
}
function getOrCreateNonceLoader() {
  if (_nonceLoader) return _nonceLoader;
  const goog = window.goog;
  const consentSettings = new goog.pal.ConsentSettings();
  consentSettings.allowStorage = true;
  _nonceLoader = new goog.pal.NonceLoader(consentSettings);
  return _nonceLoader;
}
function createPalNonceManager() {
  let currentNonce = "";
  let currentNonceManager;
  return {
    async generateNonce(options = {}) {
      try {
        await loadPalSdk();
        const loader = getOrCreateNonceLoader();
        const goog = window.goog;
        const request = new goog.pal.NonceRequest();
        request.adWillAutoPlay = options.adWillAutoPlay ?? true;
        request.adWillPlayMuted = options.adWillPlayMuted ?? false;
        request.playerVolume = options.playerVolume ?? (request.adWillPlayMuted ? 0 : 1);
        request.continuousPlayback = options.continuousPlayback ?? true;
        request.descriptionUrl = options.descriptionUrl || (typeof window !== "undefined" ? window.location.href : "");
        request.iconsSupported = true;
        request.playerType = "StormcloudVideoPlayer";
        request.playerVersion = "1.0";
        request.supportedApiFrameworks = "2,7,9";
        request.url = options.descriptionUrl || (typeof window !== "undefined" ? window.location.href : "");
        request.videoWidth = options.videoWidth || 640;
        request.videoHeight = options.videoHeight || 480;
        if (options.ppid) request.ppid = options.ppid;
        if (options.sessionId) request.sessionId = options.sessionId;
        const manager = await loader.loadNonceManager(request);
        currentNonceManager = manager;
        currentNonce = manager.getNonce();
        console.log("[PAL] Nonce generated successfully");
        return currentNonce;
      } catch (error) {
        console.warn("[PAL] Failed to generate nonce:", error);
        currentNonce = "";
        currentNonceManager = void 0;
        return "";
      }
    },
    getCurrentNonce() {
      return currentNonce;
    },
    injectNonce(vastTagUrl) {
      if (!currentNonce || !vastTagUrl) return vastTagUrl;
      try {
        const url = new URL(vastTagUrl);
        url.searchParams.delete("paln");
        url.searchParams.set("givn", currentNonce);
        return url.toString();
      } catch {
        let base = vastTagUrl.replace(/([?&])paln=[^&]*/g, "").replace(/[?&]$/, "");
        const sep = base.includes("?") ? "&" : "?";
        return `${base}${sep}givn=${encodeURIComponent(currentNonce)}`;
      }
    },
    sendPlaybackStart() {
      try {
        currentNonceManager?.sendPlaybackStart();
      } catch {
      }
    },
    sendPlaybackEnd() {
      try {
        currentNonceManager?.sendPlaybackEnd();
      } catch {
      }
    },
    sendAdClick() {
      try {
        currentNonceManager?.sendAdClick();
      } catch {
      }
    },
    sendAdTouch(event) {
      try {
        currentNonceManager?.sendAdTouch(event);
      } catch {
      }
    },
    reset() {
      currentNonce = "";
      currentNonceManager = void 0;
    }
  };
}

// src/utils/mqttConfig.ts
var MQTT_CA_CERT_FILE = "src/certs/emqxsl-ca.crt";
var DEFAULT_MQTT_CONFIG = {
  enabled: true,
  brokerAddress: "vecbae77.ala.us-east-1.emqxsl.com",
  brokerPort: 8883,
  wsPort: 8084,
  username: "for-sonifi",
  password: "sonifi-mqtt",
  topicPrefix: "adstorm/players",
  qos: 1
};
var mqttConfig = { ...DEFAULT_MQTT_CONFIG };
function applyMQTTConfig(overrides) {
  Object.assign(mqttConfig, overrides);
}
function isMQTTEnabled() {
  return mqttConfig.enabled;
}
function buildMQTTBrokerUrl() {
  if (mqttConfig.brokerUrl) return mqttConfig.brokerUrl;
  return `wss://${mqttConfig.brokerAddress}:${mqttConfig.wsPort}/mqtt`;
}
function buildPlayerTopic(licenseKey, channel) {
  return `${mqttConfig.topicPrefix}/${licenseKey}/${channel}`;
}

// src/utils/mqttClient.ts
import mqtt from "mqtt";
var LOG = "[StormcloudVideoPlayer][MQTT]";
var client = null;
var status = "disconnected";
function getMQTTStatus() {
  return status;
}
function isMQTTConnected() {
  return status === "connected" && client !== null && client.connected;
}
function isMQTTConfigured() {
  return isMQTTEnabled();
}
function configureMQTT(overrides) {
  applyMQTTConfig(overrides);
  disconnectMQTT();
}
function initMQTTClient() {
  if (client || !isMQTTEnabled()) return;
  const url = buildMQTTBrokerUrl();
  status = "connecting";
  const clientId = `stormcloud-vp-${Math.random().toString(36).slice(2, 9)}`;
  try {
    client = mqtt.connect(url, {
      clientId,
      username: mqttConfig.username,
      password: mqttConfig.password,
      keepalive: 60,
      clean: true,
      reconnectPeriod: 5e3,
      connectTimeout: 1e4,
      queueQoSZero: false
    });
  } catch (err) {
    status = "error";
    console.warn(`${LOG} connect() threw:`, err);
    return;
  }
  client.on("connect", () => {
    status = "connected";
    console.info(`${LOG} connected to ${url}`);
  });
  client.on("reconnect", () => {
    status = "connecting";
    console.info(`${LOG} reconnecting\u2026`);
  });
  client.on("offline", () => {
    status = "disconnected";
    console.warn(`${LOG} offline`);
  });
  client.on("error", (err) => {
    status = "error";
    console.warn(`${LOG} error:`, err.message);
  });
  client.on("close", () => {
    if (status === "connected") {
      status = "disconnected";
    }
  });
}
function ensureMQTTClient() {
  if (isMQTTEnabled() && !client) {
    initMQTTClient();
  }
}
function publishMQTT(topic, payload) {
  if (!isMQTTEnabled()) {
    return false;
  }
  ensureMQTTClient();
  if (!client) {
    return false;
  }
  try {
    client.publish(topic, JSON.stringify(payload), { qos: mqttConfig.qos });
    return true;
  } catch (err) {
    console.warn(`${LOG} publish failed on ${topic}:`, err);
    return false;
  }
}
function disconnectMQTT() {
  if (client) {
    client.end(true);
    client = null;
    status = "disconnected";
  }
}

// src/utils/vastEnvironmentSignals.ts
var AIRY_ANDROID_APP_ID = "com.freeairytv.android";
var AIRY_WEBOS_APP_ID = "com.freeairytv.webos";
var AIRY_TIZEN_APP_ID = "com.freeairytv.samsung";
var AIRY_ROKU_APP_ID = "com.freeairytv.roku";
var AIRY_APP_NAME = "AiryTV Movies & TV";
var AIRY_DEFAULT_CONTENT_URL = "https://live.airy.tv";
var CTV_SESSION_STORAGE_KEY = "stormcloud.ctv.sid";
var PLAYER_ID_STORAGE_KEY = "stormcloud.player.id";
var LG_TEST_APP_ID = "com.hulu.plus";
var LG_TEST_APP_NAME = "Hulu";
var LG_TEST_CONTENT_URL = "https://www.hulu.com/live-tv";
var _lgTestDeviceId;
function getLgTestDeviceId() {
  if (!_lgTestDeviceId) _lgTestDeviceId = createUuid();
  return _lgTestDeviceId;
}
function isLgWebOS() {
  if (typeof navigator === "undefined") return false;
  return /Web0S|webOS|LG Browser|LGSTB|LGE/i.test(navigator.userAgent);
}
function getDefaultAppId() {
  if (typeof navigator === "undefined") return AIRY_ANDROID_APP_ID;
  const ua = navigator.userAgent;
  if (/Web0S|webOS|LG Browser|LGSTB|LGE/i.test(ua)) return AIRY_WEBOS_APP_ID;
  if (/Tizen|Samsung/i.test(ua)) return AIRY_TIZEN_APP_ID;
  if (/Roku/i.test(ua)) return AIRY_ROKU_APP_ID;
  return AIRY_ANDROID_APP_ID;
}
var DEVICE_ID_STORAGE_KEYS = [
  "rdid",
  "ifa",
  "advertisingId",
  "advertising_id",
  "deviceAdvertisingId",
  "aaid",
  "adid",
  "rida",
  "tifa",
  "lgudid"
];
var ANDROID_ID_TYPES = /* @__PURE__ */ new Set(["aaid", "adid"]);
function resolveVastEnvironmentSignals(isCtv) {
  const bridgeSignals = readNativeBridgeSignals();
  const platformDevice = readPlatformNativeDeviceId();
  const lg = isCtv && isLgWebOS();
  const deviceId = bridgeSignals.deviceId || readStoredDeviceId() || platformDevice.deviceId || (lg && platformDevice.limitAdTracking !== true ? getLgTestDeviceId() : void 0);
  const deviceIdType = bridgeSignals.deviceIdType || readStoredString("deviceIdType") || readStoredString("idtype") || platformDevice.deviceIdType || inferDeviceIdType() || (lg ? "lgudid" : void 0);
  const platformLat = bridgeSignals.deviceId ? bridgeSignals.limitAdTracking : platformDevice.limitAdTracking;
  const limitAdTracking = deviceId != null ? platformLat ?? readStoredLimitAdTracking() ?? false : void 0;
  if (isCtv) {
    return {
      isCtv: true,
      contentUrl: bridgeSignals.contentUrl || readStoredString("contentUrl") || (lg ? LG_TEST_CONTENT_URL : AIRY_DEFAULT_CONTENT_URL),
      appId: bridgeSignals.appId || readStoredString("appId") || readStoredString("msid") || (lg ? LG_TEST_APP_ID : getDefaultAppId()),
      appName: bridgeSignals.appName || readStoredString("appName") || readStoredString("an") || (lg ? LG_TEST_APP_NAME : AIRY_APP_NAME),
      sessionId: getOrCreateCtvSessionId(),
      ...deviceId ? { deviceId } : {},
      ...deviceIdType ? { deviceIdType } : {},
      ...limitAdTracking != null ? { limitAdTracking } : {},
      deviceTypeHint: 5
    };
  }
  return {
    isCtv: false,
    ...deviceId ? { deviceId } : {},
    ...deviceIdType ? { deviceIdType } : {},
    ...limitAdTracking != null ? { limitAdTracking } : {}
  };
}
function inferDeviceIdType() {
  if (typeof navigator === "undefined") {
    return void 0;
  }
  const ua = navigator.userAgent;
  if (/Roku/i.test(ua)) return "rida";
  if (/Tizen|Samsung/i.test(ua)) return "tifa";
  if (/AppleTV|Apple TV/i.test(ua)) return "tvOS";
  if (/AFT|Fire TV|Amazon/i.test(ua)) return "afai";
  if (/Web0S|webOS|LG Browser|LGSTB|LGE/i.test(ua)) return "lgudid";
  if (/Android|Google TV/i.test(ua)) return "aaid";
  if (/iPhone|iPad|iPod/i.test(ua)) return "idfa";
  return void 0;
}
function isEquivalentDeviceIdType(tagType, runtimeType) {
  if (!tagType || !runtimeType) return false;
  const tag = tagType.toLowerCase();
  const runtime = runtimeType.toLowerCase();
  if (tag === runtime) return true;
  return ANDROID_ID_TYPES.has(tag) && ANDROID_ID_TYPES.has(runtime);
}
function resolveNativeDeviceId() {
  const bridgeSignals = readNativeBridgeSignals();
  const platformDevice = readPlatformNativeDeviceId();
  const deviceId = bridgeSignals.deviceId || readStoredDeviceId() || platformDevice.deviceId;
  if (!deviceId || isMacroPlaceholder(deviceId) || isZeroedAdId(deviceId)) {
    return void 0;
  }
  return deviceId;
}
function getOrCreateStoredUuid(storageKey) {
  if (typeof window === "undefined") {
    return void 0;
  }
  const existing = readPersistentId(storageKey);
  if (existing) {
    writePersistentId(storageKey, existing);
    return existing;
  }
  const id = createUuid();
  writePersistentId(storageKey, id);
  return id;
}
function getOrCreateCtvSessionId() {
  const existing = readPersistentId(CTV_SESSION_STORAGE_KEY);
  if (existing) {
    writePersistentId(CTV_SESSION_STORAGE_KEY, existing);
    return existing;
  }
  const sessionId = createUuid();
  writePersistentId(CTV_SESSION_STORAGE_KEY, sessionId);
  return sessionId;
}
function readPersistentId(storageKey) {
  return readStoredString(storageKey) || readCookie(storageKey);
}
function writePersistentId(storageKey, value) {
  try {
    window.localStorage?.setItem(storageKey, value);
  } catch {
  }
  writeCookie(storageKey, value);
}
var PERSISTENT_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5;
function readCookie(name) {
  if (typeof document === "undefined") {
    return void 0;
  }
  try {
    const prefix = encodeURIComponent(name) + "=";
    const parts = document.cookie ? document.cookie.split(";") : [];
    for (const part of parts) {
      const entry = part.trim();
      if (entry.startsWith(prefix)) {
        return decodeURIComponent(entry.slice(prefix.length)) || void 0;
      }
    }
  } catch {
  }
  return void 0;
}
function writeCookie(name, value) {
  if (typeof document === "undefined") {
    return;
  }
  try {
    document.cookie = encodeURIComponent(name) + "=" + encodeURIComponent(value) + ";path=/;max-age=" + PERSISTENT_ID_COOKIE_MAX_AGE + ";SameSite=Lax";
  } catch {
  }
}
function createUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    let seed = Date.now() ^ Math.floor(performanceNow() * 1e3);
    for (let i = 0; i < 16; i++) {
      seed = seed * 1103515245 + 12345 & 2147483647;
      bytes[i] = (seed ^ Math.floor(Math.random() * 256)) & 255;
    }
  }
  bytes[6] = (bytes[6] ?? 0) & 15 | 64;
  bytes[8] = (bytes[8] ?? 0) & 63 | 128;
  let hex = "";
  for (let i = 0; i < 16; i++) {
    hex += (bytes[i] ?? 0).toString(16).padStart(2, "0");
  }
  return hex.slice(0, 8) + "-" + hex.slice(8, 12) + "-" + hex.slice(12, 16) + "-" + hex.slice(16, 20) + "-" + hex.slice(20, 32);
}
function performanceNow() {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {
  }
  return 0;
}
function readPlatformNativeDeviceId() {
  if (typeof window === "undefined") return {};
  try {
    const adinfo = window.webapis?.adinfo;
    if (adinfo) {
      const tifa = typeof adinfo.getTIFA === "function" ? adinfo.getTIFA() : adinfo.tifa;
      if (typeof tifa === "string" && tifa && !isMacroPlaceholder(tifa)) {
        const lat = typeof adinfo.isLATEnabled === "function" ? adinfo.isLATEnabled() : void 0;
        return {
          deviceId: tifa,
          deviceIdType: "tifa",
          ...typeof lat === "boolean" ? { limitAdTracking: lat } : {}
        };
      }
    }
  } catch {
  }
  try {
    const webOSDev = window.webOSDev;
    if (webOSDev) {
      const lgudid = typeof webOSDev.LGUDID === "function" ? webOSDev.LGUDID() : webOSDev.lgudid ?? webOSDev.LGUDID;
      if (typeof lgudid === "string" && lgudid) {
        if (isZeroedAdId(lgudid)) {
          return { deviceIdType: "lgudid", limitAdTracking: true };
        }
        if (!isMacroPlaceholder(lgudid)) {
          return { deviceId: lgudid, deviceIdType: "lgudid", limitAdTracking: false };
        }
      }
    }
  } catch {
  }
  try {
    const roku = window.Roku;
    if (roku) {
      const rida = typeof roku.getPublisherUniqueId === "function" ? roku.getPublisherUniqueId() : roku.rida;
      if (typeof rida === "string" && rida && !isMacroPlaceholder(rida)) {
        return { deviceId: rida, deviceIdType: "rida", limitAdTracking: false };
      }
    }
  } catch {
  }
  return {};
}
var ZEROED_AD_ID_PATTERNS = /* @__PURE__ */ new Set([
  "38400000-8cf0-11bd-b23e-10b96e40000d",
  "00000000-0000-0000-0000-000000000000"
]);
function isMacroPlaceholder(value) {
  const t = value.trim();
  return !t || /^(\[[^\]]*\]|\{[^}]*\}|%%[^%]*%%)$/.test(t) || /^(unknown|null|undefined|none|n\/a|\$\{[^}]+\})$/i.test(t) || ZEROED_AD_ID_PATTERNS.has(t.toLowerCase());
}
function isZeroedAdId(value) {
  return ZEROED_AD_ID_PATTERNS.has(value.trim().toLowerCase());
}
function readNativeBridgeSignals() {
  if (typeof window === "undefined") {
    return {};
  }
  const candidates = [
    window.__STORMCLOUD_CTV_AD_INFO__,
    window.__STORMCLOUD_CTV__,
    window.stormcloudCtv,
    window.AiryTV,
    window.Android,
    window.webOS,
    window.tizen,
    window.amazon
  ].filter(Boolean);
  for (const source of candidates) {
    const contentUrl = readBridgeValue(source, ["contentUrl", "url", "videoUrl", "canonicalUrl"]) || void 0;
    const appId = readBridgeValue(source, ["appId", "msid", "applicationId", "packageName"]) || void 0;
    const appName = readBridgeValue(source, ["appName", "an", "applicationName"]) || void 0;
    const deviceId = readBridgeValue(source, [
      "rdid",
      "ifa",
      "advertisingId",
      "adId",
      "deviceId",
      "lgudid",
      "LGUDID",
      "tifa",
      "rida",
      "afai",
      "aaid"
    ]) || void 0;
    if (!contentUrl && !deviceId && !appId && !appName) continue;
    const deviceIdType = readBridgeValue(source, [
      "idtype",
      "deviceIdType",
      "advertisingIdType",
      "idType"
    ]) || inferDeviceIdType();
    const limitAdTracking = readBridgeBoolean(source, [
      "is_lat",
      "limitAdTracking",
      "limitedAdTracking",
      "lat"
    ]);
    return {
      ...contentUrl ? { contentUrl } : {},
      ...appId ? { appId } : {},
      ...appName ? { appName } : {},
      ...deviceId ? { deviceId } : {},
      ...deviceId && deviceIdType ? { deviceIdType } : {},
      ...deviceId && limitAdTracking != null ? { limitAdTracking } : {}
    };
  }
  return {};
}
function readBridgeValue(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "string" && value) {
      return value;
    }
    if (typeof value === "function") {
      try {
        const result = value.call(source);
        if (typeof result === "string" && result) {
          return result;
        }
      } catch {
      }
    }
  }
  return void 0;
}
function readBridgeBoolean(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    const normalized = normalizeBoolean(value);
    if (normalized != null) {
      return normalized;
    }
    if (typeof value === "function") {
      try {
        const result = normalizeBoolean(value.call(source));
        if (result != null) {
          return result;
        }
      } catch {
      }
    }
  }
  return void 0;
}
function readStoredDeviceId() {
  for (const key of DEVICE_ID_STORAGE_KEYS) {
    const value = readStoredString(key);
    if (value) {
      return value;
    }
  }
  return void 0;
}
function readStoredString(key) {
  if (typeof window === "undefined") {
    return void 0;
  }
  try {
    const value = window.localStorage?.getItem(key);
    return value || void 0;
  } catch {
    return void 0;
  }
}
function readStoredLimitAdTracking() {
  return normalizeBoolean(
    readStoredString("limitAdTracking") || readStoredString("limitedAdTracking") || readStoredString("is_lat")
  );
}
function normalizeBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "yes") {
      return true;
    }
    if (normalized === "0" || normalized === "false" || normalized === "no") {
      return false;
    }
  }
  return void 0;
}

// src/utils/tracking.ts
var cachedBrowserId = null;
function buildAnalyticsContext(config) {
  const context = {};
  if (config.debugAdTiming) {
    context.debugAdTiming = true;
  }
  if (config.isLiveStream !== void 0) {
    context.inputStreamType = config.isLiveStream ? "live" : "vod";
  }
  return context;
}
function getClientInfo() {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  const vendor = navigator.vendor || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const memory = navigator.deviceMemory || null;
  const hardwareConcurrency = navigator.hardwareConcurrency || 1;
  const screenInfo = {
    width: screen?.width,
    height: screen?.height,
    availWidth: screen?.availWidth,
    availHeight: screen?.availHeight,
    orientation: screen?.orientation?.type || "",
    pixelDepth: screen?.pixelDepth
  };
  let deviceType = "desktop";
  let brand = "Unknown";
  let os = "Unknown";
  let model = "";
  let isSmartTV = false;
  let isAndroid = false;
  let isWebView = false;
  let isWebApp = false;
  if (ua.includes("Web0S")) {
    brand = "LG";
    os = "webOS";
    isSmartTV = true;
    deviceType = "tv";
    const webosMatch = ua.match(/Web0S\/([^\s]+)/);
    model = webosMatch ? `webOS ${webosMatch[1]}` : "webOS TV";
  } else if (ua.includes("Tizen")) {
    brand = "Samsung";
    os = "Tizen";
    isSmartTV = true;
    deviceType = "tv";
    const tizenMatch = ua.match(/Tizen\/([^\s]+)/);
    const tvMatch = ua.match(/(?:Smart-TV|SMART-TV|TV)/i) ? "Smart TV" : "";
    model = tizenMatch ? `Tizen ${tizenMatch[1]} ${tvMatch}`.trim() : "Tizen TV";
  } else if (ua.includes("Philips")) {
    brand = "Philips";
    os = "Saphi";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("Sharp") || ua.includes("AQUOS")) {
    brand = "Sharp";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("Android") && (ua.includes("Sony") || vendor.includes("Sony"))) {
    brand = "Sony";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("Android") && (ua.includes("NetCast") || ua.includes("LG"))) {
    brand = "LG";
    os = "Android TV";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes(" Roku") || ua.includes("Roku/")) {
    brand = "Roku";
    os = "Roku OS";
    isSmartTV = true;
    deviceType = "tv";
  } else if (ua.includes("AppleTV")) {
    brand = "Apple";
    os = "tvOS";
    isSmartTV = true;
    deviceType = "tv";
  }
  if (ua.includes("Android")) {
    isAndroid = true;
    os = "Android";
    deviceType = /Mobile/.test(ua) ? "mobile" : "tablet";
    if (ua.includes("Android") && (maxTouchPoints === 0 || ua.includes("Google TV") || ua.includes("XiaoMi"))) {
      deviceType = "tv";
      isSmartTV = true;
      brand = brand === "Unknown" ? "Android TV" : brand;
    }
    const androidModelMatch = ua.match(/\(([^)]*Android[^)]*)\)/);
    if (androidModelMatch && androidModelMatch[1]) {
      model = androidModelMatch[1];
    }
  }
  if (/iPad|iPhone|iPod/.test(ua)) {
    os = "iOS";
    deviceType = "mobile";
    brand = "Apple";
    if (navigator.maxTouchPoints > 1 && /iPad/.test(ua)) {
      deviceType = "tablet";
    }
  }
  if (!isAndroid && !isSmartTV && !/Mobile/.test(ua)) {
    if (ua.includes("Windows")) {
      os = "Windows";
      deviceType = "desktop";
    } else if (ua.includes("Mac") && !/iPhone/.test(ua)) {
      os = "macOS";
      deviceType = "desktop";
      if (maxTouchPoints > 1) deviceType = "tablet";
    } else if (ua.includes("Linux")) {
      os = "Linux";
      deviceType = "desktop";
    }
  }
  if (brand === "Unknown") {
    if (vendor.includes("Google") || ua.includes("Chrome")) brand = "Google";
    if (vendor.includes("Apple")) brand = "Apple";
    if (vendor.includes("Samsung") || ua.includes("SM-")) brand = "Samsung";
  }
  isWebView = /wv|WebView|Linux; U;/.test(ua);
  if (window?.outerHeight === 0 && window?.outerWidth === 0) {
    isWebView = true;
  }
  isWebApp = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true || window.screen?.orientation?.angle !== void 0;
  return {
    brand,
    os,
    model: model || ua.substring(0, 50) + "...",
    deviceType,
    isSmartTV,
    isAndroid,
    isWebView,
    isWebApp,
    domain: window.location.hostname,
    origin: window.location.origin,
    path: window.location.pathname,
    userAgent: ua,
    vendor,
    platform,
    screen: screenInfo,
    hardwareConcurrency,
    deviceMemory: memory,
    maxTouchPoints,
    language: navigator.language,
    languages: navigator.languages?.join(",") || "",
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || "",
    referrer: document.referrer,
    visibilityState: document.visibilityState
  };
}
async function sha256Hex(input) {
  if (typeof crypto !== "undefined" && crypto.subtle?.digest) {
    try {
      await crypto.subtle.digest("SHA-256", new Uint8Array([1, 2, 3]));
      let encodedData;
      if (typeof TextEncoder !== "undefined") {
        encodedData = new TextEncoder().encode(input);
      } else {
        const utf8 = unescape(encodeURIComponent(input));
        const buffer = new Uint8Array(utf8.length);
        for (let i = 0; i < utf8.length; i++) {
          buffer[i] = utf8.charCodeAt(i);
        }
        encodedData = buffer;
      }
      const hashBuffer = await crypto.subtle.digest("SHA-256", encodedData);
      return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
      console.warn(
        "[StormcloudVideoPlayer] crypto.subtle not supported, using fallback hash"
      );
    }
  }
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const fallbackHash = Math.abs(hash).toString(16).padStart(8, "0");
  const timestamp = Date.now().toString(16).padStart(12, "0");
  const random = Math.random().toString(16).substring(2, 14).padStart(12, "0");
  return (fallbackHash + timestamp + random).padEnd(64, "0");
}
function resolvePlayerIdentitySeed(clientInfo) {
  const nativeDeviceId = resolveNativeDeviceId();
  if (nativeDeviceId) {
    return `device:${nativeDeviceId}`;
  }
  const persistedId = getOrCreateStoredUuid(PLAYER_ID_STORAGE_KEY);
  if (persistedId) {
    return `persisted:${persistedId}`;
  }
  return `fingerprint:${JSON.stringify(clientInfo)}`;
}
async function getBrowserID(clientInfo) {
  if (cachedBrowserId) {
    return cachedBrowserId;
  }
  cachedBrowserId = await sha256Hex(resolvePlayerIdentitySeed(clientInfo));
  return cachedBrowserId;
}
function canPublish(licenseKey) {
  return Boolean(isMQTTEnabled() && licenseKey);
}
async function buildPlayerMetricEvent(context = {}, flags = {}) {
  const clientInfo = getClientInfo();
  const debugAdTiming = !!context.debugAdTiming;
  if (debugAdTiming) {
    console.log("[StormcloudVideoPlayer] clientInfo: ", JSON.stringify(clientInfo, null, 2));
  }
  const playerId = await getBrowserID(clientInfo);
  if (debugAdTiming) {
    console.log("[StormcloudVideoPlayer] playerId: ", playerId);
  }
  const captureAt = flags.captureAt ?? (/* @__PURE__ */ new Date()).toISOString();
  return {
    player_id: playerId,
    device_type: clientInfo.deviceType,
    os: clientInfo.os.toLowerCase(),
    ad_loaded: flags.adLoaded ?? false,
    ad_detect: flags.adDetect ?? false,
    capture_at: captureAt,
    ...context.inputStreamType ? { input_stream_type: context.inputStreamType } : {}
  };
}
function publishTracking(licenseKey, channel, body) {
  ensureMQTTClient();
  publishMQTT(buildPlayerTopic(licenseKey, channel), body);
}
async function sendInitialTracking(licenseKey, context = {}) {
  if (!canPublish(licenseKey)) return;
  try {
    const metricEvent = await buildPlayerMetricEvent(context, {
      adLoaded: false,
      adDetect: false
    });
    publishTracking(licenseKey, "metrics", {
      events: [metricEvent]
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending initial tracking data:",
      error
    );
  }
}
async function sendAdDetectTracking(licenseKey, adDetectInfo, context = {}) {
  try {
    await sendHeartbeat(licenseKey, context, {
      adDetect: true,
      captureAt: adDetectInfo.timestamp
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad detect tracking:",
      error
    );
  }
}
async function sendAdLoadedTracking(licenseKey, adLoadedInfo, context = {}) {
  try {
    await sendHeartbeat(licenseKey, context, {
      adLoaded: true,
      captureAt: adLoadedInfo.timestamp
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad loaded tracking:",
      error
    );
  }
}
async function sendAdImpressionTracking(licenseKey, adImpressionInfo, context = {}) {
  if (!canPublish(licenseKey)) return;
  try {
    const metricEvent = await buildPlayerMetricEvent(context, {
      captureAt: adImpressionInfo.timestamp
    });
    publishTracking(licenseKey, "heartbeat", metricEvent);
    publishTracking(licenseKey, "impressions", {
      events: [
        {
          player_id: metricEvent.player_id,
          ad_played_count: 1,
          ad_url: adImpressionInfo.adUrl,
          capture_at: adImpressionInfo.timestamp
        }
      ]
    });
  } catch (error) {
    console.error(
      "[StormcloudVideoPlayer] Error sending ad impression tracking:",
      error
    );
  }
}
async function sendHeartbeat(licenseKey, context = {}, flags = {}) {
  if (!canPublish(licenseKey)) return;
  try {
    const heartbeatData = await buildPlayerMetricEvent(context, flags);
    publishTracking(licenseKey, "heartbeat", heartbeatData);
  } catch (error) {
    console.error("[StormcloudVideoPlayer] Error sending heartbeat:", error);
  }
}

// src/utils/polyfills.ts
function polyfillURLSearchParams() {
  if (typeof URLSearchParams !== "undefined") {
    return;
  }
  class URLSearchParamsPolyfill {
    constructor(init) {
      this.params = /* @__PURE__ */ new Map();
      if (typeof init === "string") {
        this.parseQueryString(init);
      } else if (init instanceof URLSearchParamsPolyfill) {
        init.forEach((value, key) => {
          this.append(key, value);
        });
      }
    }
    parseQueryString(query) {
      const cleanQuery = query.startsWith("?") ? query.slice(1) : query;
      if (!cleanQuery) return;
      cleanQuery.split("&").forEach((param) => {
        const [key, value] = param.split("=");
        if (key) {
          const decodedKey = this.safeDecodeURIComponent(key);
          const decodedValue = value ? this.safeDecodeURIComponent(value) : "";
          this.append(decodedKey, decodedValue);
        }
      });
    }
    safeDecodeURIComponent(str) {
      try {
        return decodeURIComponent(str.replace(/\+/g, " "));
      } catch (e) {
        return str;
      }
    }
    append(name, value) {
      const values = this.params.get(name) || [];
      values.push(String(value));
      this.params.set(name, values);
    }
    delete(name) {
      this.params.delete(name);
    }
    get(name) {
      const values = this.params.get(name);
      return values && values.length > 0 && values[0] !== void 0 ? values[0] : null;
    }
    getAll(name) {
      return this.params.get(name) || [];
    }
    has(name) {
      return this.params.has(name);
    }
    set(name, value) {
      this.params.set(name, [String(value)]);
    }
    forEach(callback) {
      this.params.forEach((values, key) => {
        values.forEach((value) => {
          callback(value, key, this);
        });
      });
    }
    toString() {
      const parts = [];
      this.params.forEach((values, key) => {
        values.forEach((value) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        });
      });
      return parts.join("&");
    }
  }
  window.URLSearchParams = URLSearchParamsPolyfill;
}
function polyfillTextEncoder() {
  if (typeof TextEncoder !== "undefined") {
    return;
  }
  class TextEncoderPolyfill {
    constructor() {
      this.encoding = "utf-8";
    }
    encode(str) {
      const utf8 = [];
      for (let i = 0; i < str.length; i++) {
        let charcode = str.charCodeAt(i);
        if (charcode < 128) {
          utf8.push(charcode);
        } else if (charcode < 2048) {
          utf8.push(192 | charcode >> 6, 128 | charcode & 63);
        } else if (charcode < 55296 || charcode >= 57344) {
          utf8.push(
            224 | charcode >> 12,
            128 | charcode >> 6 & 63,
            128 | charcode & 63
          );
        } else {
          i++;
          charcode = 65536 + ((charcode & 1023) << 10 | str.charCodeAt(i) & 1023);
          utf8.push(
            240 | charcode >> 18,
            128 | charcode >> 12 & 63,
            128 | charcode >> 6 & 63,
            128 | charcode & 63
          );
        }
      }
      return new Uint8Array(utf8);
    }
  }
  window.TextEncoder = TextEncoderPolyfill;
}
function polyfillPromiseFinally() {
  if (typeof Promise !== "undefined" && !Promise.prototype.finally) {
    Promise.prototype.finally = function(callback) {
      const constructor = this.constructor;
      return this.then(
        (value) => constructor.resolve(callback()).then(() => value),
        (reason) => constructor.resolve(callback()).then(() => {
          throw reason;
        })
      );
    };
  }
}
function polyfillObjectAssign() {
  if (typeof Object.assign !== "function") {
    Object.assign = function(target, ...sources) {
      if (target == null) {
        throw new TypeError("Cannot convert undefined or null to object");
      }
      const to = Object(target);
      for (let i = 0; i < sources.length; i++) {
        const nextSource = sources[i];
        if (nextSource != null) {
          for (const nextKey in nextSource) {
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    };
  }
}
function polyfillArrayFrom() {
  if (!Array.from) {
    Array.from = function(arrayLike, mapFn, thisArg) {
      const items = Object(arrayLike);
      if (arrayLike == null) {
        throw new TypeError("Array.from requires an array-like object");
      }
      const len = items.length >>> 0;
      const result = new Array(len);
      for (let i = 0; i < len; i++) {
        if (mapFn) {
          result[i] = mapFn.call(thisArg, items[i], i);
        } else {
          result[i] = items[i];
        }
      }
      return result;
    };
  }
}
function polyfillStringStartsWith() {
  if (!String.prototype.startsWith) {
    String.prototype.startsWith = function(search, pos) {
      pos = !pos || pos < 0 ? 0 : +pos;
      return this.substring(pos, pos + search.length) === search;
    };
  }
}
function polyfillStringEndsWith() {
  if (!String.prototype.endsWith) {
    String.prototype.endsWith = function(search, length) {
      if (length === void 0 || length > this.length) {
        length = this.length;
      }
      return this.substring(length - search.length, length) === search;
    };
  }
}
function polyfillStringIncludes() {
  if (!String.prototype.includes) {
    String.prototype.includes = function(search, start) {
      if (typeof start !== "number") {
        start = 0;
      }
      if (start + search.length > this.length) {
        return false;
      }
      return this.indexOf(search, start) !== -1;
    };
  }
}
function initializePolyfills() {
  polyfillObjectAssign();
  polyfillArrayFrom();
  polyfillStringStartsWith();
  polyfillStringEndsWith();
  polyfillStringIncludes();
  polyfillURLSearchParams();
  polyfillTextEncoder();
  polyfillPromiseFinally();
}

// src/utils/vastMacros.ts
var UNEXPANDED_MACRO_PATTERN = /^(\[[^\]]*\]|\{[^}]*\}|%%[^%]*%%)$/;
var INVALID_VAST_PARAM_KEY = /^\s|\s$/;
var EXAMPLE_COM_URL = /example\.com/i;
var CTV_UNSUPPORTED_PARAMS = [
  "description_url",
  "tfcd",
  "npa",
  "min_ad_duration",
  "max_ad_duration",
  "unviewed_position_start",
  "impl",
  "vad_type",
  "gdpr",
  "gdpr_consent",
  "us_privacy"
];
var INLINE_DEVICE_MACRO_REPLACEMENTS = [
  { pattern: /\[RDID\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[IFA\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[ADVERTISING[_-]?ID(?:ENTIFIER)?\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[DEVICE[_-]?ID\]/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\[IDTYPE\]/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /\{rdid\}/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\{device[_-]?id\}/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /\{idtype\}/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /%%ADVERTISING_IDENTIFIER_PLAIN%%/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /%%ADVERTISING_ID_OPT_OUT%%/gi, getValue: (ctx) => ctx.limitAdTracking != null ? ctx.limitAdTracking ? "1" : "0" : void 0 },
  { pattern: /%%ADVERTISING_ID_TYPE%%/gi, getValue: (ctx) => ctx.deviceIdType },
  { pattern: /%%ADVERTISING_ID%%/gi, getValue: (ctx) => ctx.deviceId },
  { pattern: /%%LIMIT_AD_TRACKING%%/gi, getValue: (ctx) => ctx.limitAdTracking != null ? ctx.limitAdTracking ? "1" : "0" : void 0 },
  { pattern: /%%MSID%%/gi, getValue: (ctx) => ctx.appId },
  { pattern: /%%APP_NAME%%/gi, getValue: (ctx) => ctx.appName },
  { pattern: /%%SESSION_ID%%/gi, getValue: (ctx) => ctx.sessionId },
  { pattern: /%%CORRELATOR%%/gi, getValue: (ctx) => ctx.correlator }
];
function isMacroPlaceholder2(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (UNEXPANDED_MACRO_PATTERN.test(trimmed)) return true;
  return /^(unknown|null|undefined|none|n\/a|\$\{[^}]+\})$/i.test(trimmed);
}
function generateCorrelator() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    try {
      const buf = new Uint32Array(2);
      crypto.getRandomValues(buf);
      const value = (buf[0] ?? 0) * 2097152 + ((buf[1] ?? 0) & 2097151);
      if (value > 0) {
        return String(value);
      }
    } catch {
    }
  }
  return String(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER) + 1);
}
function expandInlineMacroPlaceholders(baseUrl, ctx) {
  let url = baseUrl;
  for (const { pattern, getValue } of INLINE_DEVICE_MACRO_REPLACEMENTS) {
    const value = getValue(ctx);
    if (value != null && value !== "") {
      url = url.replace(pattern, encodeURIComponent(value));
    }
  }
  return url;
}
function applyDeviceIdentityParams(params, ctx) {
  const runtimeId = ctx.deviceId?.trim();
  const runtimeType = ctx.deviceIdType?.trim() || inferDeviceIdType();
  const tagRdid = params.get("rdid");
  const tagType = params.get("idtype");
  const tagLat = params.get("is_lat");
  const latValue = ctx.limitAdTracking != null ? ctx.limitAdTracking ? "1" : "0" : void 0;
  if (runtimeId) {
    params.set("rdid", runtimeId);
    params.set("idtype", runtimeType || tagType || "aaid");
    params.set("is_lat", latValue ?? (isMacroPlaceholder2(tagLat) ? "0" : tagLat));
    return;
  }
  const tagRdidValid = Boolean(tagRdid && !isMacroPlaceholder2(tagRdid));
  const tagTypeValid = Boolean(tagType && !isMacroPlaceholder2(tagType));
  if (tagRdidValid && tagTypeValid && runtimeType && !isEquivalentDeviceIdType(tagType, runtimeType)) {
    params.delete("rdid");
    params.delete("idtype");
    params.delete("is_lat");
    return;
  }
  if (tagRdidValid) {
    if (isMacroPlaceholder2(tagType) && runtimeType) {
      params.set("idtype", runtimeType);
    }
    if (isMacroPlaceholder2(tagLat)) {
      params.set("is_lat", latValue ?? "0");
    }
    return;
  }
  params.delete("rdid");
  params.delete("idtype");
  params.delete("is_lat");
}
function removeBrokenAppNameOrphans(params, appName) {
  const toDelete = [];
  params.forEach((_value, key) => {
    if (!key || INVALID_VAST_PARAM_KEY.test(key)) {
      toDelete.push(key);
    }
  });
  if (appName && appName.includes("&")) {
    for (const suffix of appName.split("&").slice(1)) {
      const trimmed = suffix.trimStart();
      if (!trimmed) continue;
      toDelete.push(suffix, ` ${trimmed}`, trimmed);
    }
  }
  for (const key of new Set(toDelete)) {
    if (key) params.delete(key);
  }
}
function normalizeVastTagUrl(raw) {
  if (!raw) return raw;
  try {
    const url = new URL(raw);
    const anValue = url.searchParams.get("an");
    if (anValue !== null) {
      const orphanFragments = [];
      url.searchParams.forEach((_value, key) => {
        if (key && INVALID_VAST_PARAM_KEY.test(key)) {
          orphanFragments.push(key.trim());
        }
      });
      if (orphanFragments.length > 0) {
        const reconstructed = [anValue.trimEnd(), ...orphanFragments].filter(Boolean).join(" & ");
        url.searchParams.forEach((_value, key) => {
          if (key && INVALID_VAST_PARAM_KEY.test(key)) {
            url.searchParams.delete(key);
          }
        });
        url.searchParams.set("an", reconstructed);
        return url.toString().replace(
          /([?&]an=)([^&]*)/,
          (_match, prefix, val) => prefix + val.replace(/\+/g, "%20")
        );
      }
    }
    removeBrokenAppNameOrphans(url.searchParams);
    return url.toString();
  } catch {
    return raw;
  }
}
function applyVastMacros(baseUrl, ctx) {
  const expandedUrl = expandInlineMacroPlaceholders(baseUrl, ctx);
  let url;
  try {
    url = new URL(normalizeVastTagUrl(expandedUrl));
  } catch {
    return replaceCorrelatorFallback(expandedUrl, ctx.correlator);
  }
  const params = url.searchParams;
  removeBrokenAppNameOrphans(params, ctx.appName);
  params.set("correlator", ctx.correlator);
  params.set("scor", ctx.streamCorrelator);
  if (ctx.isCtv) {
    for (const key of CTV_UNSUPPORTED_PARAMS) {
      params.delete(key);
    }
  }
  if (ctx.pod != null) {
    params.set("pod", String(ctx.pod));
  }
  const optimizedPodMode = ctx.podMaxAds != null || ctx.podMinDurationMs != null || ctx.podMaxDurationMs != null;
  if (optimizedPodMode) {
    params.delete("ppos");
    params.delete("lip");
    if (ctx.podMinDurationMs != null) {
      params.set("pmnd", String(Math.max(0, Math.round(ctx.podMinDurationMs))));
    }
    if (ctx.podMaxDurationMs != null) {
      params.set("pmxd", String(Math.max(0, Math.round(ctx.podMaxDurationMs))));
    }
    if (ctx.podMaxAds != null) {
      params.set("pmad", String(Math.max(1, Math.round(ctx.podMaxAds))));
    }
  } else if (ctx.adPosition != null) {
    params.set("ppos", String(ctx.adPosition));
  }
  const requestUrl = ctx.contentUrl || ctx.pageUrl;
  if (requestUrl) {
    params.set("url", requestUrl);
    if (!ctx.isCtv) {
      params.set("description_url", requestUrl);
    }
  } else {
    const existingUrl = params.get("url") || "";
    if (EXAMPLE_COM_URL.test(existingUrl)) {
      params.delete("url");
      params.delete("description_url");
    }
  }
  if (ctx.adWillPlayMuted != null) {
    params.set("vpmute", ctx.adWillPlayMuted ? "1" : "0");
  }
  if (ctx.adWillAutoPlay != null) {
    params.set("vpa", ctx.adWillAutoPlay ? "auto" : "click");
  }
  if (ctx.appId) {
    params.set("msid", ctx.appId);
  }
  if (ctx.appName) {
    removeBrokenAppNameOrphans(params, ctx.appName);
    params.set("an", ctx.appName);
  }
  if (ctx.sessionId) {
    params.set("sid", ctx.sessionId);
  }
  if (ctx.deviceTypeHint != null) {
    params.set("dth", String(ctx.deviceTypeHint));
  } else if (ctx.isCtv) {
    params.set("dth", "5");
  }
  applyDeviceIdentityParams(params, ctx);
  if (!ctx.isCtv) {
    const consent = ctx.consent;
    if (consent?.gdpr != null) {
      params.set("gdpr", consent.gdpr);
    }
    if (consent?.gdprConsent != null) {
      params.set("gdpr_consent", consent.gdprConsent);
    }
    if (consent?.usPrivacy != null) {
      params.set("us_privacy", consent.usPrivacy);
    }
  }
  if (ctx.adTest) {
    params.set("adtest", "on");
  }
  const staleKeys = [];
  params.forEach((value, key) => {
    if (isMacroPlaceholder2(value)) {
      staleKeys.push(key);
    }
  });
  for (const key of staleKeys) {
    params.delete(key);
  }
  removeBrokenAppNameOrphans(params, ctx.appName);
  return url.toString();
}
function replaceCorrelatorFallback(baseUrl, correlator) {
  const correlatorRegex = /([?&])correlator=([^&]*)/;
  if (correlatorRegex.test(baseUrl)) {
    return baseUrl.replace(correlatorRegex, `$1correlator=${correlator}`);
  }
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}correlator=${correlator}`;
}
function fetchConsentSignals(timeoutMs = 1500) {
  const signals = {};
  if (typeof window === "undefined") {
    return Promise.resolve(signals);
  }
  const tasks = [];
  const tcfApi = window.__tcfapi;
  if (typeof tcfApi === "function") {
    tasks.push(
      new Promise((resolve) => {
        let settled = false;
        try {
          tcfApi("addEventListener", 2, (tcData, success) => {
            if (settled) return;
            if (success && tcData && (tcData.eventStatus === "tcloaded" || tcData.eventStatus === "useractioncomplete")) {
              settled = true;
              signals.gdpr = tcData.gdprApplies ? "1" : "0";
              if (typeof tcData.tcString === "string" && tcData.tcString) {
                signals.gdprConsent = tcData.tcString;
              }
              try {
                tcfApi("removeEventListener", 2, () => {
                }, tcData.listenerId);
              } catch {
              }
              resolve();
            }
          });
        } catch {
          resolve();
        }
        setTimeout(() => {
          if (!settled) {
            settled = true;
            resolve();
          }
        }, timeoutMs);
      })
    );
  }
  const uspApi = window.__uspapi;
  if (typeof uspApi === "function") {
    tasks.push(
      new Promise((resolve) => {
        try {
          uspApi("getUSPData", 1, (data, success) => {
            if (success && typeof data?.uspString === "string" && data.uspString) {
              signals.usPrivacy = data.uspString;
            }
            resolve();
          });
        } catch {
          resolve();
        }
        setTimeout(resolve, timeoutMs);
      })
    );
  }
  if (tasks.length === 0) {
    return Promise.resolve(signals);
  }
  return Promise.all(tasks).then(() => signals);
}

// src/utils/browserCompat.ts
function getChromeVersion(ua) {
  const match = ua.match(/Chrome\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}
function getWebKitVersion(ua) {
  const match = ua.match(/AppleWebKit\/(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}
function getPlatform() {
  if ("userAgentData" in navigator && navigator.userAgentData?.platform) {
    return navigator.userAgentData.platform;
  }
  const ua = navigator.userAgent;
  if (/Mac|iPhone|iPad|iPod/i.test(ua)) {
    return /iPhone|iPad|iPod/i.test(ua) ? "iPhone" : "MacIntel";
  }
  if (/Win/i.test(ua)) {
    return "Win32";
  }
  if (/Linux/i.test(ua)) {
    return /Android/i.test(ua) ? "Linux armv8l" : "Linux x86_64";
  }
  if (/CrOS/i.test(ua)) {
    return "CrOS";
  }
  return navigator.platform || "Unknown";
}
function detectBrowser() {
  const ua = navigator.userAgent;
  const platform = getPlatform();
  let name = "Unknown";
  let version = "0";
  let majorVersion = 0;
  let isSmartTV = false;
  let isLegacyTV = false;
  let supportsModernJS2 = true;
  let webOSVersion;
  let tizenVersion;
  let chromeVersionNum;
  const chromeVersion = getChromeVersion(ua);
  const webkitVersion = getWebKitVersion(ua);
  chromeVersionNum = chromeVersion > 0 ? chromeVersion : void 0;
  if (/Web0S|webOS|LG Browser|LGSTB/i.test(ua)) {
    name = "LG WebOS";
    isSmartTV = true;
    let match = ua.match(/Web0S[/\s]*([\d.]+)/i) || ua.match(/webOS[/\s]*([\d.]+)/i);
    if (!match || !match[1]) {
      match = ua.match(/webOSTV[/\s-]*([\d.]+)/i) || ua.match(/webOS\.TV[/\s-]*([\d.]+)/i);
    }
    if (match && match[1]) {
      version = match[1];
      const parts = version.split(".");
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
      webOSVersion = majorVersion;
    } else if (chromeVersion > 0) {
      if (chromeVersion >= 79) {
        webOSVersion = 6;
        version = "6.0";
        majorVersion = 6;
      } else if (chromeVersion >= 68) {
        webOSVersion = 5;
        version = "5.0";
        majorVersion = 5;
      } else if (chromeVersion >= 53) {
        webOSVersion = 4;
        version = "4.0";
        majorVersion = 4;
      } else if (chromeVersion >= 38) {
        webOSVersion = 3;
        version = "3.0";
        majorVersion = 3;
      } else {
        webOSVersion = 2;
        version = "2.0";
        majorVersion = 2;
      }
    } else {
      version = "Unknown";
      webOSVersion = void 0;
    }
    isLegacyTV = webOSVersion !== void 0 && webOSVersion < 3;
  } else if (/Tizen/i.test(ua)) {
    name = "Samsung Tizen";
    isSmartTV = true;
    const match = ua.match(/Tizen[/\s]*([\d.]+)/i);
    version = match && match[1] ? match[1] : "Unknown";
    if (version !== "Unknown") {
      const parts = version.split(".");
      majorVersion = parts[0] ? parseInt(parts[0], 10) : 0;
      tizenVersion = majorVersion;
    }
    isLegacyTV = tizenVersion !== void 0 && tizenVersion < 3;
  } else if (/SMART-TV|SmartTV/i.test(ua)) {
    name = "Smart TV";
    isSmartTV = true;
    isLegacyTV = chromeVersion > 0 && chromeVersion < 53;
  } else if (/NetCast/i.test(ua)) {
    name = "LG NetCast";
    isSmartTV = true;
    isLegacyTV = true;
  } else if (/BRAVIA/i.test(ua)) {
    name = "Sony BRAVIA";
    isSmartTV = true;
    isLegacyTV = chromeVersion > 0 && chromeVersion < 53;
  } else {
    if (chromeVersion > 0) {
      name = "Chrome";
      version = chromeVersion.toString();
      majorVersion = chromeVersion;
      if (chromeVersion < 50) {
        supportsModernJS2 = false;
      }
    }
    if (webkitVersion > 0 && webkitVersion < 600) {
      supportsModernJS2 = false;
    }
  }
  if (typeof Promise === "undefined" || typeof Map === "undefined" || typeof Set === "undefined") {
    supportsModernJS2 = false;
  }
  if (typeof URLSearchParams === "undefined") {
    supportsModernJS2 = false;
  }
  return {
    name,
    version,
    majorVersion,
    isSmartTV,
    isLegacyTV,
    platform,
    supportsModernJS: supportsModernJS2,
    webOSVersion,
    tizenVersion,
    chromeVersion: chromeVersionNum
  };
}
function getBrowserConfigOverrides() {
  const browser = detectBrowser();
  const overrides = {};
  if (browser.isSmartTV) {
    overrides.allowNativeHls = true;
  }
  return overrides;
}
function supportsModernJS() {
  try {
    return typeof Promise !== "undefined" && typeof Map !== "undefined" && typeof Set !== "undefined" && typeof Array.from !== "undefined" && typeof Object.assign !== "undefined" && typeof Array.prototype.forEach !== "undefined" && typeof String.prototype.includes !== "undefined";
  } catch (e) {
    return false;
  }
}
function logBrowserInfo(debug = false) {
  if (!debug) return;
  const browser = detectBrowser();
  console.log("[StormcloudVideoPlayer] Browser Compatibility Info:", {
    browser: `${browser.name} ${browser.version}`,
    platform: browser.platform,
    isSmartTV: browser.isSmartTV,
    isLegacyTV: browser.isLegacyTV,
    supportsModernJS: browser.supportsModernJS,
    ...browser.webOSVersion !== void 0 ? { webOSVersion: browser.webOSVersion } : {},
    ...browser.tizenVersion !== void 0 ? { tizenVersion: browser.tizenVersion } : {},
    ...browser.chromeVersion !== void 0 ? { chromeVersion: browser.chromeVersion } : {},
    userAgent: navigator.userAgent
  });
}
function supportsFeature(feature) {
  switch (feature) {
    case "urlsearchparams":
      return typeof URLSearchParams !== "undefined";
    case "textencoder":
      return typeof TextEncoder !== "undefined";
    case "promises":
      return typeof Promise !== "undefined";
    case "fetch":
      return typeof fetch !== "undefined";
    case "crypto":
      return typeof crypto !== "undefined" && typeof crypto.subtle !== "undefined";
    default:
      return false;
  }
}

// src/player/Scte35Parser.ts
function parseCueOutDuration(value) {
  const num = parseFloat(value.trim());
  if (!Number.isNaN(num)) return num;
  const match = value.match(/(?:^|[,\s])DURATION\s*=\s*([0-9.]+)/i) || value.match(/Duration\s*=\s*([0-9.]+)/i);
  if (match && match[1] != null) {
    const dStr = match[1];
    const d = parseFloat(dStr);
    return Number.isNaN(d) ? void 0 : d;
  }
  return void 0;
}
function parseCueOutCont(value) {
  const res = {};
  const elapsedMatch = value.match(/Elapsed\s*=\s*([0-9.]+)/i);
  const durationMatch = value.match(/Duration\s*=\s*([0-9.]+)/i);
  if (elapsedMatch && elapsedMatch[1] != null) {
    const e = parseFloat(elapsedMatch[1]);
    if (!Number.isNaN(e)) res.elapsed = e;
  }
  if (durationMatch && durationMatch[1] != null) {
    const d = parseFloat(durationMatch[1]);
    if (!Number.isNaN(d)) res.duration = d;
  }
  if (!("elapsed" in res) || !("duration" in res)) {
    const slashMatch = value.match(/([0-9.]+)\s*\/\s*([0-9.]+)/);
    if (slashMatch && slashMatch[1] && slashMatch[2]) {
      const elapsed = parseFloat(slashMatch[1]);
      const duration = parseFloat(slashMatch[2]);
      if (!Number.isNaN(elapsed) && !("elapsed" in res)) res.elapsed = elapsed;
      if (!Number.isNaN(duration) && !("duration" in res)) res.duration = duration;
    }
  }
  if ("elapsed" in res || "duration" in res) return res;
  return void 0;
}
function parseAttributeList(value) {
  const attrs = {};
  const regex = /([A-Z0-9-]+)=(("[^"]*")|([^",]*))(?:,|$)/gi;
  let match;
  while ((match = regex.exec(value)) !== null) {
    const key = match[1] ?? "";
    let rawVal = match[3] ?? match[4] ?? "";
    if (rawVal.startsWith('"') && rawVal.endsWith('"')) {
      rawVal = rawVal.slice(1, -1);
    }
    if (key) {
      attrs[key] = rawVal;
    }
  }
  return attrs;
}
function toNumber(val) {
  if (val == null) return void 0;
  const n = typeof val === "string" ? parseFloat(val) : Number(val);
  return Number.isNaN(n) ? void 0 : n;
}
function isManifestBasedMarker(marker) {
  const raw = marker.raw;
  if (!raw) return false;
  if (raw.tag) {
    const tag = String(raw.tag);
    return tag.includes("EXT-X-CUE-OUT") || tag.includes("EXT-X-CUE-IN") || tag.includes("EXT-X-DATERANGE");
  }
  if (raw.id3) return false;
  if (raw.splice_command_type) return false;
  return false;
}
function decodeId3ValueToText(value) {
  try {
    if (typeof value === "string") return value;
    const decoder = new TextDecoder("utf-8", { fatal: false });
    const text = decoder.decode(value);
    if (text && /[\x20-\x7E]/.test(text)) return text;
    let out = "";
    for (let i = 0; i < value.length; i++)
      out += String.fromCharCode(value[i]);
    return out;
  } catch {
    return void 0;
  }
}
function getFragmentStartSeconds(frag) {
  const candidates = [frag?.start, frag?.startPTS];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return void 0;
}
function parseScte35FromId3(tag) {
  const text = decodeId3ValueToText(tag.value);
  if (!text) return void 0;
  const cueOutMatch = text.match(/EXT-X-CUE-OUT(?::([^\r\n]*))?/i) || text.match(/CUE-OUT(?::([^\r\n]*))?/i);
  if (cueOutMatch) {
    const arg = (cueOutMatch[1] ?? "").trim();
    const dur = parseCueOutDuration(arg);
    const marker = {
      type: "start",
      ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
      ...dur !== void 0 ? { durationSeconds: dur } : {},
      raw: { id3: text }
    };
    return marker;
  }
  const cueOutContMatch = text.match(/EXT-X-CUE-OUT-CONT:([^\r\n]*)/i);
  if (cueOutContMatch) {
    const arg = (cueOutContMatch[1] ?? "").trim();
    const cont = parseCueOutCont(arg);
    const marker = {
      type: "progress",
      ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
      ...cont?.duration !== void 0 ? { durationSeconds: cont.duration } : {},
      raw: { id3: text }
    };
    return marker;
  }
  const cueInMatch = text.match(/EXT-X-CUE-IN\b/i) || text.match(/CUE-IN\b/i);
  if (cueInMatch) {
    const marker = {
      type: "end",
      ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
      raw: { id3: text }
    };
    return marker;
  }
  const daterangeMatch = text.match(/EXT-X-DATERANGE:([^\r\n]*)/i);
  if (daterangeMatch) {
    const attrs = parseAttributeList(daterangeMatch[1] ?? "");
    const hasScteOut = "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== void 0;
    const hasScteIn = "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== void 0;
    const klass = String(attrs["CLASS"] ?? "");
    const duration = toNumber(attrs["DURATION"]);
    if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
      const marker = {
        type: "start",
        ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
        ...duration !== void 0 ? { durationSeconds: duration } : {},
        raw: { id3: text, attrs }
      };
      return marker;
    }
    if (hasScteIn) {
      const marker = {
        type: "end",
        ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
        raw: { id3: text, attrs }
      };
      return marker;
    }
  }
  if (/SCTE35-OUT/i.test(text)) {
    const marker = {
      type: "start",
      ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
      raw: { id3: text }
    };
    return marker;
  }
  if (/SCTE35-IN/i.test(text)) {
    const marker = {
      type: "end",
      ...tag.ptsSeconds !== void 0 ? { ptsSeconds: tag.ptsSeconds } : {},
      raw: { id3: text }
    };
    return marker;
  }
  if (tag.value instanceof Uint8Array) {
    const bin = parseScte35Binary(tag.value);
    if (bin) return bin;
  }
  return void 0;
}
function normalizeFragmentPayload(payload) {
  if (payload instanceof Uint8Array) {
    return payload;
  }
  if (payload instanceof ArrayBuffer) {
    return new Uint8Array(payload);
  }
  if (ArrayBuffer.isView(payload)) {
    const view = payload;
    return new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  }
  return void 0;
}
function detectTsPacketFormat(data) {
  const packetSizes = [188, 192, 204];
  for (const packetSize of packetSizes) {
    const tsPayloadOffset = packetSize === 192 ? 4 : 0;
    for (let offset = 0; offset < packetSize; offset++) {
      let matchedPackets = 0;
      for (let packetStart = offset; packetStart + tsPayloadOffset < data.length && matchedPackets < 5; packetStart += packetSize) {
        if (data[packetStart + tsPayloadOffset] !== 71) {
          break;
        }
        matchedPackets++;
      }
      if (matchedPackets >= 2) {
        return { offset, packetSize, tsPayloadOffset };
      }
    }
  }
  return void 0;
}
function getPsiSectionStart(payload, payloadUnitStart) {
  if (!payloadUnitStart) {
    return void 0;
  }
  const pointerField = payload[0];
  if (pointerField == null) {
    return void 0;
  }
  const sectionStart = 1 + pointerField;
  return sectionStart < payload.length ? sectionStart : void 0;
}
function getPsiSectionLength(data, offset) {
  return ((data[offset + 1] ?? 0) & 15) << 8 | (data[offset + 2] ?? 0);
}
function parsePatSection(payload, payloadUnitStart, pmtPids) {
  const sectionStart = getPsiSectionStart(payload, payloadUnitStart);
  if (sectionStart == null || sectionStart + 8 >= payload.length) {
    return;
  }
  if (payload[sectionStart] !== 0) {
    return;
  }
  const sectionLength = getPsiSectionLength(payload, sectionStart);
  const sectionEnd = Math.min(
    payload.length,
    sectionStart + 3 + sectionLength
  );
  for (let offset = sectionStart + 8; offset + 4 <= sectionEnd - 4; offset += 4) {
    const programNumber = (payload[offset] ?? 0) << 8 | (payload[offset + 1] ?? 0);
    const pid = ((payload[offset + 2] ?? 0) & 31) << 8 | (payload[offset + 3] ?? 0);
    if (programNumber !== 0) {
      pmtPids.add(pid);
    }
  }
}
function descriptorsIdentifyScte35(data, start, end) {
  let offset = start;
  while (offset + 2 <= end) {
    const tag = data[offset] ?? 0;
    const length = data[offset + 1] ?? 0;
    const descriptorStart = offset + 2;
    const descriptorEnd = descriptorStart + length;
    if (descriptorEnd > end) {
      return false;
    }
    const registration = length >= 4 ? String.fromCharCode(
      data[descriptorStart] ?? 0,
      data[descriptorStart + 1] ?? 0,
      data[descriptorStart + 2] ?? 0,
      data[descriptorStart + 3] ?? 0
    ) : "";
    if (tag === 138 || registration === "CUEI" || registration === "SCTE") {
      return true;
    }
    offset = descriptorEnd;
  }
  return false;
}
function parsePmtSection(payload, payloadUnitStart, tsScte35Pids) {
  const sectionStart = getPsiSectionStart(payload, payloadUnitStart);
  if (sectionStart == null || sectionStart + 12 >= payload.length) {
    return;
  }
  if (payload[sectionStart] !== 2) {
    return;
  }
  const sectionLength = getPsiSectionLength(payload, sectionStart);
  const sectionEnd = Math.min(
    payload.length,
    sectionStart + 3 + sectionLength
  );
  const programInfoLength = ((payload[sectionStart + 10] ?? 0) & 15) << 8 | (payload[sectionStart + 11] ?? 0);
  let offset = sectionStart + 12 + programInfoLength;
  while (offset + 5 <= sectionEnd - 4) {
    const streamType = payload[offset] ?? 0;
    const elementaryPid = ((payload[offset + 1] ?? 0) & 31) << 8 | (payload[offset + 2] ?? 0);
    const esInfoLength = ((payload[offset + 3] ?? 0) & 15) << 8 | (payload[offset + 4] ?? 0);
    const descriptorStart = offset + 5;
    const descriptorEnd = Math.min(descriptorStart + esInfoLength, sectionEnd - 4);
    if (streamType === 134 || streamType === 6 && descriptorsIdentifyScte35(payload, descriptorStart, descriptorEnd)) {
      tsScte35Pids.add(elementaryPid);
    }
    offset = descriptorStart + esInfoLength;
  }
}
function hasProcessedTsScte35Section(section, processedSections) {
  let checksum = 0;
  for (let i = 0; i < section.length; i++) {
    checksum = checksum * 31 + (section[i] ?? 0) >>> 0;
  }
  const key = `${section.length}:${checksum}`;
  if (processedSections.has(key)) {
    return true;
  }
  processedSections.add(key);
  if (processedSections.size > 200) {
    const firstKey = processedSections.values().next().value;
    if (firstKey) {
      processedSections.delete(firstKey);
    }
  }
  return false;
}
function addScte35SectionMarker(section, markers, processedSections) {
  if (section[0] !== 252 || hasProcessedTsScte35Section(section, processedSections)) {
    return;
  }
  const marker = parseScte35Binary(section);
  if (!marker) {
    return;
  }
  marker.raw = {
    ...typeof marker.raw === "object" && marker.raw ? marker.raw : {},
    source: "ts-packet"
  };
  markers.push(marker);
}
function appendScte35Bytes(pid, bytes, assemblers, markers, processedSections) {
  const assembler = assemblers.get(pid);
  if (!assembler) {
    return;
  }
  for (let i = 0; i < bytes.length && assembler.bytes.length < assembler.expectedLength; i++) {
    assembler.bytes.push(bytes[i] ?? 0);
  }
  if (assembler.bytes.length >= assembler.expectedLength) {
    assemblers.delete(pid);
    addScte35SectionMarker(new Uint8Array(assembler.bytes), markers, processedSections);
  }
}
function collectScte35SectionsFromPayload(pid, payload, payloadUnitStart, assemblers, markers, processedSections) {
  if (payload.length === 0) {
    return;
  }
  let offset = 0;
  if (payloadUnitStart) {
    const pointerField = payload[0] ?? 0;
    const previousAssembler = assemblers.get(pid);
    if (previousAssembler && pointerField > 0) {
      appendScte35Bytes(
        pid,
        payload.subarray(1, Math.min(1 + pointerField, payload.length)),
        assemblers,
        markers,
        processedSections
      );
    }
    assemblers.delete(pid);
    offset = 1 + pointerField;
  } else {
    appendScte35Bytes(pid, payload, assemblers, markers, processedSections);
    return;
  }
  while (offset + 3 <= payload.length) {
    const tableId = payload[offset];
    if (tableId == null || tableId === 255) {
      return;
    }
    const sectionLength = getPsiSectionLength(payload, offset);
    const totalLength = 3 + sectionLength;
    if (sectionLength <= 0 || totalLength <= 3) {
      return;
    }
    if (offset + totalLength <= payload.length) {
      addScte35SectionMarker(
        payload.subarray(offset, offset + totalLength),
        markers,
        processedSections
      );
      offset += totalLength;
    } else {
      assemblers.set(pid, {
        bytes: Array.from(payload.subarray(offset)),
        expectedLength: totalLength
      });
      return;
    }
  }
}
function parseScte35FromTsPackets(data, tsScte35Pids, pmtPids, processedSections) {
  const packetInfo = detectTsPacketFormat(data);
  if (!packetInfo) {
    return [];
  }
  const markers = [];
  const sectionAssemblers = /* @__PURE__ */ new Map();
  for (let packetStart = packetInfo.offset; packetStart + packetInfo.packetSize <= data.length; packetStart += packetInfo.packetSize) {
    const tsStart = packetStart + packetInfo.tsPayloadOffset;
    if (data[tsStart] !== 71) {
      continue;
    }
    const secondByte = data[tsStart + 1];
    const thirdByte = data[tsStart + 2];
    const fourthByte = data[tsStart + 3];
    if (secondByte == null || thirdByte == null || fourthByte == null || (secondByte & 128) !== 0) {
      continue;
    }
    const payloadUnitStart = (secondByte & 64) !== 0;
    const pid = (secondByte & 31) << 8 | thirdByte;
    const adaptationFieldControl = fourthByte >> 4 & 3;
    const hasPayload = adaptationFieldControl === 1 || adaptationFieldControl === 3;
    if (!hasPayload) {
      continue;
    }
    let payloadStart = tsStart + 4;
    if (adaptationFieldControl === 3) {
      const adaptationLength = data[payloadStart];
      if (adaptationLength == null) {
        continue;
      }
      payloadStart += 1 + adaptationLength;
    }
    const payloadEnd = Math.min(tsStart + 188, data.length);
    if (payloadStart >= payloadEnd || payloadStart >= data.length) {
      continue;
    }
    const payload = data.subarray(payloadStart, payloadEnd);
    if (pid === 0) {
      parsePatSection(payload, payloadUnitStart, pmtPids);
    } else if (pmtPids.has(pid)) {
      parsePmtSection(payload, payloadUnitStart, tsScte35Pids);
    } else if (tsScte35Pids.has(pid)) {
      collectScte35SectionsFromPayload(
        pid,
        payload,
        payloadUnitStart,
        sectionAssemblers,
        markers,
        processedSections
      );
    }
  }
  return markers;
}
var BitReader = class {
  constructor(buf) {
    this.buf = buf;
    this.bytePos = 0;
    this.bitPos = 0;
  }
  readBits(numBits) {
    let result = 0;
    while (numBits > 0) {
      if (this.bytePos >= this.buf.length) return result;
      const remainingInByte = 8 - this.bitPos;
      const toRead = Math.min(numBits, remainingInByte);
      const currentByte = this.buf[this.bytePos];
      const shift = remainingInByte - toRead;
      const mask = (1 << toRead) - 1 & 255;
      const bits = currentByte >> shift & mask;
      result = result << toRead | bits;
      this.bitPos += toRead;
      if (this.bitPos >= 8) {
        this.bitPos = 0;
        this.bytePos += 1;
      }
      numBits -= toRead;
    }
    return result >>> 0;
  }
  skipBits(n) {
    this.readBits(n);
  }
  get bytePosition() {
    return this.bitPos === 0 ? this.bytePos : this.bytePos + 1;
  }
};
function readSpliceTime(r, ptsAdjustmentTicks) {
  const timeSpecifiedFlag = r.readBits(1) === 1;
  if (!timeSpecifiedFlag) {
    r.readBits(7);
    return void 0;
  }
  r.readBits(6);
  const high = r.readBits(1);
  const low = r.readBits(32);
  const ptsTicks = (high * 4294967296 + low + ptsAdjustmentTicks) % 8589934592;
  return ptsTicks / 9e4;
}
function markerTypeFromSegmentationTypeId(segmentationTypeId) {
  switch (segmentationTypeId) {
    case 16:
    case 34:
    case 48:
    case 50:
    case 52:
    case 54:
      return "start";
    case 17:
    case 35:
    case 49:
    case 51:
    case 53:
    case 55:
      return "end";
    default:
      return void 0;
  }
}
function parseScte35SegmentationDescriptor(descriptor) {
  if (descriptor.length < 11) {
    return void 0;
  }
  const identifier = String.fromCharCode(
    descriptor[0] ?? 0,
    descriptor[1] ?? 0,
    descriptor[2] ?? 0,
    descriptor[3] ?? 0
  );
  if (identifier !== "CUEI") {
    return void 0;
  }
  const segmentationEventId = (descriptor[4] ?? 0) * 16777216 + ((descriptor[5] ?? 0) << 16) + ((descriptor[6] ?? 0) << 8) + (descriptor[7] ?? 0);
  const cancelIndicator = ((descriptor[8] ?? 0) & 128) !== 0;
  if (cancelIndicator) {
    return void 0;
  }
  const flags = descriptor[9] ?? 0;
  const programSegmentationFlag = (flags & 128) !== 0;
  const segmentationDurationFlag = (flags & 64) !== 0;
  let offset = 10;
  if (!programSegmentationFlag) {
    const componentCount = descriptor[offset] ?? 0;
    offset += 1 + componentCount * 6;
    if (offset > descriptor.length) {
      return void 0;
    }
  }
  let durationSeconds = void 0;
  if (segmentationDurationFlag) {
    if (offset + 5 > descriptor.length) {
      return void 0;
    }
    const durationTicks = (descriptor[offset] ?? 0) * 4294967296 + (descriptor[offset + 1] ?? 0) * 16777216 + ((descriptor[offset + 2] ?? 0) << 16) + ((descriptor[offset + 3] ?? 0) << 8) + (descriptor[offset + 4] ?? 0);
    durationSeconds = durationTicks / 9e4;
    offset += 5;
  }
  if (offset + 2 > descriptor.length) {
    return void 0;
  }
  offset += 1;
  const segmentationUpidLength = descriptor[offset] ?? 0;
  offset += 1 + segmentationUpidLength;
  if (offset >= descriptor.length) {
    return void 0;
  }
  const segmentationTypeId = descriptor[offset] ?? 0;
  const markerType = markerTypeFromSegmentationTypeId(segmentationTypeId);
  if (!markerType) {
    return void 0;
  }
  return {
    type: markerType,
    ...durationSeconds !== void 0 ? { durationSeconds } : {},
    segmentationEventId
  };
}
function parseScte35SegmentationDescriptors(data, offset) {
  if (offset + 2 > data.length) {
    return void 0;
  }
  const descriptorLoopLength = (data[offset] ?? 0) << 8 | (data[offset + 1] ?? 0);
  let descriptorOffset = offset + 2;
  const descriptorEnd = Math.min(
    data.length,
    descriptorOffset + descriptorLoopLength
  );
  while (descriptorOffset + 2 <= descriptorEnd) {
    const descriptorTag = data[descriptorOffset] ?? 0;
    const descriptorLength = data[descriptorOffset + 1] ?? 0;
    const descriptorDataStart = descriptorOffset + 2;
    const descriptorDataEnd = descriptorDataStart + descriptorLength;
    if (descriptorDataEnd > descriptorEnd) {
      return void 0;
    }
    if (descriptorTag === 2) {
      const descriptorMarker = parseScte35SegmentationDescriptor(
        data.subarray(descriptorDataStart, descriptorDataEnd)
      );
      if (descriptorMarker) {
        return descriptorMarker;
      }
    }
    descriptorOffset = descriptorDataEnd;
  }
  return void 0;
}
function parseScte35Binary(data) {
  const r = new BitReader(data);
  const tableId = r.readBits(8);
  if (tableId !== 252) return void 0;
  r.readBits(1);
  r.readBits(1);
  r.readBits(2);
  const sectionLength = r.readBits(12);
  if (sectionLength <= 0 || data.length < 3 + sectionLength) {
    return void 0;
  }
  r.readBits(8);
  r.readBits(1);
  r.readBits(6);
  const ptsAdjHigh = r.readBits(1);
  const ptsAdjLow = r.readBits(32);
  const ptsAdjustmentTicks = ptsAdjHigh * 4294967296 + ptsAdjLow;
  r.readBits(8);
  r.readBits(12);
  const spliceCommandLength = r.readBits(12);
  const spliceCommandType = r.readBits(8);
  let markerType;
  let durationSeconds = void 0;
  let ptsSeconds = void 0;
  let spliceEventId = void 0;
  const commandBodyStart = r.bytePosition;
  if (spliceCommandType === 5) {
    spliceEventId = r.readBits(32);
    const cancel = r.readBits(1) === 1;
    r.readBits(7);
    if (cancel) return void 0;
    const outOfNetwork = r.readBits(1) === 1;
    const programSpliceFlag = r.readBits(1) === 1;
    const durationFlag = r.readBits(1) === 1;
    const spliceImmediateFlag = r.readBits(1) === 1;
    r.readBits(4);
    markerType = outOfNetwork ? "start" : "end";
    if (programSpliceFlag && !spliceImmediateFlag) {
      ptsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
    } else if (!programSpliceFlag) {
      const componentCount = r.readBits(8);
      for (let i = 0; i < componentCount; i++) {
        r.readBits(8);
        if (!spliceImmediateFlag) {
          const componentPtsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
          if (ptsSeconds === void 0) {
            ptsSeconds = componentPtsSeconds;
          }
        }
      }
    }
    if (durationFlag) {
      r.readBits(6);
      r.readBits(1);
      const high = r.readBits(1);
      const low = r.readBits(32);
      const durationTicks = high * 4294967296 + low;
      durationSeconds = durationTicks / 9e4;
    }
    r.readBits(16);
    r.readBits(8);
    r.readBits(8);
  } else if (spliceCommandType === 6) {
    ptsSeconds = readSpliceTime(r, ptsAdjustmentTicks);
  } else {
    return void 0;
  }
  const expectedDescriptorOffset = spliceCommandLength === 4095 ? r.bytePosition : commandBodyStart + spliceCommandLength;
  const descriptorMarker = parseScte35SegmentationDescriptors(
    data,
    expectedDescriptorOffset
  );
  if (descriptorMarker) {
    markerType = descriptorMarker.type;
    if (durationSeconds === void 0) {
      durationSeconds = descriptorMarker.durationSeconds;
    }
  }
  if (!markerType) {
    return void 0;
  }
  const marker = {
    type: markerType,
    ...ptsSeconds !== void 0 ? { ptsSeconds } : {},
    ...durationSeconds !== void 0 ? { durationSeconds } : {},
    raw: {
      splice_command_type: spliceCommandType,
      ...spliceEventId !== void 0 ? { splice_event_id: spliceEventId } : {},
      ...descriptorMarker?.segmentationEventId !== void 0 ? { segmentation_event_id: descriptorMarker.segmentationEventId } : {}
    }
  };
  return marker;
}

// src/player/Scte35CueManager.ts
var Scte35CueManager = class {
  constructor(debug, callbacks) {
    this.pendingScte35Cues = /* @__PURE__ */ new Map();
    this.recentScte35CueKeys = /* @__PURE__ */ new Map();
    this.recentScte35CueLimit = 200;
    this.tsScte35Pids = /* @__PURE__ */ new Set();
    this.pmtPids = /* @__PURE__ */ new Set();
    this.processedTsScte35Sections = /* @__PURE__ */ new Set();
    this.debug = debug;
    this.callbacks = callbacks;
  }
  get pendingScte35CueKey() {
    return this.pendingScte35CueKeyValue;
  }
  set pendingScte35CueKey(value) {
    this.pendingScte35CueKeyValue = value;
  }
  get activeScte35BreakKey() {
    return this.activeScte35BreakKeyValue;
  }
  set activeScte35BreakKey(value) {
    this.activeScte35BreakKeyValue = value;
  }
  get scheduledScte35BreakKey() {
    return this.scheduledScte35BreakKeyValue;
  }
  set scheduledScte35BreakKey(value) {
    this.scheduledScte35BreakKeyValue = value;
  }
  createScte35CueContext(source, readiness, frag) {
    const context = { source, readiness };
    if (typeof frag?.sn === "number") {
      context.fragmentSn = frag.sn;
    }
    const fragmentStartSec = getFragmentStartSeconds(frag);
    if (fragmentStartSec !== void 0) {
      context.fragmentStartSeconds = fragmentStartSec;
    }
    return context;
  }
  onScte35Cue(marker, context) {
    const cue = this.createScte35Cue(marker, context);
    const pendingKey = this.getPendingScte35CueKey(cue);
    if (context.readiness === "early") {
      this.pendingScte35Cues.set(pendingKey, cue);
      this.trimRecentScte35CueMaps();
      if (marker.type === "start") {
        this.callbacks.onEarlyStartCue(marker, context.fragmentSn, cue.key);
      }
      return;
    }
    if (marker.type === "end" && !this.shouldAcceptScte35EndCue(cue)) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ignoring SCTE-35 end cue", {
          key: cue.key,
          activeScte35BreakKey: this.activeScte35BreakKeyValue,
          inAdBreak: this.callbacks.isInAdBreak(),
          source: cue.source
        });
      }
      return;
    }
    if (marker.type === "start" && this.hasRecentlyHandledScte35Cue(cue)) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ignoring duplicate SCTE-35 start cue", {
          key: cue.key,
          source: cue.source
        });
      }
      return;
    }
    this.pendingScte35Cues.delete(pendingKey);
    this.markScte35CueHandled(cue);
    this.callbacks.onPlaybackCue(marker, cue.key);
  }
  activatePendingScte35CuesForFragment(frag) {
    const fragmentSn = typeof frag?.sn === "number" ? frag.sn : void 0;
    if (fragmentSn === void 0) {
      return;
    }
    const matchingCues = Array.from(this.pendingScte35Cues.entries()).filter(
      ([, cue]) => cue.fragmentSn === fragmentSn
    );
    matchingCues.forEach(([pendingKey, cue]) => {
      this.pendingScte35Cues.delete(pendingKey);
      const context = {
        source: cue.source,
        readiness: "playback"
      };
      context.fragmentSn = fragmentSn;
      const fragmentStartSec = cue.fragmentStartSeconds ?? getFragmentStartSeconds(frag);
      if (fragmentStartSec !== void 0) {
        context.fragmentStartSeconds = fragmentStartSec;
      }
      this.onScte35Cue(cue.marker, context);
    });
  }
  createScte35Cue(marker, context) {
    const cue = {
      marker,
      source: context.source,
      readiness: context.readiness,
      key: this.getScte35CueKey(marker, context)
    };
    if (context.fragmentSn !== void 0) {
      cue.fragmentSn = context.fragmentSn;
    }
    if (context.fragmentStartSeconds !== void 0) {
      cue.fragmentStartSeconds = context.fragmentStartSeconds;
    }
    return cue;
  }
  getScte35CueKey(marker, context) {
    const raw = this.getScte35MarkerRaw(marker);
    const spliceEventId = this.toIdentityString(raw?.splice_event_id);
    if (spliceEventId) {
      return `splice:${spliceEventId}`;
    }
    const segmentationEventId = this.toIdentityString(raw?.segmentation_event_id);
    if (segmentationEventId) {
      return `segmentation:${segmentationEventId}`;
    }
    const daterangeId = this.toIdentityString(raw?.attrs?.ID);
    if (daterangeId) {
      return `daterange:${daterangeId}`;
    }
    const scteValue = this.toIdentityString(raw?.attrs?.["SCTE35-OUT"]) || this.toIdentityString(raw?.attrs?.["SCTE35-IN"]);
    if (scteValue) {
      return `daterange-scte:${scteValue}`;
    }
    if (typeof marker.ptsSeconds === "number" && Number.isFinite(marker.ptsSeconds)) {
      return `pts:${Math.round(marker.ptsSeconds * 2) / 2}`;
    }
    if (context.fragmentSn !== void 0) {
      return `fragment:${context.fragmentSn}`;
    }
    return `${context.source}:wallclock:${Math.floor(Date.now() / 1e3)}`;
  }
  getPendingScte35CueKey(cue) {
    return `${cue.marker.type}:${cue.key}:${cue.fragmentSn !== void 0 ? cue.fragmentSn : "unknown"}`;
  }
  getScte35MarkerRaw(marker) {
    return typeof marker.raw === "object" && marker.raw !== null ? marker.raw : {};
  }
  toIdentityString(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
    return void 0;
  }
  isStrongScte35CueKey(key) {
    return !!(key && (key.startsWith("splice:") || key.startsWith("segmentation:") || key.startsWith("daterange:") || key.startsWith("daterange-scte:")));
  }
  hasRecentlyHandledScte35Cue(cue) {
    if (this.activeScte35BreakKeyValue === cue.key || this.scheduledScte35BreakKeyValue === cue.key) {
      return true;
    }
    const recentKey = `${cue.marker.type}:${cue.key}`;
    return this.recentScte35CueKeys.has(recentKey);
  }
  markScte35CueHandled(cue) {
    this.recentScte35CueKeys.set(`${cue.marker.type}:${cue.key}`, Date.now());
    this.trimRecentScte35CueMaps();
  }
  trimRecentScte35CueMaps() {
    while (this.recentScte35CueKeys.size > this.recentScte35CueLimit) {
      const firstKey = this.recentScte35CueKeys.keys().next().value;
      if (!firstKey) {
        break;
      }
      this.recentScte35CueKeys.delete(firstKey);
    }
    while (this.pendingScte35Cues.size > this.recentScte35CueLimit) {
      const firstKey = this.pendingScte35Cues.keys().next().value;
      if (!firstKey) {
        break;
      }
      this.pendingScte35Cues.delete(firstKey);
    }
  }
  shouldAcceptScte35EndCue(cue) {
    if (!this.callbacks.isInAdBreak()) {
      return false;
    }
    if (this.isStrongScte35CueKey(this.activeScte35BreakKeyValue) && this.isStrongScte35CueKey(cue.key) && this.activeScte35BreakKeyValue !== cue.key) {
      return false;
    }
    return true;
  }
};

// src/player/AdConfigManager.ts
var AdConfigManager = class {
  constructor(config, video) {
    this.vmapBreaks = [];
    this.consumedVmapBreakIds = /* @__PURE__ */ new Set();
    this.streamCorrelator = generateCorrelator();
    this.consentSignals = {};
    this.podCounter = 0;
    this.podAssignedByPrefetch = false;
    this.adRequestPositionInBreak = 0;
    this.lastHeartbeatTime = 0;
    this.defaultPodMaxAds = 6;
    this.config = config;
    this.video = video;
  }
  get debug() {
    return !!this.config.debugAdTiming;
  }
  isVmapEnabled() {
    return !!(this.config.isVmap && this.config.vmapUrl?.trim());
  }
  async fetchAdConfiguration() {
    if (this.isVmapEnabled()) {
      await this.fetchAndParseVmap(this.config.vmapUrl);
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] VMAP mode enabled");
      }
      return;
    }
    const vastMode = this.config.vastMode || "default";
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] VAST mode:", vastMode);
    }
    if (vastMode === "adstorm") {
      if (!this.config.licenseKey) {
        if (this.debug) {
          console.warn(
            "[StormcloudVideoPlayer] AdStorm mode requires a license key"
          );
        }
        return;
      }
      const vastEndpoint = `https://adstorm.co/api-adstorm-dev/adstorm/vast/${this.config.licenseKey}`;
      this.apiVastTagUrl = vastEndpoint;
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Using AdStorm VAST endpoint (adstorm mode):",
          vastEndpoint
        );
      }
      return;
    }
    if (this.config.vastTagUrl) {
      this.apiVastTagUrl = normalizeVastTagUrl(this.config.vastTagUrl);
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Using custom VAST tag URL:",
          this.apiVastTagUrl
        );
      }
      return;
    }
    const apiUrl = "https://adstorm.co/api-adstorm-dev/adstorm/ads/web";
    if (this.debug) {
      console.log(
        "[StormcloudVideoPlayer] Fetching ad configuration from:",
        apiUrl
      );
    }
    const headers = {};
    if (this.config.licenseKey) {
      headers["Authorization"] = `Bearer ${this.config.licenseKey}`;
    }
    const response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      if (this.debug) {
        console.warn(
          `[StormcloudVideoPlayer] Failed to fetch ad configuration: ${response.status}`
        );
      }
      return;
    }
    const data = await response.json();
    const imaPayload = data.response?.ima?.["publisherdesk.ima"]?.payload;
    if (imaPayload) {
      let decodedPayload = imaPayload;
      try {
        decodedPayload = decodeURIComponent(imaPayload);
      } catch {
        console.warn("[StormcloudVideoPlayer] imaPayload was not URI-encoded; using as-is:", imaPayload);
      }
      console.log("[StormcloudVideoPlayer] Raw VAST tag URL (from API):", decodedPayload);
      this.apiVastTagUrl = normalizeVastTagUrl(decodedPayload);
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Normalized VAST tag URL:",
          this.apiVastTagUrl
        );
      }
    } else {
      if (this.debug) {
        console.warn(
          "[StormcloudVideoPlayer] No VAST tag URL found in /ads/web response"
        );
      }
    }
    const numberAds = data.response?.options?.vast?.cue_tones?.number_ads;
    if (numberAds != null && numberAds > 0) {
      this.apiNumberAds = numberAds;
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Number of ads per break from API:",
          this.apiNumberAds
        );
      }
    }
  }
  async fetchAndParseVmap(vmapUrl) {
    if (!vmapUrl.trim()) {
      return;
    }
    try {
      const response = await fetch(vmapUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch VMAP (${response.status})`);
      }
      const vmapXml = await response.text();
      this.vmapBreaks = this.parseVmapToBreaks(vmapXml);
      this.consumedVmapBreakIds.clear();
      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] Loaded ${this.vmapBreaks.length} VMAP ad break(s) from:`,
          vmapUrl
        );
      }
    } catch (error) {
      this.vmapBreaks = [];
      this.consumedVmapBreakIds.clear();
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Failed to load VMAP:", error);
      }
    }
  }
  parseVmapToBreaks(vmapXml) {
    if (typeof DOMParser === "undefined") {
      return [];
    }
    const doc = new DOMParser().parseFromString(vmapXml, "application/xml");
    if (doc.querySelector("parsererror")) {
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Invalid VMAP XML received");
      }
      return [];
    }
    const VMAP_NS = "http://www.iab.net/videosuite/vmap";
    let adBreakNodes = Array.from(doc.getElementsByTagNameNS(VMAP_NS, "AdBreak"));
    if (adBreakNodes.length === 0) {
      adBreakNodes = Array.from(
        doc.querySelectorAll("AdBreak, vmap\\:AdBreak")
      );
    }
    if (adBreakNodes.length === 0) {
      adBreakNodes = Array.from(doc.getElementsByTagName("*")).filter(
        (el) => el.localName === "AdBreak"
      );
    }
    const parsed = [];
    adBreakNodes.forEach((node, index) => {
      const timeOffsetRaw = (node.getAttribute("timeOffset") || "").trim();
      const startTimeMs = this.parseVmapTimeOffsetToMs(timeOffsetRaw);
      if (startTimeMs == null) {
        return;
      }
      let adTagNode = node.getElementsByTagNameNS(VMAP_NS, "AdTagURI")[0];
      if (!adTagNode) {
        adTagNode = node.querySelector("AdTagURI, vmap\\:AdTagURI") ?? void 0;
      }
      const adTagUrl = (adTagNode?.textContent || "").trim();
      if (!adTagUrl) {
        return;
      }
      const breakId = node.getAttribute("breakId") || `vmap-break-${index}-${timeOffsetRaw || "unknown"}`;
      parsed.push({
        id: breakId,
        startTimeMs,
        vastTagUrl: adTagUrl
      });
    });
    parsed.sort((a, b) => {
      const aStart = a.startTimeMs < 0 ? Number.MAX_SAFE_INTEGER : a.startTimeMs;
      const bStart = b.startTimeMs < 0 ? Number.MAX_SAFE_INTEGER : b.startTimeMs;
      return aStart - bStart;
    });
    return parsed;
  }
  parseVmapTimeOffsetToMs(timeOffset) {
    if (!timeOffset) {
      return void 0;
    }
    const normalized = timeOffset.trim().toLowerCase();
    if (normalized === "start") {
      return 0;
    }
    if (normalized === "end") {
      return -1;
    }
    const hms = timeOffset.match(
      /^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/
    );
    if (hms) {
      const [, hh, mm, ss, ms = "0"] = hms;
      const hours = Number(hh);
      const minutes = Number(mm);
      const seconds = Number(ss);
      const millis = Number(ms.padEnd(3, "0").slice(0, 3));
      return (hours * 3600 + minutes * 60 + seconds) * 1e3 + millis;
    }
    const percent = timeOffset.match(/^(\d+(?:\.\d+)?)%$/);
    if (percent) {
      const ratio = Number(percent[1]) / 100;
      const durationSec = this.video.duration;
      if (Number.isFinite(durationSec) && durationSec > 0) {
        return Math.floor(durationSec * 1e3 * ratio);
      }
      return void 0;
    }
    return void 0;
  }
  getAdBreakKey(adBreak) {
    if (adBreak.id) {
      return adBreak.id;
    }
    return `${adBreak.startTimeMs}-${adBreak.vastTagUrl || ""}`;
  }
  resolveBreakStartMs(adBreak) {
    if (adBreak.startTimeMs >= 0) {
      return adBreak.startTimeMs;
    }
    if (adBreak.startTimeMs === -1) {
      const durationSec = this.video.duration;
      if (Number.isFinite(durationSec) && durationSec > 0) {
        return Math.floor(durationSec * 1e3);
      }
    }
    return void 0;
  }
  findCurrentOrNextBreak(nowMs) {
    const schedule = this.vmapBreaks;
    let candidate;
    const tol = this.config.driftToleranceMs ?? 1e3;
    for (const b of schedule) {
      if (this.consumedVmapBreakIds.has(this.getAdBreakKey(b))) {
        continue;
      }
      const breakStartMs = this.resolveBreakStartMs(b);
      if (breakStartMs == null) {
        continue;
      }
      if (breakStartMs <= nowMs + tol && (candidate == null || breakStartMs > (this.resolveBreakStartMs(candidate) ?? 0))) {
        candidate = b;
      }
    }
    return candidate;
  }
  findBreakForTime(nowMs) {
    const schedule = this.vmapBreaks;
    for (const b of schedule) {
      if (this.consumedVmapBreakIds.has(this.getAdBreakKey(b))) {
        continue;
      }
      const breakStartMs = this.resolveBreakStartMs(b);
      if (breakStartMs == null) {
        continue;
      }
      if (b.durationMs) {
        const end = breakStartMs + b.durationMs;
        if (nowMs >= breakStartMs && nowMs < end) {
          return b;
        }
        continue;
      }
      if (nowMs >= breakStartMs) {
        return b;
      }
    }
    return void 0;
  }
  selectVastTagsForBreak(b) {
    if (!b || !b.vastTagUrl) return void 0;
    if (b.vastTagUrl.includes(",")) {
      return b.vastTagUrl.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
    }
    return [b.vastTagUrl];
  }
  beginNewAdPod() {
    this.podCounter++;
    this.adRequestPositionInBreak = 0;
  }
  generateVastUrlsWithCorrelators(baseUrl, count, adPlayer, palNonce, inAdBreak) {
    const urls = [];
    for (let i = 0; i < count; i++) {
      this.adRequestPositionInBreak++;
      const adWillPlayMuted = inAdBreak ? adPlayer.getOriginalMutedState() : this.video.muted;
      const envSignals = resolveVastEnvironmentSignals(!!this.config.ctvAdRequest);
      const urlWithMacros = applyVastMacros(baseUrl, {
        correlator: generateCorrelator(),
        streamCorrelator: this.streamCorrelator,
        pod: this.podCounter > 0 ? this.podCounter : void 0,
        adPosition: this.adRequestPositionInBreak,
        isCtv: envSignals.isCtv,
        contentUrl: envSignals.contentUrl,
        pageUrl: !envSignals.isCtv && typeof window !== "undefined" ? window.location.href : void 0,
        adWillPlayMuted,
        adWillAutoPlay: !!this.config.autoplay,
        appId: envSignals.appId,
        appName: envSignals.appName,
        sessionId: envSignals.sessionId,
        deviceId: envSignals.deviceId,
        deviceIdType: envSignals.deviceIdType,
        limitAdTracking: envSignals.limitAdTracking,
        deviceTypeHint: envSignals.deviceTypeHint,
        adTest: this.config.adTest,
        consent: this.consentSignals
      });
      const finalUrl = palNonce.injectNonce(urlWithMacros);
      console.log(
        `[StormcloudVideoPlayer] Resolved ad request URL (pod=${this.podCounter}, pos=${this.adRequestPositionInBreak}):`,
        finalUrl
      );
      urls.push(finalUrl);
    }
    return urls;
  }
  resolvePodParams(breakDurationMs) {
    const maxDurationMs = breakDurationMs != null && breakDurationMs > 0 ? breakDurationMs : this.config.podMaxDurationMs;
    const maxAds = this.config.podMaxAds && this.config.podMaxAds > 0 ? this.config.podMaxAds : this.apiNumberAds && this.apiNumberAds > 0 ? this.apiNumberAds : this.defaultPodMaxAds;
    const minDurationMs = this.config.podExactDuration && maxDurationMs != null ? maxDurationMs : 0;
    return { maxAds, minDurationMs, maxDurationMs };
  }
  generatePodVastUrl(baseUrl, podParams, adPlayer, palNonce, inAdBreak) {
    const adWillPlayMuted = inAdBreak ? adPlayer.getOriginalMutedState() : this.video.muted;
    const envSignals = resolveVastEnvironmentSignals(!!this.config.ctvAdRequest);
    const urlWithMacros = applyVastMacros(baseUrl, {
      correlator: generateCorrelator(),
      streamCorrelator: this.streamCorrelator,
      pod: this.podCounter > 0 ? this.podCounter : void 0,
      podMaxAds: podParams.maxAds,
      podMinDurationMs: podParams.minDurationMs,
      podMaxDurationMs: podParams.maxDurationMs,
      isCtv: envSignals.isCtv,
      contentUrl: envSignals.contentUrl,
      pageUrl: !envSignals.isCtv && typeof window !== "undefined" ? window.location.href : void 0,
      adWillPlayMuted,
      adWillAutoPlay: !!this.config.autoplay,
      appId: envSignals.appId,
      appName: envSignals.appName,
      sessionId: envSignals.sessionId,
      deviceId: envSignals.deviceId,
      deviceIdType: envSignals.deviceIdType,
      limitAdTracking: envSignals.limitAdTracking,
      deviceTypeHint: envSignals.deviceTypeHint,
      adTest: this.config.adTest,
      consent: this.consentSignals
    });
    const finalUrl = palNonce.injectNonce(urlWithMacros);
    console.log(
      `[StormcloudVideoPlayer] Resolved optimized-pod request URL (pod=${this.podCounter}, pmad=${podParams.maxAds}, pmnd=${podParams.minDurationMs}, pmxd=${podParams.maxDurationMs ?? "?"}):`,
      finalUrl
    );
    return finalUrl;
  }
  // --- Tracking ---
  initializeTracking() {
    sendInitialTracking(this.config.licenseKey, buildAnalyticsContext(this.config)).then(() => {
      this.heartbeatInterval = window.setInterval(() => {
        this.sendHeartbeatIfNeeded();
      }, 5e3);
    }).catch((error) => {
      if (this.debug) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to send initial tracking:",
          error
        );
      }
      this.heartbeatInterval = window.setInterval(() => {
        this.sendHeartbeatIfNeeded();
      }, 5e3);
    });
  }
  sendHeartbeatIfNeeded() {
    const now = Date.now();
    if (!this.lastHeartbeatTime || now - this.lastHeartbeatTime > 3e4) {
      this.lastHeartbeatTime = now;
      sendHeartbeat(this.config.licenseKey, buildAnalyticsContext(this.config)).catch((error) => {
        if (this.debug) {
          console.warn(
            "[StormcloudVideoPlayer] Failed to send heartbeat:",
            error
          );
        }
      });
    }
  }
  destroyTracking() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = void 0;
    }
  }
};

// src/player/AdTimingService.ts
var AdTimingService = class {
  constructor(config, video, callbacks) {
    this.adRequestWatchdogToken = null;
    this.adFailsafeToken = null;
    this.ptsDriftEmaMs = 0;
    this.adBreakPlayedDurationMs = 0;
    this.adBreakEpoch = 0;
    this.adRequestTokenCounter = 0;
    this.activeAdRequestToken = null;
    this.currentAdIndex = 0;
    this.totalAdsInBreak = 0;
    this.totalAdRequestsInBreak = 0;
    this.maxTotalAdRequestsPerBreak = 10;
    this.lastAdRequestTime = 0;
    this.consecutiveFailures = 0;
    this.maxConsecutiveFailures = 5;
    this.minAdRequestIntervalMs = 3e3;
    this.backoffBaseMs = 1e3;
    this.maxBackoffMs = 15e3;
    this.defaultMinRemainingMsToStartAd = 4e3;
    this.failedVastUrls = /* @__PURE__ */ new Set();
    this.temporaryFailureUrls = /* @__PURE__ */ new Map();
    this.temporaryFailureCooldownMs = 3e4;
    this.globalConsecutiveNoFills = 0;
    this.globalNoFillThreshold = 3;
    this.globalNoFillCooldownUntil = 0;
    this.globalNoFillBackoffBaseMs = 5e3;
    this.globalNoFillBackoffMaxMs = 6e4;
    this.globalLastGamRequestTime = 0;
    this.globalMinGamIntervalMs = 3e3;
    this.config = config;
    this.video = video;
    this.callbacks = callbacks;
  }
  get debug() {
    return !!this.config.debugAdTiming;
  }
  // --- Ad stop timer ---
  scheduleAdStopCountdown(remainingMs) {
    this.clearAdStopTimer();
    const ms = Math.max(0, Math.floor(remainingMs));
    if (ms === 0) {
      this.ensureAdStoppedByTimer();
      return;
    }
    this.adStopTimerId = window.setTimeout(() => {
      this.ensureAdStoppedByTimer();
    }, ms);
  }
  clearAdStopTimer() {
    if (this.adStopTimerId != null) {
      clearTimeout(this.adStopTimerId);
      this.adStopTimerId = void 0;
    }
  }
  ensureAdStoppedByTimer() {
    this.adStopTimerId = void 0;
    this.callbacks.onAdStopTimerFired();
  }
  // --- Ad start timer ---
  scheduleAdStartIn(delayMs, marker = { type: "start" }, cueKey) {
    this.clearAdStartTimer();
    const ms = Math.max(0, Math.floor(delayMs));
    if (ms === 0) {
      this.callbacks.onAdStartTimerFired(marker, cueKey);
      return;
    }
    this.adStartTimerId = window.setTimeout(() => {
      this.callbacks.onAdStartTimerFired(marker, cueKey);
    }, ms);
  }
  clearAdStartTimer() {
    if (this.adStartTimerId != null) {
      clearTimeout(this.adStartTimerId);
      this.adStartTimerId = void 0;
    }
  }
  // --- PTS drift ---
  updatePtsDrift(ptsSecondsSample) {
    const sampleMs = (this.video.currentTime - ptsSecondsSample) * 1e3;
    if (!Number.isFinite(sampleMs) || Math.abs(sampleMs) > 6e4) return;
    const alpha = 0.1;
    this.ptsDriftEmaMs = this.ptsDriftEmaMs * (1 - alpha) + sampleMs * alpha;
  }
  resolveScteBreakStartWallClockMs(marker) {
    const nowWallClockMs = Date.now();
    if (typeof marker.ptsSeconds !== "number") {
      return nowWallClockMs;
    }
    const nowMs = this.video.currentTime * 1e3;
    const estimatedCurrentPtsMs = nowMs - this.ptsDriftEmaMs;
    const deltaMs = marker.ptsSeconds * 1e3 - estimatedCurrentPtsMs;
    if (!Number.isFinite(deltaMs)) {
      return nowWallClockMs;
    }
    return nowWallClockMs + Math.floor(deltaMs);
  }
  // --- Duration boundary ---
  setAdBreakDurationBoundary(durationMs) {
    this.expectedAdBreakDurationMs = durationMs;
    if (durationMs != null && this.currentAdBreakStartWallClockMs != null) {
      this.scteAdBreakEndWallClockMs = this.currentAdBreakStartWallClockMs + durationMs;
    } else {
      this.scteAdBreakEndWallClockMs = void 0;
    }
  }
  scheduleAdStopAtBreakBoundary() {
    if (this.expectedAdBreakDurationMs == null) {
      return;
    }
    this.scheduleAdStopCountdown(this.getWallClockRemainingAdMs());
  }
  // --- Duration budget ---
  getWallClockRemainingAdMs() {
    const endWallClockMs = this.getAdBreakEndWallClockMs();
    if (endWallClockMs != null) {
      return Math.max(0, endWallClockMs - Date.now());
    }
    return 0;
  }
  getAdBreakEndWallClockMs() {
    if (this.scteAdBreakEndWallClockMs != null) {
      return this.scteAdBreakEndWallClockMs;
    }
    if (this.expectedAdBreakDurationMs == null || this.currentAdBreakStartWallClockMs == null) {
      return void 0;
    }
    return this.currentAdBreakStartWallClockMs + this.expectedAdBreakDurationMs;
  }
  getDurationBudgetRemainingMs() {
    if (this.expectedAdBreakDurationMs == null) {
      return 0;
    }
    return Math.max(0, this.expectedAdBreakDurationMs - this.adBreakPlayedDurationMs);
  }
  getLoadedAdDurationMs(adController) {
    const durationSec = adController.getLoadedAdDuration?.();
    if (durationSec == null || durationSec <= 0) {
      return 0;
    }
    return Math.round(durationSec * 1e3);
  }
  adFitsRemainingBudget(durationMs) {
    if (durationMs <= 0) {
      return true;
    }
    return durationMs <= this.getDurationBudgetRemainingMs();
  }
  logAdDurationDecision(accepted, durationMs, adController) {
    if (!this.debug) {
      return;
    }
    const remainingBudget = this.getDurationBudgetRemainingMs();
    const adId = adController.getLoadedAdId?.() ?? "unknown";
    if (accepted) {
      console.log(
        `[AD-DURATION] Accepted ad id=${adId}, duration=${durationMs}ms, budget remaining after play=${Math.max(0, remainingBudget - durationMs)}ms`
      );
    } else {
      console.log(
        `[AD-DURATION] Rejected ad id=${adId}, duration=${durationMs}ms exceeds remaining budget=${remainingBudget}ms \u2014 requesting another ad`
      );
    }
  }
  recordPlayedAdDuration(adController) {
    const durationMs = this.getLoadedAdDurationMs(adController);
    if (durationMs <= 0) {
      return;
    }
    this.adBreakPlayedDurationMs += durationMs;
    if (this.debug) {
      console.log(
        `[AD-DURATION] Played ${durationMs}ms \u2014 total played=${this.adBreakPlayedDurationMs}ms / ${this.expectedAdBreakDurationMs ?? "?"}ms`
      );
    }
  }
  ensureLoadedAdFitsBudget(adController) {
    const durationMs = this.getLoadedAdDurationMs(adController);
    const fits = this.adFitsRemainingBudget(durationMs);
    this.logAdDurationDecision(fits, durationMs, adController);
    return fits;
  }
  rejectLoadedAdForDuration(adController) {
    try {
      adController.destroy();
    } catch {
    }
  }
  markAdStarted() {
    this.currentAdIndex++;
    this.totalAdsInBreak = this.currentAdIndex;
    this.totalAdRequestsInBreak++;
    this.lastAdRequestTime = Date.now();
  }
  notePodAdStarted() {
    this.currentAdIndex++;
    this.totalAdsInBreak = this.currentAdIndex;
  }
  getRemainingAdMs() {
    const wallClockRemaining = this.getWallClockRemainingAdMs();
    const budgetRemaining = this.getDurationBudgetRemainingMs();
    if (this.expectedAdBreakDurationMs == null) {
      return wallClockRemaining;
    }
    return Math.min(wallClockRemaining, budgetRemaining);
  }
  getMinRemainingMsToStartAd() {
    const configured = this.config.minRemainingMsToStartAd;
    if (typeof configured === "number" && configured >= 0) {
      return configured;
    }
    return this.defaultMinRemainingMsToStartAd;
  }
  hasTimeToStartAnotherAd() {
    const minRemaining = this.getMinRemainingMsToStartAd();
    return this.getWallClockRemainingAdMs() > minRemaining && this.getDurationBudgetRemainingMs() > minRemaining;
  }
  // --- Circuit breaker ---
  isTemporaryAdError(error) {
    const message = error?.message?.toLowerCase() || "";
    if (message.includes("no ads available") || message.includes("no fill") || message.includes("nofill")) {
      return true;
    }
    if (message.includes("timeout")) {
      return true;
    }
    if (message.includes("rate limit") || message.includes("too many requests")) {
      return true;
    }
    if (message.includes("ad playback error")) {
      return true;
    }
    return false;
  }
  isUrlInCooldown(vastTagUrl) {
    const failureTime = this.temporaryFailureUrls.get(vastTagUrl);
    if (failureTime == null) {
      return false;
    }
    const elapsed = Date.now() - failureTime;
    if (elapsed >= this.temporaryFailureCooldownMs) {
      this.temporaryFailureUrls.delete(vastTagUrl);
      return false;
    }
    return true;
  }
  cleanupTemporaryFailures() {
    const now = Date.now();
    for (const [url, timestamp] of this.temporaryFailureUrls) {
      if (now - timestamp >= this.temporaryFailureCooldownMs) {
        this.temporaryFailureUrls.delete(url);
      }
    }
  }
  isGamInCooldown() {
    return Date.now() < this.globalNoFillCooldownUntil;
  }
  recordGamNoFill() {
    this.globalConsecutiveNoFills++;
    if (this.globalConsecutiveNoFills >= this.globalNoFillThreshold) {
      const exponent = this.globalConsecutiveNoFills - this.globalNoFillThreshold;
      const backoff = Math.min(
        this.globalNoFillBackoffBaseMs * Math.pow(2, exponent),
        this.globalNoFillBackoffMaxMs
      );
      this.globalNoFillCooldownUntil = Date.now() + backoff;
      if (this.debug) {
        console.log(
          `[CIRCUIT-BREAKER] GAM cooldown activated: ${backoff}ms (${this.globalConsecutiveNoFills} consecutive no-fills)`
        );
      }
    }
  }
  resetGamNoFillCounter() {
    if (this.globalConsecutiveNoFills > 0 && this.debug) {
      console.log(
        `[CIRCUIT-BREAKER] Resetting no-fill counter (was ${this.globalConsecutiveNoFills})`
      );
    }
    this.globalConsecutiveNoFills = 0;
    this.globalNoFillCooldownUntil = 0;
  }
  async enforceGlobalRateLimit() {
    const now = Date.now();
    const elapsed = now - this.globalLastGamRequestTime;
    if (elapsed < this.globalMinGamIntervalMs) {
      const waitMs = this.globalMinGamIntervalMs - elapsed;
      if (this.debug) {
        console.log(`[RATE-LIMIT] Waiting ${waitMs}ms before next GAM request`);
      }
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
    this.globalLastGamRequestTime = Date.now();
  }
  // --- Watchdog ---
  startAdRequestWatchdog(token) {
    this.clearAdRequestWatchdog();
    const timeoutMs = this.config.adFailsafeTimeoutMs ?? 1e4;
    this.adRequestWatchdogToken = token;
    this.adRequestWatchdogId = window.setTimeout(() => {
      if (this.adRequestWatchdogToken !== token) {
        return;
      }
      this.adRequestWatchdogId = void 0;
      this.adRequestWatchdogToken = null;
      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }
      this.logAdState("ad_request_timeout", { token, timeoutMs });
      this.callbacks.onWatchdogTimeout();
    }, timeoutMs);
    this.logAdState("ad_request_watchdog_started", { token, timeoutMs });
  }
  clearAdRequestWatchdog() {
    if (this.adRequestWatchdogId != null) {
      clearTimeout(this.adRequestWatchdogId);
      this.adRequestWatchdogId = void 0;
    }
    if (this.adRequestWatchdogToken != null) {
      this.logAdState("ad_request_watchdog_cleared", {
        token: this.adRequestWatchdogToken
      });
      this.adRequestWatchdogToken = null;
    }
  }
  // --- Failsafe ---
  startAdFailsafeTimer(token) {
    this.clearAdFailsafeTimer();
    const failsafeMs = this.config.adFailsafeTimeoutMs ?? 1e4;
    this.adFailsafeToken = token;
    this.adFailsafeTimerId = window.setTimeout(() => {
      if (this.adFailsafeToken !== token) {
        return;
      }
      this.adFailsafeTimerId = void 0;
      this.adFailsafeToken = null;
      if (this.activeAdRequestToken === token) {
        this.activeAdRequestToken = null;
      }
      this.logAdState("ad_failsafe_triggered", {
        token,
        failsafeMs,
        videoPaused: this.video.paused
      });
      this.callbacks.onFailsafeTimeout();
    }, failsafeMs);
    this.logAdState("ad_failsafe_started", { token, failsafeMs });
  }
  clearAdFailsafeTimer() {
    if (this.adFailsafeTimerId != null) {
      clearTimeout(this.adFailsafeTimerId);
      this.logAdState("ad_failsafe_cleared", { token: this.adFailsafeToken });
      this.adFailsafeTimerId = void 0;
    }
    this.adFailsafeToken = null;
  }
  logAdState(event, extra = {}, adPlayer, inAdBreak, showAds) {
    if (!this.debug) {
      return;
    }
    console.log("[StormcloudVideoPlayer][AdState]", {
      event,
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      showAds: showAds ?? false,
      adPlaying: adPlayer?.isAdPlaying() ?? false,
      inAdBreak: inAdBreak ?? false,
      activeAdRequestToken: this.activeAdRequestToken,
      ...extra
    });
  }
  // --- Reset for ad break end ---
  resetForBreakEnd() {
    this.clearAdStopTimer();
    this.clearAdStartTimer();
    this.clearAdRequestWatchdog();
    this.clearAdFailsafeTimer();
    this.activeAdRequestToken = null;
    this.expectedAdBreakDurationMs = void 0;
    this.currentAdBreakStartWallClockMs = void 0;
    this.scteAdBreakEndWallClockMs = void 0;
    this.adBreakPlayedDurationMs = 0;
    this.currentAdIndex = 0;
    this.totalAdsInBreak = 0;
    this.consecutiveFailures = 0;
    this.globalConsecutiveNoFills = 0;
    this.adBreakEpoch++;
  }
  destroy() {
    this.clearAdStopTimer();
    this.clearAdStartTimer();
    this.clearAdFailsafeTimer();
    this.clearAdRequestWatchdog();
  }
};

// src/player/AdPreloadPool.ts
var AdPreloadPool = class {
  constructor(deps) {
    this.pool = [];
    this.maxPoolSize = 2;
    this.poolActive = false;
    this.loopRunning = false;
    this.deps = deps;
  }
  get active() {
    return this.poolActive;
  }
  set active(value) {
    this.poolActive = value;
  }
  get isLoopRunning() {
    return this.loopRunning;
  }
  startPreloadPool(baseVastUrl, initialUrls, startLoopImmediately = true) {
    if (this.poolActive) {
      if (this.deps.debug) {
        console.log("[PRELOAD-POOL] Already active, skipping duplicate start");
      }
      return;
    }
    this.poolActive = true;
    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Starting preload pool EARLY (target size: ${this.maxPoolSize})`);
      console.log(`[PRELOAD-POOL] Ad break hasn't started yet, but preloading ads in advance`);
    }
    for (const url of initialUrls) {
      if (this.pool.length < this.maxPoolSize) {
        this.preloadAdIntoPool(url);
      }
    }
    if (startLoopImmediately) {
      this.preloadPoolLoop(baseVastUrl);
    } else if (this.deps.debug) {
      console.log(
        "[PRELOAD-POOL] Pre-break mode: preloaded once, waiting for SCTE start to continue fetching"
      );
    }
  }
  async preloadAdIntoPool(vastUrl) {
    const timing = this.deps.timing;
    if (timing.isGamInCooldown()) {
      if (this.deps.debug) {
        console.log("[CIRCUIT-BREAKER] GAM in cooldown, skipping preload");
      }
      return;
    }
    if (this.pool.some((entry) => entry.vastUrl === vastUrl) || timing.failedVastUrls.has(vastUrl) || timing.isUrlInCooldown(vastUrl)) {
      return;
    }
    if (this.pool.length >= this.maxPoolSize) {
      return;
    }
    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Preloading ad into pool: ${vastUrl}`);
    }
    const epoch = timing.adBreakEpoch;
    const loadPromise = (async () => {
      let hasAdError = false;
      let adErrorPayload = null;
      let errorListenerCleanup = null;
      try {
        const continueLiveStreamDuringAds = this.deps.shouldContinueLiveStreamDuringAds();
        const preloadAd = this.deps.createAdPlayer(continueLiveStreamDuringAds);
        preloadAd.initialize();
        const errorListener = (payload) => {
          hasAdError = true;
          adErrorPayload = payload;
        };
        preloadAd.on("ad_error", errorListener);
        errorListenerCleanup = () => preloadAd.off("ad_error", errorListener);
        await timing.enforceGlobalRateLimit();
        await preloadAd.requestAds(vastUrl);
        preloadAd.pause();
        await new Promise((resolve) => setTimeout(resolve, 1500));
        preloadAd.pause();
        if (hasAdError) {
          const isNoFill = adErrorPayload?.isNoFill || adErrorPayload?.code === 303 || adErrorPayload?.vastErrorCode === 303;
          if (errorListenerCleanup) {
            errorListenerCleanup();
          }
          preloadAd.destroy();
          timing.recordGamNoFill();
          if (isNoFill) {
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Ad preload returned no-fill: ${vastUrl}`);
            }
            timing.temporaryFailureUrls.set(vastUrl, Date.now());
          } else {
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Ad preload failed with error: ${vastUrl}`, adErrorPayload);
            }
            if (adErrorPayload && !timing.isTemporaryAdError(adErrorPayload)) {
              timing.failedVastUrls.add(vastUrl);
            } else {
              timing.temporaryFailureUrls.set(vastUrl, Date.now());
            }
          }
          return;
        }
        if (!this.poolActive || epoch !== timing.adBreakEpoch) {
          if (this.deps.debug) {
            console.log(
              "[PRELOAD-POOL] Discarding preloaded ad - break ended during fetch"
            );
          }
          if (errorListenerCleanup) {
            errorListenerCleanup();
          }
          try {
            preloadAd.destroy();
          } catch {
          }
          return;
        }
        const poolEntry = {
          vastUrl,
          adController: preloadAd,
          loadedAt: Date.now(),
          isReady: true,
          loadPromise: Promise.resolve()
        };
        const loadedDuration = preloadAd.getLoadedAdDuration?.();
        if (loadedDuration != null && loadedDuration > 0) {
          poolEntry.durationSeconds = loadedDuration;
        }
        const lateErrorListener = (payload) => {
          const index = this.pool.findIndex((entry) => entry.vastUrl === vastUrl);
          if (index >= 0) {
            this.pool.splice(index, 1);
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Late error detected, removed from pool: ${vastUrl}`, payload);
            }
            try {
              preloadAd.destroy();
            } catch {
            }
          }
        };
        preloadAd.on("ad_error", lateErrorListener);
        this.pool.push(poolEntry);
        if (this.deps.debug) {
          console.log(`[PRELOAD-POOL] Ad preloaded (no errors detected within timeout) (pool size: ${this.pool.length}/${this.maxPoolSize}, duration=${poolEntry.durationSeconds ?? "?"}s)`);
        }
        if (errorListenerCleanup) {
          errorListenerCleanup();
        }
      } catch (error) {
        if (errorListenerCleanup) {
          errorListenerCleanup();
        }
        timing.recordGamNoFill();
        if (this.deps.debug) {
          console.warn(`[PRELOAD-POOL] Failed to preload ad: ${vastUrl}`, error);
        }
        if (error instanceof Error && !timing.isTemporaryAdError(error)) {
          timing.failedVastUrls.add(vastUrl);
        } else if (error instanceof Error && timing.isTemporaryAdError(error)) {
          timing.temporaryFailureUrls.set(vastUrl, Date.now());
        }
      }
    })();
    loadPromise.catch(() => {
    });
  }
  async preloadPoolLoop(baseVastUrl) {
    if (this.loopRunning) {
      return;
    }
    this.loopRunning = true;
    const epoch = this.deps.timing.adBreakEpoch;
    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Starting continuous preload loop");
    }
    while (this.poolActive && epoch === this.deps.timing.adBreakEpoch) {
      if (this.deps.timing.isGamInCooldown()) {
        if (this.deps.debug) {
          console.log("[CIRCUIT-BREAKER] GAM in cooldown, stopping preload pool loop");
        }
        break;
      }
      if (!this.deps.isInAdBreak() && this.pool.length >= this.maxPoolSize) {
        await new Promise((resolve) => setTimeout(resolve, 2e3));
        continue;
      }
      if (!this.deps.isInAdBreak() && this.deps.getPendingAdBreak() === null) {
        if (this.deps.debug) {
          console.log("[PRELOAD-POOL] Ad break cancelled, stopping preload pool");
        }
        break;
      }
      while (this.pool.length < this.maxPoolSize) {
        if (!this.poolActive || epoch !== this.deps.timing.adBreakEpoch || this.deps.timing.isGamInCooldown()) {
          break;
        }
        if (this.deps.isInAdBreak() && !this.deps.timing.hasTimeToStartAnotherAd()) {
          if (this.deps.debug) {
            console.log("[PRELOAD-POOL] Not enough ad break time left, stopping preload");
          }
          break;
        }
        const newUrls = this.deps.generateVastUrlsWithCorrelators(baseVastUrl, 1);
        const newUrl = newUrls[0];
        if (newUrl && !this.deps.timing.failedVastUrls.has(newUrl) && !this.deps.timing.isUrlInCooldown(newUrl)) {
          await this.preloadAdIntoPool(newUrl);
        }
        await new Promise((resolve) => setTimeout(resolve, 1e3));
      }
      await new Promise((resolve) => setTimeout(resolve, 2e3));
    }
    this.loopRunning = false;
    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Preload loop ended");
    }
  }
  getPreloadedAd() {
    if (this.pool.length === 0) {
      return null;
    }
    const entry = this.pool.shift();
    if (!entry || !entry.isReady) {
      return null;
    }
    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Using preloaded ad from pool (${this.pool.length} remaining)`);
    }
    return {
      vastUrl: entry.vastUrl,
      adController: entry.adController
    };
  }
  stop() {
    this.poolActive = false;
    this.loopRunning = false;
    for (const entry of this.pool) {
      try {
        entry.adController.destroy();
      } catch (error) {
        if (this.deps.debug) {
          console.warn("[PRELOAD-POOL] Error destroying preloaded controller:", error);
        }
      }
    }
    this.pool = [];
    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Preload pool stopped and cleaned up");
    }
  }
};

// src/player/PlaceholderLayer.ts
var PlaceholderLayer = class {
  constructor(video, debug) {
    this.video = video;
    this.debug = debug;
  }
  ensureContainer() {
    if (this.container) {
      return;
    }
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.left = "0";
    el.style.top = "0";
    el.style.right = "0";
    el.style.bottom = "0";
    el.style.display = "none";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.pointerEvents = "none";
    el.style.zIndex = "5";
    el.style.backgroundColor = "#000";
    el.style.transition = "opacity 0.3s ease-in-out";
    el.style.opacity = "0";
    if (!this.video.parentElement) {
      if (this.debug) {
        console.warn("[StormcloudVideoPlayer] Video element has no parent for placeholder container");
      }
      return;
    }
    this.video.parentElement.appendChild(el);
    this.container = el;
  }
  show() {
    this.ensureContainer();
    if (!this.container) {
      return;
    }
    if (!this.video.muted) {
      this.video.muted = true;
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Muted video when showing placeholder");
      }
    }
    const wasHidden = this.container.style.display === "none" || this.container.style.opacity === "0";
    if (wasHidden) {
      this.container.style.transition = "none";
    } else {
      this.container.style.transition = "opacity 0.3s ease-in-out";
    }
    this.container.style.backgroundColor = "#000";
    this.container.style.display = "flex";
    this.container.offsetHeight;
    this.container.style.opacity = "1";
    this.container.style.pointerEvents = "auto";
    if (wasHidden) {
      requestAnimationFrame(() => {
        if (this.container) {
          this.container.style.transition = "opacity 0.3s ease-in-out";
        }
      });
    }
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Showing placeholder layer (between content and ad layer)");
    }
  }
  hide() {
    if (!this.container) {
      return;
    }
    this.container.style.opacity = "0";
    setTimeout(() => {
      if (this.container) {
        this.container.style.display = "none";
        this.container.style.pointerEvents = "none";
        this.container.style.backgroundColor = "#000";
      }
    }, 300);
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Hiding placeholder layer");
    }
  }
  destroy() {
    if (this.container) {
      if (this.container.parentElement) {
        this.container.parentElement.removeChild(this.container);
      }
      this.container = void 0;
    }
  }
};

// src/player/HlsEngine.ts
import Hls2 from "hls.js";
var HlsEngine = class {
  constructor(config, video, cueManager, timing, callbacks) {
    this.isLiveStream = false;
    this.nativeHlsMode = false;
    this.videoSrcProtection = null;
    this.bufferedSegmentsCount = 0;
    this.shouldAutoplayAfterBuffering = false;
    this.hasInitialBufferCompleted = false;
    this.config = config;
    this.video = video;
    this.cueManager = cueManager;
    this.timing = timing;
    this.callbacks = callbacks;
  }
  get debug() {
    return !!this.config.debugAdTiming;
  }
  shouldUseNativeHls(getStreamType) {
    const streamType = getStreamType();
    if (streamType === "other") {
      return true;
    }
    const canNative = this.video.canPlayType("application/vnd.apple.mpegurl");
    return !!(this.config.allowNativeHls && canNative);
  }
  async setupNativeHls() {
    this.nativeHlsMode = true;
    this.videoSrcProtection = this.config.src;
    this.video.src = this.config.src;
    this.isLiveStream = this.config.isLiveStream ?? (this.config.lowLatencyMode ?? false);
    if (this.debug) {
      const adBehavior = this.isLiveStream ? "live (main video continues muted during ads)" : "vod (main video pauses during ads)";
      console.log(
        "[StormcloudVideoPlayer] Using native HLS playback:",
        {
          isLive: this.isLiveStream,
          allowNativeHls: this.config.allowNativeHls,
          adBehavior
        }
      );
    }
    if (this.config.autoplay) {
      await this.video.play()?.catch(() => {
      });
    }
  }
  setupHls() {
    this.hls = new Hls2({
      enableWorker: true,
      backBufferLength: 30,
      liveDurationInfinity: true,
      lowLatencyMode: !!this.config.lowLatencyMode,
      maxLiveSyncPlaybackRate: this.config.lowLatencyMode ? 1.5 : 1,
      ...this.config.lowLatencyMode ? { liveSyncDuration: 2 } : {},
      maxBufferLength: 30,
      maxMaxBufferLength: 600,
      maxBufferSize: 60 * 1e3 * 1e3,
      maxBufferHole: 0.5,
      highBufferWatchdogPeriod: 2,
      nudgeOffset: 0.1,
      nudgeMaxRetry: 3,
      startPosition: -1
    });
    this.hls.on(Hls2.Events.MEDIA_ATTACHED, () => {
      this.hls?.loadSource(this.config.src);
    });
    this.hls.on(Hls2.Events.MANIFEST_PARSED, async (_, _data) => {
      this.isLiveStream = this.config.isLiveStream ?? (this.hls?.levels?.some(
        (level) => level?.details?.live === true || level?.details?.type === "LIVE"
      ) ?? false);
      await this.callbacks.onManifestParsed(this.isLiveStream);
      this.bufferedSegmentsCount = 0;
      this.hasInitialBufferCompleted = false;
      this.shouldAutoplayAfterBuffering = !!this.config.autoplay;
      const minSegments = this.config.minSegmentsBeforePlay ?? 2;
      if (this.debug) {
        console.log(
          "[StormcloudVideoPlayer] Waiting for",
          minSegments,
          "segments to buffer before playback"
        );
      }
      if (minSegments === 0 || !this.config.autoplay) {
        this.hasInitialBufferCompleted = true;
        if (this.config.autoplay) {
          await this.video.play()?.catch(() => {
          });
        }
      }
    });
    this.hls.on(Hls2.Events.LEVEL_LOADED, (_evt, data) => {
      if (this.callbacks.isInAdBreak() || this.callbacks.hasPendingAdBreak()) {
        return;
      }
      const details = data?.details;
      if (!details || !details.fragments || details.fragments.length === 0) {
        return;
      }
      const fragmentsToScan = Math.min(5, details.fragments.length);
      for (let i = 0; i < fragmentsToScan; i++) {
        const frag = details.fragments[i];
        const tagList = frag?.tagList;
        if (!Array.isArray(tagList)) continue;
        for (const entry of tagList) {
          let tag = "";
          let value = "";
          if (Array.isArray(entry)) {
            tag = String(entry[0] ?? "");
            value = String(entry[1] ?? "");
          } else if (typeof entry === "string") {
            const idx = entry.indexOf(":");
            if (idx >= 0) {
              tag = entry.substring(0, idx);
              value = entry.substring(idx + 1);
            } else {
              tag = entry;
            }
          }
          if (!tag) continue;
          if (tag.includes("EXT-X-CUE-OUT") || tag.includes("EXT-X-DATERANGE")) {
            const attrs = tag.includes("EXT-X-DATERANGE") ? parseAttributeList(value) : {};
            const hasScteOut = tag.includes("EXT-X-CUE-OUT") || "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== void 0;
            if (hasScteOut) {
              const durationSeconds = parseCueOutDuration(value);
              const marker = {
                type: "start",
                ...durationSeconds !== void 0 ? { durationSeconds } : {},
                raw: { tag, value, earlyDetection: true }
              };
              if (this.debug) {
                console.log("[StormcloudVideoPlayer] EARLY SCTE-35 DETECTION: Ad break marker found in fragment", i, "- starting pre-fetch (NOT playing yet)");
              }
              this.callbacks.onScte35Cue(marker, {
                ...this.cueManager.createScte35CueContext("manifest", "early", frag)
              });
              return;
            }
          }
        }
      }
    });
    this.hls.on(Hls2.Events.FRAG_BUFFERED, async (_evt, _data) => {
      if (this.hasInitialBufferCompleted) {
        return;
      }
      this.bufferedSegmentsCount++;
      const minSegments = this.config.minSegmentsBeforePlay ?? 2;
      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] Buffered segment ${this.bufferedSegmentsCount}/${minSegments}`
        );
      }
      if (this.bufferedSegmentsCount >= minSegments) {
        this.hasInitialBufferCompleted = true;
        if (this.shouldAutoplayAfterBuffering) {
          if (this.debug) {
            console.log(
              `[StormcloudVideoPlayer] Initial buffer complete (${this.bufferedSegmentsCount} segments). Starting playback.`
            );
          }
          await this.video.play()?.catch((err) => {
            if (this.debug) {
              console.warn("[StormcloudVideoPlayer] Autoplay failed:", err);
            }
          });
        }
      }
    });
    this.hls.on(Hls2.Events.FRAG_PARSING_METADATA, (_evt, data) => {
      const id3Tags = (data?.samples || []).map((s) => ({
        key: "ID3",
        value: s?.data,
        ptsSeconds: s?.pts
      }));
      id3Tags.forEach((tag) => this.onId3Tag(tag));
    });
    this.hls.on(Hls2.Events.FRAG_LOADED, (_evt, data) => {
      this.processFragmentScte35Payload(
        data?.payload,
        typeof data?.frag?.sn === "number" ? data.frag.sn : void 0,
        getFragmentStartSeconds(data?.frag)
      );
    });
    this.hls.on(Hls2.Events.FRAG_DECRYPTED, (_evt, data) => {
      this.processFragmentScte35Payload(
        data?.payload,
        typeof data?.frag?.sn === "number" ? data.frag.sn : void 0,
        getFragmentStartSeconds(data?.frag)
      );
    });
    this.hls.on(Hls2.Events.FRAG_CHANGED, (_evt, data) => {
      const frag = data?.frag;
      const tagList = frag?.tagList;
      if (!Array.isArray(tagList)) {
        this.callbacks.activatePendingCuesForFragment(frag);
        return;
      }
      for (const entry of tagList) {
        let tag = "";
        let value = "";
        if (Array.isArray(entry)) {
          tag = String(entry[0] ?? "");
          value = String(entry[1] ?? "");
        } else if (typeof entry === "string") {
          const idx = entry.indexOf(":");
          if (idx >= 0) {
            tag = entry.substring(0, idx);
            value = entry.substring(idx + 1);
          } else {
            tag = entry;
            value = "";
          }
        }
        if (!tag) continue;
        if (tag.includes("EXT-X-CUE-OUT-CONT")) {
          const prog = parseCueOutCont(value);
          const marker = {
            type: "progress",
            ...prog?.duration !== void 0 ? { durationSeconds: prog.duration } : {},
            ...prog?.elapsed !== void 0 ? { ptsSeconds: prog.elapsed } : {},
            raw: { tag, value }
          };
          this.callbacks.onScte35Cue(marker, {
            ...this.cueManager.createScte35CueContext("manifest", "playback", frag)
          });
        } else if (tag.includes("EXT-X-CUE-OUT")) {
          const durationSeconds = parseCueOutDuration(value);
          const marker = {
            type: "start",
            ...durationSeconds !== void 0 ? { durationSeconds } : {},
            raw: { tag, value }
          };
          this.callbacks.onScte35Cue(marker, {
            ...this.cueManager.createScte35CueContext("manifest", "playback", frag)
          });
        } else if (tag.includes("EXT-X-CUE-IN")) {
          this.callbacks.onScte35Cue(
            { type: "end", raw: { tag, value } },
            {
              ...this.cueManager.createScte35CueContext("manifest", "playback", frag)
            }
          );
        } else if (tag.includes("EXT-X-DATERANGE")) {
          const attrs = parseAttributeList(value);
          const hasScteOut = "SCTE35-OUT" in attrs || attrs["SCTE35-OUT"] !== void 0;
          const hasScteIn = "SCTE35-IN" in attrs || attrs["SCTE35-IN"] !== void 0;
          const klass = String(attrs["CLASS"] ?? "");
          const duration = toNumber(attrs["DURATION"]);
          if (hasScteOut || /com\.apple\.hls\.cue/i.test(klass)) {
            const marker = {
              type: "start",
              ...duration !== void 0 ? { durationSeconds: duration } : {},
              raw: { tag, value, attrs }
            };
            this.callbacks.onScte35Cue(marker, {
              ...this.cueManager.createScte35CueContext("manifest", "playback", frag)
            });
          }
          if (hasScteIn) {
            this.callbacks.onScte35Cue(
              { type: "end", raw: { tag, value, attrs } },
              {
                ...this.cueManager.createScte35CueContext("manifest", "playback", frag)
              }
            );
          }
        }
      }
      this.callbacks.activatePendingCuesForFragment(frag);
    });
    this.hls.on(Hls2.Events.ERROR, (_evt, data) => {
      if (data?.fatal) {
        switch (data.type) {
          case Hls2.ErrorTypes.NETWORK_ERROR:
            this.hls?.startLoad();
            break;
          case Hls2.ErrorTypes.MEDIA_ERROR:
            this.hls?.recoverMediaError();
            break;
          default:
            this.callbacks.onFatalDestroy();
            break;
        }
      }
    });
    this.hls.attachMedia(this.video);
  }
  onId3Tag(tag) {
    if (typeof tag.ptsSeconds === "number") {
      this.timing.updatePtsDrift(tag.ptsSeconds);
    }
    const marker = parseScte35FromId3(tag);
    if (marker) {
      this.callbacks.onScte35Cue(marker, {
        source: "id3",
        readiness: "playback"
      });
    }
  }
  processFragmentScte35Payload(payloadData, fragmentSn, fragmentStartSeconds) {
    const payload = normalizeFragmentPayload(payloadData);
    if (!payload) {
      return;
    }
    const markers = parseScte35FromTsPackets(
      payload,
      this.cueManager.tsScte35Pids,
      this.cueManager.pmtPids,
      this.cueManager.processedTsScte35Sections
    );
    markers.forEach((marker) => {
      const context = {
        source: "ts",
        readiness: "early"
      };
      if (fragmentSn !== void 0) {
        context.fragmentSn = fragmentSn;
      }
      if (fragmentStartSeconds !== void 0) {
        context.fragmentStartSeconds = fragmentStartSeconds;
      }
      this.callbacks.onScte35Cue(marker, context);
    });
  }
  getLiveSyncPosition() {
    const pos = this.hls?.liveSyncPosition;
    return typeof pos === "number" && Number.isFinite(pos) ? pos : void 0;
  }
  destroy() {
    this.hls?.destroy();
    this.hls = void 0;
  }
};

// src/player/PlayerControls.ts
function getAdAudioVolume(adPlayer) {
  return adPlayer.getOriginalMutedState() ? 0 : adPlayer.getOriginalVolume();
}
function toggleMute(video, adPlayer, debug) {
  if (adPlayer.isAdPlaying()) {
    const newMutedState = !adPlayer.getOriginalMutedState();
    const restoreVolume = adPlayer.getOriginalVolume() || 1;
    adPlayer.updateOriginalMutedState(newMutedState, video.volume);
    adPlayer.setAdVolume(newMutedState ? 0 : restoreVolume);
    if (debug) {
      console.log(
        "[StormcloudVideoPlayer] Mute toggle during ad - immediately applied:",
        newMutedState
      );
    }
  } else {
    video.muted = !video.muted;
    adPlayer.updateOriginalMutedState(video.muted, video.volume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] Muted:", video.muted);
    }
  }
}
function toggleFullscreen(video, debug) {
  return new Promise((resolve, reject) => {
    if (!document.fullscreenElement) {
      const container = video.parentElement;
      if (!container) {
        reject(new Error("No parent container found for fullscreen"));
        return;
      }
      container.requestFullscreen().then(() => {
        if (debug) {
          console.log("[StormcloudVideoPlayer] Entered fullscreen");
        }
        resolve();
      }).catch((err) => {
        if (debug) {
          console.error("[StormcloudVideoPlayer] Fullscreen error:", err);
        }
        reject(err);
      });
    } else {
      document.exitFullscreen().then(() => {
        if (debug) {
          console.log("[StormcloudVideoPlayer] Exited fullscreen");
        }
        resolve();
      }).catch((err) => {
        if (debug) {
          console.error(
            "[StormcloudVideoPlayer] Exit fullscreen error:",
            err
          );
        }
        reject(err);
      });
    }
  });
}
function isMuted(video, adPlayer) {
  if (adPlayer.isAdPlaying()) {
    return adPlayer.getOriginalMutedState();
  }
  return video.muted;
}
function setMuted(video, adPlayer, muted, debug) {
  const adPlaying = adPlayer.isAdPlaying();
  if (adPlaying && muted === video.muted) {
    if (debug) {
      console.log(
        "[StormcloudVideoPlayer] setMuted reflective update during ad ignored",
        { muted }
      );
    }
    return;
  }
  video.muted = muted;
  if (adPlaying) {
    adPlayer.updateOriginalMutedState(muted, video.volume);
    adPlayer.setAdVolume(muted ? 0 : 1);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setMuted applied during ad", {
        muted
      });
    }
    return;
  }
  adPlayer.updateOriginalMutedState(muted, video.volume);
  if (debug) {
    console.log("[StormcloudVideoPlayer] setMuted called:", muted);
  }
}
function setVolume(video, adPlayer, volume, debug) {
  const clampedVolume = Math.max(0, Math.min(1, volume));
  const adPlaying = adPlayer.isAdPlaying();
  if (adPlaying) {
    adPlayer.setAdVolume(clampedVolume);
    adPlayer.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setVolume applied during ad", {
        volume: clampedVolume
      });
    }
  } else {
    video.volume = clampedVolume;
    video.muted = clampedVolume === 0;
    adPlayer.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setVolume called:", clampedVolume);
    }
  }
}
function getVolume(video, adPlayer) {
  const adPlaying = adPlayer.isAdPlaying();
  if (adPlaying) {
    return adPlayer.getAdVolume();
  }
  return video.volume;
}
function isFullscreen() {
  return !!document.fullscreenElement;
}
function resizePlayer(video, adPlayer, debug) {
  if (debug) {
    console.log("[StormcloudVideoPlayer] Resizing player");
  }
  if (adPlayer && adPlayer.isAdPlaying()) {
    const width = video.clientWidth || 640;
    const height = video.clientHeight || 480;
    if (debug) {
      console.log(
        `[StormcloudVideoPlayer] Resizing ads manager to ${width}x${height}`
      );
    }
    adPlayer.resize(width, height);
  }
}

// src/player/AdBreakOrchestrator.ts
var AdBreakOrchestrator = class {
  constructor(host, timing, preloadPool, adConfig, cueManager, placeholder) {
    this.inAdBreak = false;
    this.showAds = false;
    this.isShowingPlaceholder = false;
    this.adDetectSentForCurrentBreak = false;
    this.continuousFetchingActive = false;
    this.continuousFetchLoopRunning = false;
    this.adRequestQueue = [];
    this.adPodQueue = [];
    this.maxPlaceholderDurationMs = 5e3;
    this.pendingAdBreak = null;
    this.host = host;
    this.timing = timing;
    this.preloadPool = preloadPool;
    this.adConfig = adConfig;
    this.cueManager = cueManager;
    this.placeholder = placeholder;
  }
  get debug() {
    return !!this.host.config.debugAdTiming;
  }
  get analyticsContext() {
    return buildAnalyticsContext(this.host.config);
  }
  // ───────────────────────────────────────────────────────────────
  //  IMA event listeners
  // ───────────────────────────────────────────────────────────────
  get optimizedPodsEnabled() {
    return !!this.host.config.optimizedPods;
  }
  attachImaEventListeners() {
    const adPlayer = this.host.getAdPlayer();
    adPlayer.on("pod_ad_started", (payload) => {
      this.timing.notePodAdStarted();
      if (this.debug) {
        console.log(
          `[POD] Playing pod ad ${payload?.index != null ? payload.index + 1 : "?"}/${payload?.total ?? "?"}`
        );
      }
    });
    adPlayer.on("all_ads_completed", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();
      this.timing.consecutiveFailures = 0;
      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] all_ads_completed event received - remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }
      if (this.inAdBreak) {
        if (this.timing.hasTimeToStartAnotherAd()) {
          this.tryNextAvailableAdWithRateLimit();
        } else {
          this.handleAdPodComplete();
        }
      }
    });
    adPlayer.on("ad_error", (errorPayload) => {
      let errorMessage = "Ad playback failed";
      if (errorPayload) {
        const errorCode = errorPayload.code || errorPayload.errorCode || "unknown";
        const vastErrorCode = errorPayload.vastErrorCode;
        const message = errorPayload.message || errorPayload.errorMessage || "Unknown error";
        const cause = errorPayload.cause || errorPayload.innerError || errorPayload.error;
        errorMessage = `Ad error: AdError ${errorCode}: ${message}`;
        if (vastErrorCode && vastErrorCode !== "N/A" && vastErrorCode !== errorCode) {
          errorMessage += ` (VAST Error Code: ${vastErrorCode})`;
        }
        if (cause) {
          const causeMessage = typeof cause === "string" ? cause : cause.message || String(cause);
          errorMessage += `. Caused by: ${causeMessage}`;
        }
      }
      console.error("[AD-ERROR]", errorMessage, errorPayload || "");
      this.handleAdFailure();
    });
    adPlayer.on("content_pause", () => {
      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = true;
      this.timing.resetGamNoFillCounter();
      if (this.inAdBreak && this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
        this.timing.scheduleAdStopAtBreakBoundary();
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Starting ad break timer on content_pause (first ad starting)");
        }
      }
      this.host.getAdPlayer().setAdVolume(getAdAudioVolume(this.host.getAdPlayer()));
      if (this.isShowingPlaceholder) {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Hiding placeholder - new ads starting");
        }
        this.host.getAdPlayer().hidePlaceholder();
        this.isShowingPlaceholder = false;
      }
    });
    adPlayer.on("content_resume", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      if (!this.host.video.muted) {
        this.host.video.muted = true;
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Muted video on content_resume (ad ended, gap between ads)");
        }
      }
      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = false;
      if (!this.inAdBreak) {
        return;
      }
      this.timing.consecutiveFailures = 0;
      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();
      if (this.debug) {
        console.log(
          `[CONTINUOUS-FETCH] content_resume event: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }
      if (this.timing.hasTimeToStartAnotherAd()) {
        this.tryNextAvailableAdWithRateLimit();
      } else {
        this.handleAdPodComplete();
      }
    });
  }
  recreateAdController() {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Recreating AdController for new ad");
    }
    const shouldShowPlaceholder = this.inAdBreak && this.showAds;
    const adPlayer = this.host.getAdPlayer();
    if (shouldShowPlaceholder && adPlayer) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Showing placeholder before destroying old AdController");
      }
      this.placeholder.show();
      adPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }
    const preservedMutedState = adPlayer.getOriginalMutedState();
    const preservedVolumeState = adPlayer.getOriginalVolume();
    if (adPlayer) {
      try {
        adPlayer.destroy();
        this.host.video.muted = true;
      } catch (error) {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] Error destroying old AdController:", error);
        }
      }
    }
    const continueLiveStreamDuringAds = this.host.shouldContinueLiveStreamDuringAds();
    const newAdPlayer = this.host.createAdPlayer(continueLiveStreamDuringAds);
    this.host.setAdPlayer(newAdPlayer);
    newAdPlayer.initialize();
    newAdPlayer.updateOriginalMutedState(preservedMutedState, preservedVolumeState);
    this.attachImaEventListeners();
    if (shouldShowPlaceholder) {
      this.placeholder.show();
      newAdPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }
  }
  clearPendingAdBreak() {
    if (this.prefetchTimerId != null) {
      clearTimeout(this.prefetchTimerId);
      this.prefetchTimerId = void 0;
    }
    this.pendingAdBreak = null;
    this.cueManager.pendingScte35CueKey = void 0;
    this.adConfig.podAssignedByPrefetch = false;
  }
  // ───────────────────────────────────────────────────────────────
  //  Ad break start
  // ───────────────────────────────────────────────────────────────
  async handleAdStart(_marker) {
    const scheduled = this.adConfig.findCurrentOrNextBreak(
      this.host.video.currentTime * 1e3
    );
    if (scheduled) {
      this.adConfig.consumedVmapBreakIds.add(this.adConfig.getAdBreakKey(scheduled));
    }
    const tags = this.adConfig.selectVastTagsForBreak(scheduled);
    let baseVastUrl;
    if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else if (this.adConfig.apiVastTagUrl) {
      baseVastUrl = this.adConfig.apiVastTagUrl;
    } else {
      this.clearPendingAdBreak();
      return;
    }
    const adBreakDurationMs = _marker.durationSeconds != null ? _marker.durationSeconds * 1e3 : scheduled?.durationMs;
    if (this.debug) {
      const mode = this.host.config.isLiveStream ? "LIVE" : "VOD";
      console.log(
        `[CONTINUOUS-FETCH] ${mode} MODE: Target duration=${adBreakDurationMs}ms`
      );
    }
    this.timing.failedVastUrls.clear();
    this.timing.temporaryFailureUrls.clear();
    this.timing.consecutiveFailures = 0;
    this.continuousFetchingActive = true;
    this.isShowingPlaceholder = false;
    this.timing.totalAdRequestsInBreak = 0;
    if (this.adConfig.podAssignedByPrefetch) {
      this.adConfig.podAssignedByPrefetch = false;
    } else {
      this.adConfig.beginNewAdPod();
    }
    const currentMuted = this.host.video.muted;
    const currentVolume = this.host.video.volume;
    this.host.getAdPlayer().updateOriginalMutedState(currentMuted, currentVolume);
    this.inAdBreak = true;
    this.timing.adBreakPlayedDurationMs = 0;
    this.timing.currentAdIndex = 0;
    this.timing.totalAdsInBreak = 0;
    this.adPodQueue = [];
    this.showAds = false;
    if (this.timing.expectedAdBreakDurationMs == null && adBreakDurationMs != null) {
      this.timing.expectedAdBreakDurationMs = adBreakDurationMs;
    }
    if (this.optimizedPodsEnabled) {
      const podPlayed = await this.startOptimizedPod(
        baseVastUrl,
        adBreakDurationMs,
        currentMuted,
        currentVolume
      );
      if (podPlayed) {
        return;
      }
      if (this.debug) {
        console.log("[POD] Optimized pod unavailable, falling back to sequential single-ad path");
      }
    }
    let firstAdUrl;
    let usePreloadedAd = false;
    let preloadedController;
    const preloaded = this.preloadPool.getPreloadedAd();
    if (preloaded) {
      firstAdUrl = preloaded.vastUrl;
      preloadedController = preloaded.adController;
      usePreloadedAd = true;
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: firstAdUrl, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Using preloaded ad from pool (preloaded in advance, ready immediately!)");
      }
    } else if (this.pendingAdBreak && this.pendingAdBreak.vastUrls.length > 0) {
      this.adRequestQueue = [...this.pendingAdBreak.vastUrls];
      firstAdUrl = this.adRequestQueue.shift();
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Pool empty, using prefetched URLs (${this.adRequestQueue.length + 1} available)`);
      }
      this.clearPendingAdBreak();
    } else {
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Generating new ad URLs...");
      }
      const firstAdUrlArray = this.host.generateVastUrls(baseVastUrl, 1);
      firstAdUrl = firstAdUrlArray[0];
      this.adRequestQueue = [];
    }
    if (!firstAdUrl) {
      if (this.debug) {
        console.warn("[CONTINUOUS-FETCH] Failed to get first ad URL");
      }
      this.handleAdPodComplete();
      return;
    }
    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] First VAST URL:", firstAdUrl);
    }
    try {
      if (usePreloadedAd && preloadedController) {
        if (!this.timing.ensureLoadedAdFitsBudget(preloadedController)) {
          this.timing.rejectLoadedAdForDuration(preloadedController);
          this.startContinuousFetching(baseVastUrl);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }
        const adPlayer = this.host.getAdPlayer();
        const preservedMuted = adPlayer.getOriginalMutedState();
        const preservedVolume = adPlayer.getOriginalVolume();
        adPlayer.destroy();
        this.host.video.muted = true;
        this.host.setAdPlayer(preloadedController);
        preloadedController.updateOriginalMutedState(preservedMuted, preservedVolume);
        this.attachImaEventListeners();
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] Using preloaded ad, resuming and starting playback immediately (no request delay!)");
        }
        preloadedController.resume();
        this.timing.consecutiveFailures = 0;
        this.startContinuousFetching(baseVastUrl);
        if (!this.preloadPool.active) {
          this.preloadPool.active = true;
        }
        if (!this.preloadPool.isLoopRunning) {
          this.preloadPool.preloadPoolLoop(baseVastUrl);
        }
        await preloadedController.play();
        this.timing.markAdStarted();
        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }
        const adVolume = currentMuted ? 0 : currentVolume;
        preloadedController.setAdVolume(adVolume);
      } else {
        const adPlayer = this.host.getAdPlayer();
        await this.timing.enforceGlobalRateLimit();
        this.timing.lastAdRequestTime = Date.now();
        await adPlayer.requestAds(firstAdUrl);
        sendAdLoadedTracking(
          this.host.config.licenseKey,
          { source: "hls", vastUrl: firstAdUrl, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
          this.analyticsContext
        ).catch(() => {
        });
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad request successful, starting playback");
        }
        if (!this.timing.ensureLoadedAdFitsBudget(adPlayer)) {
          this.timing.rejectLoadedAdForDuration(adPlayer);
          this.recreateAdController();
          this.startContinuousFetching(baseVastUrl);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }
        this.timing.consecutiveFailures = 0;
        this.startContinuousFetching(baseVastUrl);
        if (!this.preloadPool.active) {
          this.preloadPool.startPreloadPool(baseVastUrl, []);
        }
        await adPlayer.play();
        this.timing.markAdStarted();
        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }
        const adVolume = currentMuted ? 0 : currentVolume;
        adPlayer.setAdVolume(adVolume);
      }
    } catch (error) {
      if (this.debug) {
        console.warn("[CONTINUOUS-FETCH] First ad request failed:", error);
      }
      if (!usePreloadedAd) {
        const fallbackPreloaded = this.preloadPool.getPreloadedAd();
        if (fallbackPreloaded) {
          if (this.debug) {
            console.log("[CONTINUOUS-FETCH] First ad failed, using preloaded fallback");
          }
          try {
            if (!this.timing.ensureLoadedAdFitsBudget(fallbackPreloaded.adController)) {
              this.timing.rejectLoadedAdForDuration(fallbackPreloaded.adController);
              this.startContinuousFetching(baseVastUrl);
              await this.tryNextAvailableAdWithRateLimit();
              return;
            }
            this.swapToPreloadedAdPlayer(fallbackPreloaded.adController);
            this.timing.consecutiveFailures = 0;
            this.startContinuousFetching(baseVastUrl);
            if (!this.preloadPool.active) {
              this.preloadPool.active = true;
              this.preloadPool.preloadPoolLoop(baseVastUrl);
            }
            const ap = this.host.getAdPlayer();
            await ap.play();
            this.timing.markAdStarted();
            if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
              this.timing.scheduleAdStopAtBreakBoundary();
            }
            const adVolume = currentMuted ? 0 : currentVolume;
            ap.setAdVolume(adVolume);
            return;
          } catch (fallbackError) {
            if (this.debug) {
              console.warn("[CONTINUOUS-FETCH] Preloaded fallback also failed:", fallbackError);
            }
          }
        }
      }
      if (this.timing.isTemporaryAdError(error)) {
        this.timing.temporaryFailureUrls.set(firstAdUrl, Date.now());
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad had temporary error (no-fill) - will try alternatives");
        }
      } else {
        this.timing.failedVastUrls.add(firstAdUrl);
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad had permanent error - URL blacklisted");
        }
      }
      this.timing.consecutiveFailures++;
      this.startContinuousFetching(baseVastUrl);
      await this.tryNextAvailableAdWithRateLimit();
    }
  }
  // ───────────────────────────────────────────────────────────────
  //  Optimized pod (single multi-ad request)
  // ───────────────────────────────────────────────────────────────
  async startOptimizedPod(baseVastUrl, breakDurationMs, currentMuted, currentVolume) {
    let podUrl;
    if (this.pendingAdBreak && this.pendingAdBreak.vastUrls.length > 0) {
      podUrl = this.pendingAdBreak.vastUrls[0];
      this.clearPendingAdBreak();
    } else {
      podUrl = this.host.generatePodVastUrl(baseVastUrl, breakDurationMs);
    }
    if (!podUrl) {
      return false;
    }
    this.continuousFetchingActive = false;
    this.recreateAdController();
    const adPlayer = this.host.getAdPlayer();
    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);
    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      await adPlayer.requestAds(podUrl);
      this.timing.clearAdRequestWatchdog();
      if (this.timing.activeAdRequestToken !== requestToken) {
        return true;
      }
      const podCount = adPlayer.getPodAdCount?.() ?? 1;
      if (podCount < 1) {
        return false;
      }
      if (this.debug) {
        console.log(
          podCount === 1 ? "[POD] Response contained a single ad; playing it and enabling sequential top-up" : `[POD] Optimized pod returned ${podCount} ads; playing pod in one controller`
        );
      }
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: podUrl, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      this.timing.startAdFailsafeTimer(requestToken);
      await adPlayer.play();
      this.timing.markAdStarted();
      if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
        this.timing.scheduleAdStopAtBreakBoundary();
      }
      adPlayer.setAdVolume(currentMuted ? 0 : currentVolume);
      this.timing.consecutiveFailures = 0;
      if (podCount === 1) {
        this.continuousFetchingActive = true;
        this.startContinuousFetching(baseVastUrl);
        if (!this.preloadPool.active) {
          this.preloadPool.startPreloadPool(baseVastUrl, []);
        }
      }
      return true;
    } catch (error) {
      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      if (this.debug) {
        console.warn("[POD] Optimized pod request/play failed:", error);
      }
      if (this.timing.isTemporaryAdError(error)) {
        this.timing.temporaryFailureUrls.set(podUrl, Date.now());
      }
      this.continuousFetchingActive = true;
      this.recreateAdController();
      return false;
    }
  }
  swapToPreloadedAdPlayer(preloadedController) {
    const adPlayer = this.host.getAdPlayer();
    const preservedMuted = adPlayer.getOriginalMutedState();
    const preservedVolume = adPlayer.getOriginalVolume();
    adPlayer.destroy();
    this.host.video.muted = true;
    this.host.setAdPlayer(preloadedController);
    this.attachImaEventListeners();
    preloadedController.updateOriginalMutedState(preservedMuted, preservedVolume);
    preloadedController.resume();
  }
  // ───────────────────────────────────────────────────────────────
  //  Continuous fetching
  // ───────────────────────────────────────────────────────────────
  startContinuousFetching(baseVastUrl) {
    if (!this.continuousFetchingActive) {
      return;
    }
    if (this.continuousFetchLoopRunning) {
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Fetch loop already running, skipping duplicate start");
      }
      return;
    }
    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Starting continuous ad fetching loop");
    }
    this.continuousFetchLoopRunning = true;
    this.continuousFetchLoop(baseVastUrl);
  }
  async continuousFetchLoop(baseVastUrl) {
    const epoch = this.timing.adBreakEpoch;
    while (this.continuousFetchingActive && this.inAdBreak && epoch === this.timing.adBreakEpoch) {
      const remaining = Math.min(
        this.timing.getWallClockRemainingAdMs(),
        this.timing.getDurationBudgetRemainingMs()
      );
      if (remaining <= this.timing.getMinRemainingMsToStartAd()) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Not enough ad break time left (wall=${this.timing.getWallClockRemainingAdMs()}ms, budget=${this.timing.getDurationBudgetRemainingMs()}ms), stopping URL generation`);
        }
        break;
      }
      if (this.timing.isGamInCooldown()) {
        if (this.debug) {
          console.log("[CIRCUIT-BREAKER] GAM in cooldown, stopping URL generation");
        }
        break;
      }
      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), stopping URL generation`);
        }
        break;
      }
      if (this.timing.totalAdRequestsInBreak >= this.timing.maxTotalAdRequestsPerBreak) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Maximum ad requests reached (${this.timing.maxTotalAdRequestsPerBreak}), stopping`);
        }
        break;
      }
      const maxQueueSize = 5;
      if (this.adRequestQueue.length >= maxQueueSize) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] URL queue full (${this.adRequestQueue.length}), waiting...`);
        }
        await new Promise((resolve) => setTimeout(resolve, 1e3));
        continue;
      }
      const newAdUrl = this.host.generateVastUrls(baseVastUrl, 1)[0];
      if (!newAdUrl || this.timing.failedVastUrls.has(newAdUrl) || this.timing.isUrlInCooldown(newAdUrl)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        continue;
      }
      if (this.debug) {
        const queuedUrlsPreview = [...this.adRequestQueue, newAdUrl];
        console.log(
          `[CONTINUOUS-FETCH] Queued VAST URL (queue size: ${queuedUrlsPreview.length})`,
          { queuedUrls: queuedUrlsPreview }
        );
      }
      this.adRequestQueue.push(newAdUrl);
      const generationDelay = this.timing.consecutiveFailures > 0 ? Math.min(1e3 * Math.pow(2, this.timing.consecutiveFailures), 5e3) : 500;
      await new Promise((resolve) => setTimeout(resolve, generationDelay));
    }
    this.continuousFetchLoopRunning = false;
    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] URL generation loop ended (queued: ${this.adRequestQueue.length})`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }
  }
  stopContinuousFetching() {
    this.continuousFetchingActive = false;
    this.continuousFetchLoopRunning = false;
    this.placeholder.hide();
    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Stopping continuous ad fetching");
    }
  }
  // ───────────────────────────────────────────────────────────────
  //  Ad iteration
  // ───────────────────────────────────────────────────────────────
  async tryNextAvailableAdWithRateLimit() {
    if (!this.inAdBreak) {
      return;
    }
    const epoch = this.timing.adBreakEpoch;
    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      }
      this.handleAdPodComplete();
      return;
    }
    const backoffMultiplier = Math.pow(2, this.timing.consecutiveFailures);
    const backoffDelay = Math.min(this.timing.backoffBaseMs * backoffMultiplier, this.timing.maxBackoffMs);
    const effectiveMinInterval = this.timing.minAdRequestIntervalMs + (this.timing.consecutiveFailures > 0 ? backoffDelay : 0);
    const timeSinceLastRequest = Date.now() - this.timing.lastAdRequestTime;
    if (timeSinceLastRequest < effectiveMinInterval) {
      const waitTime = effectiveMinInterval - timeSinceLastRequest;
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Rate limiting: waiting ${waitTime}ms before next request (backoff: ${this.timing.consecutiveFailures} failures)`);
      }
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
    if (!this.inAdBreak || epoch !== this.timing.adBreakEpoch) {
      return;
    }
    return this.tryNextAvailableAd(0);
  }
  async tryNextAvailableAd(retryCount = 0) {
    if (!this.inAdBreak) {
      return;
    }
    const remaining = this.timing.getRemainingAdMs();
    if (!this.timing.hasTimeToStartAnotherAd()) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Not enough time remaining (${remaining}ms) to start another ad, ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }
    if (this.timing.isGamInCooldown()) {
      if (this.debug) {
        console.log("[CIRCUIT-BREAKER] GAM in cooldown, ending ad break gracefully");
      }
      this.handleAdPodComplete();
      return;
    }
    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }
    this.timing.cleanupTemporaryFailures();
    const preloaded = this.preloadPool.getPreloadedAd();
    if (preloaded) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Using preloaded ad from pool`);
      }
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: preloaded.vastUrl, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      try {
        if (!this.timing.ensureLoadedAdFitsBudget(preloaded.adController)) {
          this.timing.rejectLoadedAdForDuration(preloaded.adController);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }
        this.swapToPreloadedAdPlayer(preloaded.adController);
        const ap = this.host.getAdPlayer();
        await ap.play();
        this.timing.markAdStarted();
        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }
        ap.setAdVolume(getAdAudioVolume(ap));
        this.timing.consecutiveFailures = 0;
        return;
      } catch (error) {
        if (this.debug) {
          console.warn("[CONTINUOUS-FETCH] Preloaded ad failed to play:", error);
        }
      }
    }
    while (this.adRequestQueue.length > 0) {
      const nextAdUrl = this.adRequestQueue.shift();
      if (!nextAdUrl) continue;
      if (this.timing.failedVastUrls.has(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping permanently-failed URL, trying next...");
        continue;
      }
      if (this.timing.isUrlInCooldown(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping URL in cooldown (recent no-fill), trying next...");
        continue;
      }
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Requesting next ad via HLS ad player (${this.timing.currentAdIndex + 1}, ${this.adRequestQueue.length} remaining in queue)`);
      }
      try {
        await this.playSingleAd(nextAdUrl);
        this.timing.consecutiveFailures = 0;
      } catch (error) {
        const errorMessage = error.message;
        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad request failed:", errorMessage);
        if (errorMessage.includes("ad_duration_exceeds_budget")) {
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }
        if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
          this.timing.consecutiveFailures++;
        }
        await this.tryNextAvailableAdWithRateLimit();
      }
      return;
    }
    const maxRetries = 3;
    if (this.continuousFetchingActive && retryCount < maxRetries && remaining > 2e3) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Queue empty, waiting for URLs... (${retryCount + 1}/${maxRetries})`);
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      await this.tryNextAvailableAd(retryCount + 1);
      return;
    }
    if (!this.isShowingPlaceholder && remaining > 1e3) {
      this.showPlaceholderAndWaitForAds();
    } else {
      if (this.debug) console.log("[CONTINUOUS-FETCH] No more ads available, ending ad break");
      this.handleAdPodComplete();
    }
  }
  // ───────────────────────────────────────────────────────────────
  //  Single ad playback
  // ───────────────────────────────────────────────────────────────
  async playSingleAd(vastTagUrl) {
    const adPlayer = this.host.getAdPlayer();
    if (adPlayer.isAdPlaying()) return;
    if (this.timing.failedVastUrls.has(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping permanently-failed VAST URL:", vastTagUrl);
      throw new Error("VAST URL permanently failed");
    }
    if (this.timing.isUrlInCooldown(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping URL in cooldown (recent no-fill):", vastTagUrl);
      throw new Error("VAST URL in cooldown after no-fill");
    }
    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.warn("[AD-ERROR] Too many consecutive failures, not requesting more ads");
      throw new Error("Too many consecutive failures");
    }
    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.warn("[CIRCUIT-BREAKER] GAM in cooldown, skipping ad request");
      throw new Error("GAM in cooldown");
    }
    this.recreateAdController();
    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);
    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      const ap = this.host.getAdPlayer();
      await ap.requestAds(vastTagUrl);
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: vastTagUrl, timestamp: (/* @__PURE__ */ new Date()).toISOString() },
        this.analyticsContext
      ).catch(() => {
      });
      this.timing.clearAdRequestWatchdog();
      if (this.timing.activeAdRequestToken !== requestToken) return;
      if (!this.timing.ensureLoadedAdFitsBudget(ap)) {
        this.timing.rejectLoadedAdForDuration(ap);
        this.recreateAdController();
        throw new Error("ad_duration_exceeds_budget");
      }
      try {
        this.timing.startAdFailsafeTimer(requestToken);
        await ap.play();
        this.timing.markAdStarted();
        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }
        ap.setAdVolume(getAdAudioVolume(ap));
        this.timing.consecutiveFailures = 0;
        this.timing.temporaryFailureUrls.delete(vastTagUrl);
      } catch (playError) {
        if (this.debug) console.error("[AD-ERROR] Failed to play ad:", playError);
        const preloadedFallback = this.preloadPool.getPreloadedAd();
        if (preloadedFallback) {
          if (this.debug) console.log("[AD-ERROR] Play failed, trying preloaded fallback ad");
          try {
            if (!this.timing.ensureLoadedAdFitsBudget(preloadedFallback.adController)) {
              this.timing.rejectLoadedAdForDuration(preloadedFallback.adController);
              throw playError;
            }
            this.timing.clearAdFailsafeTimer();
            this.swapToPreloadedAdPlayer(preloadedFallback.adController);
            this.timing.consecutiveFailures = 0;
            const fbAp = this.host.getAdPlayer();
            await fbAp.play();
            this.timing.markAdStarted();
            if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
              this.timing.scheduleAdStopAtBreakBoundary();
            }
            fbAp.setAdVolume(getAdAudioVolume(fbAp));
            return;
          } catch (fallbackError) {
            if (this.debug) console.warn("[AD-ERROR] Preloaded fallback also failed:", fallbackError);
          }
        }
        if (this.timing.isTemporaryAdError(playError)) {
          this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
          if (this.debug) console.log("[AD-ERROR] Temporary play error - URL can be retried after cooldown");
        } else {
          this.timing.failedVastUrls.add(vastTagUrl);
          if (this.debug) console.log("[AD-ERROR] Permanent play error - URL blacklisted");
        }
        this.timing.clearAdFailsafeTimer();
        if (this.timing.activeAdRequestToken === requestToken) {
          this.timing.activeAdRequestToken = null;
        }
        throw playError;
      }
    } catch (error) {
      const errorMessage = error?.message || "";
      if (this.debug) console.error("[AD-ERROR] Ad request failed:", errorMessage);
      const preloadedFallback = this.preloadPool.getPreloadedAd();
      if (preloadedFallback) {
        if (this.debug) console.log("[AD-ERROR] Request failed, trying preloaded fallback ad");
        try {
          if (!this.timing.ensureLoadedAdFitsBudget(preloadedFallback.adController)) {
            this.timing.rejectLoadedAdForDuration(preloadedFallback.adController);
            throw error;
          }
          this.timing.clearAdRequestWatchdog();
          this.timing.clearAdFailsafeTimer();
          this.swapToPreloadedAdPlayer(preloadedFallback.adController);
          this.timing.consecutiveFailures = 0;
          const fbAp = this.host.getAdPlayer();
          await fbAp.play();
          this.timing.markAdStarted();
          fbAp.setAdVolume(getAdAudioVolume(fbAp));
          return;
        } catch (fallbackError) {
          if (this.debug) console.warn("[AD-ERROR] Preloaded fallback also failed:", fallbackError);
        }
      }
      if (this.timing.isTemporaryAdError(error)) {
        this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
        if (this.debug) console.log("[AD-ERROR] Temporary error (no-fill/timeout) - URL can be retried after cooldown");
      } else {
        this.timing.failedVastUrls.add(vastTagUrl);
        if (this.debug) console.log("[AD-ERROR] Permanent error - URL blacklisted:", errorMessage);
      }
      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      throw error;
    }
  }
  // ───────────────────────────────────────────────────────────────
  //  Placeholder wait
  // ───────────────────────────────────────────────────────────────
  async showPlaceholderAndWaitForAds() {
    const remaining = this.timing.getRemainingAdMs();
    const waitTime = Math.min(this.maxPlaceholderDurationMs, remaining);
    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.log("[CIRCUIT-BREAKER] GAM in cooldown, skipping placeholder wait");
      this.handleAdPodComplete();
      return;
    }
    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping placeholder - too many consecutive failures");
      this.handleAdPodComplete();
      return;
    }
    if (waitTime < 1e3) {
      this.handleAdPodComplete();
      return;
    }
    if (this.debug) {
      console.log(`[CONTINUOUS-FETCH] Showing placeholder for ${waitTime}ms while waiting for ad URLs`);
    }
    this.isShowingPlaceholder = true;
    this.host.getAdPlayer().showPlaceholder();
    const checkInterval = 300;
    const maxChecks = Math.floor(waitTime / checkInterval);
    for (let i = 0; i < maxChecks; i++) {
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
      if (!this.inAdBreak) return;
      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Too many failures during placeholder wait");
        break;
      }
      if (!this.timing.hasTimeToStartAnotherAd()) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Not enough time remaining during placeholder wait, ending ad break");
        break;
      }
      while (this.adRequestQueue.length > 0) {
        if (!this.timing.hasTimeToStartAnotherAd()) break;
        const nextAdUrl = this.adRequestQueue.shift();
        if (!nextAdUrl) continue;
        if (this.timing.failedVastUrls.has(nextAdUrl)) continue;
        if (this.timing.isUrlInCooldown(nextAdUrl)) continue;
        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad URL available, requesting via HLS ad player");
        this.isShowingPlaceholder = false;
        this.host.getAdPlayer().hidePlaceholder();
        try {
          await this.playSingleAd(nextAdUrl);
          this.timing.consecutiveFailures = 0;
        } catch (error) {
          const errorMessage = error.message;
          if (errorMessage.includes("ad_duration_exceeds_budget")) {
            await this.tryNextAvailableAdWithRateLimit();
            return;
          }
          if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
            this.timing.consecutiveFailures++;
          }
          await this.tryNextAvailableAdWithRateLimit();
        }
        return;
      }
    }
    if (this.debug) console.log("[CONTINUOUS-FETCH] Placeholder timeout, ending ad break");
    this.isShowingPlaceholder = false;
    this.host.getAdPlayer().hidePlaceholder();
    this.handleAdPodComplete();
  }
  // ───────────────────────────────────────────────────────────────
  //  Ad break completion
  // ───────────────────────────────────────────────────────────────
  handleAdPodComplete() {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Ad pod complete - cleaning up");
    }
    this.timing.resetForBreakEnd();
    this.stopContinuousFetching();
    this.preloadPool.stop();
    this.clearPendingAdBreak();
    if (this.isShowingPlaceholder) {
      this.host.getAdPlayer().hidePlaceholder();
      this.isShowingPlaceholder = false;
    }
    this.adRequestQueue = [];
    this.inAdBreak = false;
    this.adDetectSentForCurrentBreak = false;
    this.cueManager.activeScte35BreakKey = void 0;
    this.cueManager.scheduledScte35BreakKey = void 0;
    this.adPodQueue = [];
    this.showAds = false;
    this.host.getAdPlayer().stop().catch(() => {
    });
    const restoredMuted = this.host.getAdPlayer().getOriginalMutedState();
    const restoredVolume = this.host.getAdPlayer().getOriginalVolume();
    if (this.host.video.muted !== restoredMuted) {
      this.host.video.muted = restoredMuted;
    }
    if (Math.abs(this.host.video.volume - restoredVolume) > 0.01) {
      this.host.video.volume = restoredVolume;
    }
    if (this.host.shouldContinueLiveStreamDuringAds()) {
      const liveSyncPos = this.host.getLiveSyncPosition();
      if (liveSyncPos != null && liveSyncPos > this.host.video.currentTime) {
        if (this.debug) {
          console.log(
            `[StormcloudVideoPlayer] Live break end \u2014 snapping to live edge: ${this.host.video.currentTime.toFixed(2)}s \u2192 ${liveSyncPos.toFixed(2)}s`
          );
        }
        this.host.video.currentTime = liveSyncPos;
      }
      if (this.host.video.paused) {
        if (this.debug) console.log("[StormcloudVideoPlayer] Content video paused in live mode after ads, resuming playback");
        this.host.video.play()?.catch(() => {
        });
      } else {
        if (this.debug) console.log("[StormcloudVideoPlayer] Content video already playing in live mode after ads");
      }
    } else if (this.host.video.paused) {
      this.host.video.play()?.catch(() => {
      });
    }
  }
  handleAdFailure() {
    this.timing.consecutiveFailures++;
    this.timing.recordGamNoFill();
    const remaining = this.timing.getRemainingAdMs();
    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] Ad failure: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}, consecutiveFailures=${this.timing.consecutiveFailures}`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }
    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log(`[CONTINUOUS-FETCH] Max consecutive failures reached (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      this.handleAdPodComplete();
      return;
    }
    if (this.timing.hasTimeToStartAnotherAd()) {
      this.tryNextAvailableAdWithRateLimit();
    } else {
      if (this.debug) console.log("[AD-ERROR] Ad failed and not enough time remaining for another ad. Failed URLs:", this.timing.failedVastUrls.size);
      this.handleAdPodComplete();
    }
  }
};

// src/player/StormcloudVideoPlayer.ts
var StormcloudVideoPlayer = class {
  constructor(config) {
    this.attached = false;
    this.palPlaybackStarted = false;
    initializePolyfills();
    const browserOverrides = getBrowserConfigOverrides();
    this.config = { ...browserOverrides, ...config };
    this.video = config.videoElement;
    logBrowserInfo(config.debugAdTiming);
    this.adPlayer = this.createAdPlayer(false);
    this.palNonce = createPalNonceManager();
    this.cueManager = new Scte35CueManager(!!this.config.debugAdTiming, {
      onEarlyStartCue: (marker, fragmentSn, cueKey) => {
        this.startAdPrefetch(marker, fragmentSn, cueKey);
      },
      onPlaybackCue: (marker, cueKey) => {
        this.onScte35Marker(marker, cueKey);
      },
      isInAdBreak: () => this.adBreak.inAdBreak
    });
    this.adConfig = new AdConfigManager(this.config, this.video);
    this.timing = new AdTimingService(this.config, this.video, {
      onAdStopTimerFired: () => this.onAdStopTimerFired(),
      onAdStartTimerFired: (marker, cueKey) => {
        this.startScte35AdBreak(
          marker,
          cueKey,
          marker.durationSeconds != null ? marker.durationSeconds * 1e3 : this.timing.expectedAdBreakDurationMs
        );
      },
      onWatchdogTimeout: () => this.adBreak.handleAdFailure(),
      onFailsafeTimeout: () => this.adBreak.handleAdFailure()
    });
    this.preloadPool = new AdPreloadPool({
      debug: !!this.config.debugAdTiming,
      createAdPlayer: (c) => this.createAdPlayer(c),
      shouldContinueLiveStreamDuringAds: () => this.shouldContinueLiveStreamDuringAds(),
      timing: this.timing,
      generateVastUrlsWithCorrelators: (base, count) => this.generateVastUrls(base, count),
      isInAdBreak: () => this.adBreak.inAdBreak,
      getPendingAdBreak: () => this.adBreak.pendingAdBreak
    });
    this.placeholder = new PlaceholderLayer(this.video, !!this.config.debugAdTiming);
    this.hlsEngine = new HlsEngine(this.config, this.video, this.cueManager, this.timing, {
      onManifestParsed: async (isLive) => this.onManifestParsed(isLive),
      onScte35Cue: (marker, context) => this.cueManager.onScte35Cue(marker, context),
      activatePendingCuesForFragment: (frag) => this.cueManager.activatePendingScte35CuesForFragment(frag),
      onFatalDestroy: () => this.destroy(),
      isInAdBreak: () => this.adBreak.inAdBreak,
      hasPendingAdBreak: () => this.adBreak.pendingAdBreak !== null
    });
    this.adBreak = new AdBreakOrchestrator(
      {
        config: this.config,
        video: this.video,
        getAdPlayer: () => this.adPlayer,
        setAdPlayer: (p) => {
          this.adPlayer = p;
        },
        createAdPlayer: (c) => this.createAdPlayer(c),
        shouldContinueLiveStreamDuringAds: () => this.shouldContinueLiveStreamDuringAds(),
        getLiveSyncPosition: () => this.hlsEngine.getLiveSyncPosition(),
        generateVastUrls: (base, count) => this.generateVastUrls(base, count),
        generatePodVastUrl: (base, breakDurationMs) => this.generatePodVastUrl(base, breakDurationMs)
      },
      this.timing,
      this.preloadPool,
      this.adConfig,
      this.cueManager,
      this.placeholder
    );
  }
  get debug() {
    return !!this.config.debugAdTiming;
  }
  createAdPlayer(continueLiveStreamDuringAds) {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Creating HLS ad player");
    }
    return createHlsAdPlayer(this.video, {
      continueLiveStreamDuringAds,
      ...this.config.licenseKey ? { licenseKey: this.config.licenseKey } : {},
      ...this.hlsEngine?.hls ? { mainHlsInstance: this.hlsEngine.hls } : {}
    });
  }
  generateVastUrls(baseUrl, count) {
    return this.adConfig.generateVastUrlsWithCorrelators(
      baseUrl,
      count,
      this.adPlayer,
      this.palNonce,
      this.adBreak.inAdBreak
    );
  }
  generatePodVastUrl(baseUrl, breakDurationMs) {
    const podParams = this.adConfig.resolvePodParams(breakDurationMs);
    return this.adConfig.generatePodVastUrl(
      baseUrl,
      podParams,
      this.adPlayer,
      this.palNonce,
      this.adBreak.inAdBreak
    );
  }
  // ───────────────────────────────────────────────────────────────
  //  Lifecycle
  // ───────────────────────────────────────────────────────────────
  async load() {
    if (!this.attached) {
      this.attach();
    }
    try {
      await this.adConfig.fetchAdConfiguration();
    } catch (error) {
      if (this.debug) {
        console.warn(
          "[StormcloudVideoPlayer] Failed to fetch ad configuration:",
          error
        );
      }
    }
    this.palNonce.generateNonce({
      videoWidth: this.video.offsetWidth || this.video.clientWidth || 640,
      videoHeight: this.video.offsetHeight || this.video.clientHeight || 480,
      adWillAutoPlay: !!this.config.autoplay,
      adWillPlayMuted: true,
      playerVolume: 0,
      continuousPlayback: this.config.lowLatencyMode ?? false
    }).catch(() => {
    });
    fetchConsentSignals().then((signals) => {
      this.adConfig.consentSignals = signals;
    }).catch(() => {
    });
    this.adConfig.initializeTracking();
    if (this.hlsEngine.shouldUseNativeHls(() => this.getStreamType())) {
      await this.hlsEngine.setupNativeHls();
      const continueLiveStreamDuringAds = this.shouldContinueLiveStreamDuringAds();
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Native HLS ad behavior:", {
          isLive: this.hlsEngine.isLiveStream,
          continueLiveStreamDuringAds
        });
      }
      this.adPlayer.destroy();
      this.adPlayer = this.createAdPlayer(continueLiveStreamDuringAds);
      this.adPlayer.initialize();
      return;
    }
    this.hlsEngine.setupHls();
  }
  async onManifestParsed(isLive) {
    if (!this.adConfig.isVmapEnabled() && !isLive && this.adConfig.vmapBreaks.length === 0 && this.adConfig.apiVastTagUrl) {
      const prerollKey = "synthetic-vod-preroll";
      if (!this.adConfig.consumedVmapBreakIds.has(prerollKey)) {
        this.adConfig.vmapBreaks = [
          { id: prerollKey, startTimeMs: 0, vastTagUrl: this.adConfig.apiVastTagUrl }
        ];
        if (this.debug) {
          console.log(
            "[StormcloudVideoPlayer] Injected synthetic VOD preroll from apiVastTagUrl"
          );
        }
      }
    }
    if (this.debug) {
      const adBehavior = this.shouldContinueLiveStreamDuringAds() ? "live (main video continues muted during ads)" : "vod (main video pauses during ads)";
      console.log("[StormcloudVideoPlayer] Stream type detected:", {
        isLive,
        allowNativeHls: this.config.allowNativeHls,
        adBehavior
      });
    }
    this.adPlayer.destroy();
    this.adPlayer = this.createAdPlayer(this.shouldContinueLiveStreamDuringAds());
    this.adPlayer.initialize();
  }
  attach() {
    if (this.attached) return;
    this.attached = true;
    this.video.autoplay = !!this.config.autoplay;
    this.video.muted = !!this.config.muted;
    this.adPlayer.initialize();
    this.adPlayer.updateOriginalMutedState(this.video.muted, this.video.volume);
    this.adBreak.attachImaEventListeners();
    this.video.addEventListener("play", () => {
      if (!this.palPlaybackStarted && !this.adPlayer.isAdPlaying()) {
        this.palPlaybackStarted = true;
        this.palNonce.sendPlaybackStart();
      }
    });
    this.endedHandler = () => {
      if (this.palPlaybackStarted) {
        this.palPlaybackStarted = false;
        this.palNonce.sendPlaybackEnd();
      }
      this.onVideoEnded();
    };
    this.video.addEventListener("ended", this.endedHandler);
    this.video.addEventListener("mousedown", (e) => {
      this.palNonce.sendAdTouch(e);
    });
    this.video.addEventListener("touchstart", (e) => {
      this.palNonce.sendAdTouch(e);
    });
    this.timeUpdateHandler = () => {
      this.onTimeUpdate(this.video.currentTime);
    };
    this.video.addEventListener("timeupdate", this.timeUpdateHandler);
    this.emptiedHandler = () => {
      if (this.hlsEngine.nativeHlsMode && this.hlsEngine.videoSrcProtection && !this.adPlayer.isAdPlaying()) {
        if (this.debug) {
          console.log(
            "[StormcloudVideoPlayer] Video src was cleared, restoring:",
            this.hlsEngine.videoSrcProtection
          );
        }
        const currentTime = this.video.currentTime;
        const wasPaused = this.video.paused;
        this.video.src = this.hlsEngine.videoSrcProtection;
        this.video.currentTime = currentTime;
        if (!wasPaused) {
          this.video.play().catch(() => {
          });
        }
      }
    };
    this.video.addEventListener("emptied", this.emptiedHandler);
  }
  shouldContinueLiveStreamDuringAds() {
    if (this.hlsEngine.isLiveStream) {
      return true;
    }
    const duration = this.video.duration;
    if (!Number.isFinite(duration) || duration === Infinity) {
      return true;
    }
    return false;
  }
  // ───────────────────────────────────────────────────────────────
  //  SCTE-35 marker orchestration
  // ───────────────────────────────────────────────────────────────
  onScte35Marker(marker, cueKey) {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] SCTE-35 marker detected:", {
        type: marker.type,
        ptsSeconds: marker.ptsSeconds,
        durationSeconds: marker.durationSeconds,
        currentTime: this.video.currentTime,
        raw: marker.raw,
        hasPendingAdBreak: !!this.adBreak.pendingAdBreak
      });
    }
    if (marker.type === "start") {
      if (this.adBreak.inAdBreak) {
        if (marker.durationSeconds != null) {
          const newDurationMs = marker.durationSeconds * 1e3;
          if (this.timing.expectedAdBreakDurationMs == null || newDurationMs > this.timing.expectedAdBreakDurationMs) {
            this.timing.setAdBreakDurationBoundary(newDurationMs);
            this.timing.scheduleAdStopAtBreakBoundary();
            if (this.debug) {
              console.log(
                `[StormcloudVideoPlayer] Updated ad break duration from subsequent marker: ${newDurationMs}ms, remaining: ${this.timing.getWallClockRemainingAdMs()}ms`
              );
            }
          }
        }
        return;
      }
      if (!this.adBreak.adDetectSentForCurrentBreak) {
        this.adBreak.adDetectSentForCurrentBreak = true;
        const detectPayload = {};
        if (marker.durationSeconds != null) detectPayload.durationSeconds = marker.durationSeconds;
        if (marker.ptsSeconds != null) detectPayload.ptsSeconds = marker.ptsSeconds;
        sendAdDetectTracking(this.config.licenseKey, { source: "scte35", timestamp: (/* @__PURE__ */ new Date()).toISOString(), ...detectPayload }, buildAnalyticsContext(this.config)).catch(() => {
        });
      }
      const hasPrefetchedAds = this.adBreak.pendingAdBreak && this.adBreak.pendingAdBreak.vastUrls.length > 0;
      const durationMs = marker.durationSeconds != null ? marker.durationSeconds * 1e3 : this.adBreak.pendingAdBreak?.marker.durationSeconds != null ? this.adBreak.pendingAdBreak.marker.durationSeconds * 1e3 : void 0;
      const isManifest = isManifestBasedMarker(marker);
      const forceImmediate = this.config.immediateManifestAds ?? true;
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Ad start decision:", {
          isManifestMarker: isManifest,
          forceImmediate,
          hasPts: typeof marker.ptsSeconds === "number",
          hasPrefetchedAds
        });
      }
      if (isManifest && forceImmediate) {
        if (this.debug) {
          console.log(
            `[StormcloudVideoPlayer] Starting ad immediately (manifest-based)${hasPrefetchedAds ? " with prefetched ads" : ""}`
          );
        }
        this.timing.clearAdStartTimer();
        this.startScte35AdBreak(marker, cueKey, durationMs);
      } else if (typeof marker.ptsSeconds === "number") {
        const tol = this.config.driftToleranceMs ?? 1e3;
        const nowMs = this.video.currentTime * 1e3;
        const estCurrentPtsMs = nowMs - this.timing.ptsDriftEmaMs;
        const deltaMs = Math.floor(marker.ptsSeconds * 1e3 - estCurrentPtsMs);
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] PTS-based timing calculation:", {
            nowMs,
            estCurrentPtsMs,
            markerPtsMs: marker.ptsSeconds * 1e3,
            deltaMs,
            tolerance: tol
          });
        }
        if (deltaMs > tol) {
          if (this.debug) {
            console.log(`[StormcloudVideoPlayer] Scheduling ad start in ${deltaMs}ms`);
          }
          this.timing.expectedAdBreakDurationMs = durationMs;
          this.cueManager.scheduledScte35BreakKey = cueKey;
          this.timing.scheduleAdStartIn(deltaMs, marker, cueKey);
        } else {
          if (this.debug) {
            console.log("[StormcloudVideoPlayer] Starting ad immediately (within tolerance)");
          }
          this.timing.clearAdStartTimer();
          this.startScte35AdBreak(marker, cueKey, durationMs);
        }
      } else {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Starting ad immediately (fallback)");
        }
        this.timing.clearAdStartTimer();
        this.startScte35AdBreak(marker, cueKey, durationMs);
      }
      return;
    }
    if (marker.type === "progress" && this.adBreak.inAdBreak) {
      if (marker.durationSeconds != null) {
        this.timing.setAdBreakDurationBoundary(marker.durationSeconds * 1e3);
      }
      this.timing.scheduleAdStopAtBreakBoundary();
      if (!this.adPlayer.isAdPlaying() && this.timing.activeAdRequestToken === null && this.adBreak.adRequestQueue.length > 0 && this.timing.hasTimeToStartAnotherAd()) {
        this.adBreak.tryNextAvailableAdWithRateLimit();
      }
      return;
    }
    if (marker.type === "end") {
      if (!this.video.muted) {
        this.video.muted = true;
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Muted video on SCTE end marker");
        }
      }
      const remaining = this.timing.getRemainingAdMs();
      const adPlaying = this.adPlayer.isAdPlaying();
      const hasQueuedAds = this.adBreak.adRequestQueue.length > 0;
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] SCTE-35 end marker received:", {
          remaining,
          adPlaying,
          hasQueuedAds,
          activeAdRequest: this.timing.activeAdRequestToken !== null
        });
      }
      this.timing.scteAdBreakEndWallClockMs = Date.now();
      this.timing.clearAdStartTimer();
      this.timing.clearAdStopTimer();
      if (adPlaying) {
        this.adPlayer.stop().catch(() => {
        });
      }
      this.adBreak.handleAdPodComplete();
      return;
    }
  }
  startScte35AdBreak(marker, cueKey, durationMs) {
    this.adBreak.inAdBreak = true;
    this.cueManager.activeScte35BreakKey = cueKey ?? this.cueManager.pendingScte35CueKey;
    this.cueManager.scheduledScte35BreakKey = void 0;
    this.timing.currentAdBreakStartWallClockMs = this.timing.resolveScteBreakStartWallClockMs(marker);
    this.timing.setAdBreakDurationBoundary(durationMs);
    this.adBreak.handleAdStart(marker);
    this.timing.scheduleAdStopAtBreakBoundary();
  }
  onAdStopTimerFired() {
    if (!this.adBreak.inAdBreak) return;
    const adPlaying = this.adPlayer.isAdPlaying();
    const checkIntervalMs = Math.max(
      250,
      Math.floor(this.config.adBreakCheckIntervalMs ?? 1e3)
    );
    const maxExtensionMsConfig = this.config.maxAdBreakExtensionMs;
    const maxExtensionMs = typeof maxExtensionMsConfig === "number" && maxExtensionMsConfig > 0 ? maxExtensionMsConfig : 0;
    const endWallClockMs = this.timing.getAdBreakEndWallClockMs() ?? Date.now();
    const overrunMs = Math.max(0, Date.now() - endWallClockMs);
    const shouldExtendAdBreak = adPlaying && overrunMs < maxExtensionMs;
    if (shouldExtendAdBreak) {
      this.timing.scheduleAdStopCountdown(checkIntervalMs);
      return;
    }
    if (this.debug) {
      console.log(
        `[StormcloudVideoPlayer] Ad break boundary reached (overrun=${overrunMs}ms) - hard-stopping ads and resuming stream`
      );
    }
    if (adPlaying) {
      this.adPlayer.stop().catch(() => {
      });
    }
    this.adBreak.handleAdPodComplete();
  }
  // ───────────────────────────────────────────────────────────────
  //  Ad prefetch
  // ───────────────────────────────────────────────────────────────
  startAdPrefetch(marker, fragmentSn, cueKey) {
    if (this.adBreak.pendingAdBreak || this.adBreak.inAdBreak) return;
    const scheduled = this.adConfig.findCurrentOrNextBreak(this.video.currentTime * 1e3);
    const tags = this.adConfig.selectVastTagsForBreak(scheduled);
    let baseVastUrl;
    if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else if (this.adConfig.apiVastTagUrl) {
      baseVastUrl = this.adConfig.apiVastTagUrl;
    } else {
      if (this.debug) console.warn("[StormcloudVideoPlayer] No VAST URL available for prefetch");
      return;
    }
    this.adConfig.beginNewAdPod();
    this.adConfig.podAssignedByPrefetch = true;
    const optimizedPods = !!this.config.optimizedPods;
    const breakDurationMs = marker.durationSeconds != null ? marker.durationSeconds * 1e3 : void 0;
    const generatedUrls = optimizedPods ? [this.generatePodVastUrl(baseVastUrl, breakDurationMs)] : this.generateVastUrls(baseVastUrl, 1);
    this.adBreak.pendingAdBreak = {
      marker,
      ...fragmentSn !== void 0 ? { detectedAtFragmentSn: fragmentSn } : {},
      vastUrls: generatedUrls,
      isFetching: false,
      fetchStartTime: Date.now()
    };
    this.cueManager.pendingScte35CueKey = cueKey;
    this.adBreak.adDetectSentForCurrentBreak = true;
    const detectPayload = {};
    if (marker.durationSeconds != null) detectPayload.durationSeconds = marker.durationSeconds;
    if (marker.ptsSeconds != null) detectPayload.ptsSeconds = marker.ptsSeconds;
    if (fragmentSn !== void 0) detectPayload.detectedAtFragmentSn = fragmentSn;
    sendAdDetectTracking(this.config.licenseKey, { source: "scte35", timestamp: (/* @__PURE__ */ new Date()).toISOString(), ...detectPayload }, buildAnalyticsContext(this.config)).catch(() => {
    });
    if (this.debug) {
      console.log("[PREFETCH] Starting ad prefetch for upcoming ad break");
      console.log(
        `[PREFETCH] Pre-generated ${generatedUrls.length} ${optimizedPods ? "pod" : "VAST"} URL(s)`
      );
    }
    if (optimizedPods) {
      if (this.debug) {
        console.log("[PREFETCH] Optimized pods enabled - skipping single-ad preload pool");
      }
      return;
    }
    this.preloadPool.startPreloadPool(baseVastUrl, generatedUrls.slice(0, 1), false);
  }
  // ───────────────────────────────────────────────────────────────
  //  VMAP time-based ad breaks
  // ───────────────────────────────────────────────────────────────
  onTimeUpdate(currentTimeSec) {
    if (!this.adConfig.isVmapEnabled() || this.adConfig.vmapBreaks.length === 0) return;
    if (this.adPlayer.isAdPlaying() || this.adBreak.inAdBreak) return;
    const nowMs = currentTimeSec * 1e3;
    const breakToPlay = this.adConfig.findBreakForTime(nowMs);
    if (breakToPlay) {
      void this.handleVmapAdBreak(breakToPlay, nowMs).catch((error) => {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP ad break failed gracefully:", error);
        }
      });
    }
  }
  onVideoEnded() {
    if (!this.adConfig.isVmapEnabled() || this.adConfig.vmapBreaks.length === 0) return;
    if (this.adPlayer.isAdPlaying() || this.adBreak.inAdBreak) return;
    const durationMs = Number.isFinite(this.video.duration) ? Math.floor(this.video.duration * 1e3) : 0;
    const postroll = this.adConfig.vmapBreaks.find(
      (b) => b.startTimeMs === -1 && !this.adConfig.consumedVmapBreakIds.has(this.adConfig.getAdBreakKey(b))
    );
    if (postroll) {
      void this.handleVmapAdBreak(postroll, durationMs).catch((error) => {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP post-roll failed gracefully:", error);
        }
      });
    }
  }
  async handleVmapAdBreak(adBreak, nowMs) {
    const key = this.adConfig.getAdBreakKey(adBreak);
    if (this.adConfig.consumedVmapBreakIds.has(key)) return;
    const breakStartMs = this.adConfig.resolveBreakStartMs(adBreak);
    if (breakStartMs == null) return;
    const durationMs = adBreak.durationMs ?? 0;
    const endMs = breakStartMs + durationMs;
    const inWindow = durationMs > 0 ? nowMs >= breakStartMs && nowMs < endMs : nowMs >= breakStartMs;
    if (inWindow) {
      this.adConfig.consumedVmapBreakIds.add(key);
      const tags = this.adConfig.selectVastTagsForBreak(adBreak);
      if (!tags || tags.length === 0) return;
      const first = tags[0];
      const rest = tags.slice(1);
      this.adBreak.adPodQueue = rest;
      this.adPlayer.updateOriginalMutedState(this.video.muted, this.video.volume);
      this.adBreak.showAds = true;
      this.adBreak.inAdBreak = true;
      this.timing.currentAdBreakStartWallClockMs = Date.now();
      try {
        await this.adBreak.playSingleAd(first);
      } catch (error) {
        this.adBreak.adPodQueue = [];
        this.adBreak.inAdBreak = false;
        this.adBreak.showAds = false;
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] VMAP ad request failed:", error);
        }
      }
    }
  }
  // ───────────────────────────────────────────────────────────────
  //  Public API
  // ───────────────────────────────────────────────────────────────
  getCurrentAdIndex() {
    return this.timing.currentAdIndex;
  }
  getTotalAdsInBreak() {
    return this.timing.totalAdsInBreak;
  }
  getAdRemainingMs() {
    return this.timing.getRemainingAdMs();
  }
  isAdPlaying() {
    return this.adBreak.inAdBreak && this.adPlayer.isAdPlaying();
  }
  isShowingAds() {
    return this.adBreak.showAds;
  }
  getStreamType() {
    const url = this.config.src.toLowerCase();
    if (url.includes(".m3u8") || url.includes("/hls/") || url.includes("application/vnd.apple.mpegurl")) {
      return "hls";
    }
    return "other";
  }
  shouldShowNativeControls() {
    const streamType = this.getStreamType();
    if (streamType === "other") {
      return !(this.config.showCustomControls ?? false);
    }
    return !!(this.config.allowNativeHls && !(this.config.showCustomControls ?? false));
  }
  isLive() {
    return this.hlsEngine.isLiveStream;
  }
  get videoElement() {
    return this.video;
  }
  toggleMute() {
    toggleMute(this.video, this.adPlayer, this.debug);
  }
  toggleFullscreen() {
    return toggleFullscreen(this.video, this.debug);
  }
  isMuted() {
    return isMuted(this.video, this.adPlayer);
  }
  setMuted(muted) {
    setMuted(this.video, this.adPlayer, muted, this.debug);
  }
  setVolume(volume) {
    setVolume(this.video, this.adPlayer, volume, this.debug);
  }
  getVolume() {
    return getVolume(this.video, this.adPlayer);
  }
  isFullscreen() {
    return isFullscreen();
  }
  resize() {
    resizePlayer(this.video, this.adPlayer, this.debug);
  }
  // ───────────────────────────────────────────────────────────────
  //  Destroy
  // ───────────────────────────────────────────────────────────────
  destroy() {
    this.adBreak.stopContinuousFetching();
    this.preloadPool.stop();
    this.timing.destroy();
    this.adBreak.clearPendingAdBreak();
    this.placeholder.destroy();
    if (this.timeUpdateHandler) {
      this.video.removeEventListener("timeupdate", this.timeUpdateHandler);
      delete this.timeUpdateHandler;
    }
    if (this.endedHandler) {
      this.video.removeEventListener("ended", this.endedHandler);
      delete this.endedHandler;
    }
    if (this.emptiedHandler) {
      this.video.removeEventListener("emptied", this.emptiedHandler);
      delete this.emptiedHandler;
    }
    this.adConfig.destroyTracking();
    this.hlsEngine.destroy();
    this.adPlayer?.destroy();
    if (this.palPlaybackStarted) {
      this.palPlaybackStarted = false;
      this.palNonce.sendPlaybackEnd();
    }
    this.palNonce.reset();
    this.adBreak.adRequestQueue = [];
    this.timing.consecutiveFailures = 0;
  }
};

// src/ui/StormcloudVideoPlayer.tsx
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaVolumeDown,
  FaExpand,
  FaCompress
} from "react-icons/fa";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var CRITICAL_PROPS = [
  "src",
  "allowNativeHls",
  "isLiveStream",
  "licenseKey",
  "isVmap",
  "vmapUrl",
  "lowLatencyMode",
  "driftToleranceMs",
  "vastMode",
  "ctvAdRequest"
];
var CONTROLS_HIDE_DELAY = 3e3;
var DEFAULT_PLAYER_ASPECT_RATIO = 16 / 9;
var StormcloudVideoPlayerComponent = React.memo(
  (props) => {
    const {
      src,
      autoplay,
      muted,
      lowLatencyMode,
      allowNativeHls,
      isLiveStream,
      driftToleranceMs,
      immediateManifestAds,
      debugAdTiming,
      showCustomControls,
      hideLoadingIndicator,
      onVolumeToggle,
      onFullscreenToggle,
      onControlClick,
      onReady,
      wrapperClassName,
      wrapperStyle,
      className,
      style,
      controls,
      playsInline,
      preload,
      poster,
      children,
      licenseKey,
      vastMode,
      vastTagUrl,
      isVmap,
      vmapUrl,
      minSegmentsBeforePlay,
      ctvAdRequest,
      ...restVideoAttrs
    } = props;
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const bufferingTimeoutRef = useRef(null);
    const controlsTimerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [adStatus, setAdStatus] = React.useState({ showAds: false, currentIndex: 0, totalAds: 0, remainingSeconds: 0 });
    const [shouldShowNativeControls, setShouldShowNativeControls] = React.useState(true);
    const [isMuted2, setIsMuted] = React.useState(false);
    const [isFullscreen2, setIsFullscreen] = React.useState(false);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume2] = React.useState(1);
    const [playbackRate, setPlaybackRate] = React.useState(1);
    const [showVolumeSlider, setShowVolumeSlider] = React.useState(false);
    const [showSpeedMenu, setShowSpeedMenu] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isBuffering, setIsBuffering] = React.useState(false);
    const [showCenterPlay, setShowCenterPlay] = React.useState(false);
    const [showLicenseWarning, setShowLicenseWarning] = React.useState(false);
    const [controlsVisible, setControlsVisible] = React.useState(true);
    const [viewportWidth, setViewportWidth] = React.useState(
      typeof window !== "undefined" ? window.innerWidth : 1920
    );
    const [isPortrait, setIsPortrait] = React.useState(
      typeof window !== "undefined" ? window.innerHeight > window.innerWidth : false
    );
    const [playerAspectRatio, setPlayerAspectRatio] = React.useState(
      DEFAULT_PLAYER_ASPECT_RATIO
    );
    const getResponsiveScale = () => {
      if (viewportWidth < 480) return 0.7;
      if (viewportWidth < 768) return 0.8;
      if (viewportWidth < 1024) return 0.9;
      return 1;
    };
    const responsiveScale = getResponsiveScale();
    const resetControlsTimer = useCallback(() => {
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
      setControlsVisible(true);
      controlsTimerRef.current = window.setTimeout(() => {
        if (!showVolumeSlider && !showSpeedMenu) {
          setControlsVisible(false);
        }
      }, CONTROLS_HIDE_DELAY);
    }, [showVolumeSlider, showSpeedMenu]);
    const handleWrapperMouseMove = useCallback(() => {
      resetControlsTimer();
    }, [resetControlsTimer]);
    const handleWrapperMouseLeave = useCallback(() => {
      if (!showVolumeSlider && !showSpeedMenu) {
        setControlsVisible(false);
      }
    }, [showVolumeSlider, showSpeedMenu]);
    const formatTime = (seconds) => {
      if (!isFinite(seconds)) return "0:00:00";
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor(seconds % 3600 / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    };
    const handlePlayPause = () => {
      if (videoRef.current) {
        if (videoRef.current.paused) {
          const hasValidSource = videoRef.current.src || videoRef.current.currentSrc && videoRef.current.currentSrc !== "" || videoRef.current.readyState >= 1;
          if (hasValidSource) {
            videoRef.current.play()?.catch((error) => {
              console.error("[StormcloudVideoPlayer] Failed to play:", error);
            });
            setShowCenterPlay(false);
          } else {
            console.warn(
              "[StormcloudVideoPlayer] Cannot play: video has no valid source"
            );
          }
        } else {
          videoRef.current.pause();
          setShowCenterPlay(true);
        }
      }
      resetControlsTimer();
    };
    const handleCenterPlayClick = () => {
      if (videoRef.current && videoRef.current.paused) {
        const hasValidSource = videoRef.current.src || videoRef.current.currentSrc && videoRef.current.currentSrc !== "" || videoRef.current.readyState >= 1;
        if (hasValidSource) {
          videoRef.current.play()?.catch((error) => {
            console.error("[StormcloudVideoPlayer] Failed to play:", error);
          });
          setShowCenterPlay(false);
        } else {
          console.warn(
            "[StormcloudVideoPlayer] Cannot play: video has no valid source"
          );
        }
      }
    };
    const handleTimelineSeek = (e) => {
      if (videoRef.current && duration > 0 && isFinite(duration)) {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const progress = Math.max(0, Math.min(1, clickX / rect.width));
        const newTime = progress * duration;
        if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
          videoRef.current.currentTime = newTime;
        }
      }
      resetControlsTimer();
    };
    const handleVolumeChange = (newVolume) => {
      if (playerRef.current && isFinite(newVolume)) {
        const clampedVolume = Math.max(0, Math.min(1, newVolume));
        playerRef.current.setVolume(clampedVolume);
      }
    };
    const handlePlaybackRateChange = (rate) => {
      if (videoRef.current && isFinite(rate) && rate > 0) {
        videoRef.current.playbackRate = rate;
      }
      setShowSpeedMenu(false);
    };
    const isHlsStream = src?.toLowerCase().includes(".m3u8") || src?.toLowerCase().includes("/hls/");
    const isLiveUiMode = isLiveStream === true;
    const shouldShowEnhancedControls = showCustomControls && (isHlsStream ? !isLiveUiMode : true);
    const criticalPropsKey = useMemo(() => {
      return CRITICAL_PROPS.map((prop) => `${prop}:${props[prop]}`).join("|");
    }, [
      src,
      allowNativeHls,
      isLiveStream,
      licenseKey,
      isVmap,
      vmapUrl,
      lowLatencyMode,
      driftToleranceMs,
      vastMode
    ]);
    useEffect(() => {
      if (typeof window === "undefined") return;
      const el = videoRef.current;
      if (!el || !src) return;
      if (!licenseKey) {
        setShowLicenseWarning(true);
        setIsLoading(false);
        console.warn(
          "StormcloudVideoPlayer: License key is required but not provided. Please set the licenseKey prop to use the player."
        );
        return;
      }
      setShowLicenseWarning(false);
      if (debugAdTiming) {
        console.log("[StormcloudUI] Initializing player, isLoading=true");
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch {
        }
        playerRef.current = null;
      }
      const cfg = {
        src,
        videoElement: el
      };
      if (autoplay !== void 0) cfg.autoplay = autoplay;
      if (muted !== void 0) cfg.muted = muted;
      if (lowLatencyMode !== void 0) cfg.lowLatencyMode = lowLatencyMode;
      if (allowNativeHls !== void 0) cfg.allowNativeHls = allowNativeHls;
      if (isLiveStream !== void 0) cfg.isLiveStream = isLiveStream;
      if (driftToleranceMs !== void 0)
        cfg.driftToleranceMs = driftToleranceMs;
      if (immediateManifestAds !== void 0)
        cfg.immediateManifestAds = immediateManifestAds;
      if (debugAdTiming !== void 0) cfg.debugAdTiming = debugAdTiming;
      if (showCustomControls !== void 0)
        cfg.showCustomControls = showCustomControls;
      if (onVolumeToggle !== void 0) cfg.onVolumeToggle = onVolumeToggle;
      if (onFullscreenToggle !== void 0)
        cfg.onFullscreenToggle = onFullscreenToggle;
      if (onControlClick !== void 0) cfg.onControlClick = onControlClick;
      if (licenseKey !== void 0) cfg.licenseKey = licenseKey;
      if (vastMode !== void 0) cfg.vastMode = vastMode;
      if (vastTagUrl !== void 0) cfg.vastTagUrl = vastTagUrl;
      if (isVmap !== void 0) cfg.isVmap = isVmap;
      if (vmapUrl !== void 0) cfg.vmapUrl = vmapUrl;
      if (minSegmentsBeforePlay !== void 0)
        cfg.minSegmentsBeforePlay = minSegmentsBeforePlay;
      if (ctvAdRequest !== void 0) cfg.ctvAdRequest = ctvAdRequest;
      const player = new StormcloudVideoPlayer(cfg);
      playerRef.current = player;
      player.load().then(() => {
        const showNative = player.shouldShowNativeControls();
        setShouldShowNativeControls(showNative);
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Player loaded successfully, waiting for video ready"
          );
        }
        onReady?.(player);
      }).catch((error) => {
        console.error(
          "StormcloudVideoPlayer: Failed to load player:",
          error
        );
        setIsLoading(false);
        onReady?.(player);
      });
      return () => {
        try {
          player.destroy();
        } catch {
        }
        playerRef.current = null;
      };
    }, [criticalPropsKey]);
    useEffect(() => {
      if (!playerRef.current) return;
      try {
        if (autoplay !== void 0 && playerRef.current.videoElement) {
          playerRef.current.videoElement.autoplay = autoplay;
        }
        if (muted !== void 0 && !playerRef.current.isShowingAds()) {
          playerRef.current.setMuted(muted);
        }
      } catch (error) {
        console.warn("Failed to update player properties:", error);
      }
    }, [autoplay, muted]);
    useEffect(() => {
      if (!playerRef.current) return;
      const checkAdStatus = () => {
        if (playerRef.current) {
          const showAdsFromMethod = playerRef.current.isShowingAds();
          const showAdsFromAttribute = videoRef.current?.dataset?.stormcloudAdPlaying === "true";
          const showAds = showAdsFromMethod || showAdsFromAttribute;
          const currentIndex = playerRef.current.getCurrentAdIndex();
          const totalAds = playerRef.current.getTotalAdsInBreak();
          const remainingMs = playerRef.current.getAdRemainingMs();
          const remainingSeconds = showAds ? Math.max(0, Math.ceil(remainingMs / 1e3)) : 0;
          setAdStatus((prev) => {
            if (prev.showAds !== showAds || prev.currentIndex !== currentIndex || prev.totalAds !== totalAds || prev.remainingSeconds !== remainingSeconds) {
              if (showAds && !prev.showAds) {
                setShowCenterPlay(false);
              }
              return { showAds, currentIndex, totalAds, remainingSeconds };
            }
            return prev;
          });
        }
      };
      const interval = setInterval(checkAdStatus, 50);
      return () => clearInterval(interval);
    }, []);
    useEffect(() => {
      if (typeof window === "undefined" || !playerRef.current) return;
      const handleResize = () => {
        if (playerRef.current && videoRef.current) {
          if (typeof playerRef.current.resize === "function") {
            playerRef.current.resize();
          }
        }
        setViewportWidth(window.innerWidth);
        setIsPortrait(window.innerHeight > window.innerWidth);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    useEffect(() => {
      if (!playerRef.current || !videoRef.current) return;
      const updateStates = () => {
        if (playerRef.current && videoRef.current) {
          setIsMuted(playerRef.current.isMuted());
          setIsPlaying(!videoRef.current.paused);
          const currentTimeValue = videoRef.current.currentTime;
          setCurrentTime(isFinite(currentTimeValue) ? currentTimeValue : 0);
          const durationValue = videoRef.current.duration;
          setDuration(isFinite(durationValue) ? durationValue : 0);
          const volumeValue = playerRef.current.getVolume();
          setVolume2(
            isFinite(volumeValue) ? Math.max(0, Math.min(1, volumeValue)) : 1
          );
          const rateValue = videoRef.current.playbackRate;
          setPlaybackRate(
            isFinite(rateValue) && rateValue > 0 ? rateValue : 1
          );
        }
        setIsFullscreen(
          document.fullscreenElement === wrapperRef.current
        );
      };
      const interval = setInterval(updateStates, 200);
      const handleFullscreenChange = () => {
        setIsFullscreen(
          document.fullscreenElement === wrapperRef.current
        );
      };
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => {
        clearInterval(interval);
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
      };
    }, []);
    useEffect(() => {
      if (!videoRef.current) return;
      const handleLoadedMetadata = () => {
        const video2 = videoRef.current;
        if (video2) {
          if (video2.videoWidth > 0 && video2.videoHeight > 0) {
            setPlayerAspectRatio(video2.videoWidth / video2.videoHeight);
          }
          void video2.offsetHeight;
        }
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: loadedmetadata, readyState:",
            videoRef.current?.readyState
          );
        }
      };
      const handleLoadedData = () => {
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: loadeddata, readyState:",
            videoRef.current?.readyState
          );
        }
      };
      const handleLoadStart = () => {
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: loadstart, readyState:",
            videoRef.current?.readyState
          );
        }
      };
      const handleCanPlay = () => {
        setIsLoading(false);
        if (bufferingTimeoutRef.current) {
          clearTimeout(bufferingTimeoutRef.current);
          bufferingTimeoutRef.current = null;
        }
        setIsBuffering(false);
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: canplay, readyState:",
            videoRef.current?.readyState,
            "- clearing loading state, isLoading=false"
          );
        }
      };
      const handleCanPlayThrough = () => {
        setIsLoading(false);
        if (bufferingTimeoutRef.current) {
          clearTimeout(bufferingTimeoutRef.current);
          bufferingTimeoutRef.current = null;
        }
        setIsBuffering(false);
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: canplaythrough, readyState:",
            videoRef.current?.readyState,
            "- clearing loading state, isLoading=false"
          );
        }
      };
      const handleWaiting = () => {
        if (bufferingTimeoutRef.current) {
          clearTimeout(bufferingTimeoutRef.current);
        }
        bufferingTimeoutRef.current = window.setTimeout(() => {
          setIsBuffering(true);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video buffering detected (after 300ms delay), readyState:",
              videoRef.current?.readyState,
              "- showing spinner, isBuffering=true"
            );
          }
        }, 300);
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: waiting, readyState:",
            videoRef.current?.readyState,
            "- buffering delay started (300ms)"
          );
        }
      };
      const handlePlaying = () => {
        setIsLoading(false);
        if (bufferingTimeoutRef.current) {
          clearTimeout(bufferingTimeoutRef.current);
          bufferingTimeoutRef.current = null;
        }
        setIsBuffering(false);
        setShowCenterPlay(false);
        if (debugAdTiming) {
          console.log(
            "[StormcloudUI] Video event: playing, readyState:",
            videoRef.current?.readyState,
            "- playback started, isLoading=false, isBuffering=false"
          );
        }
      };
      const handlePause = () => {
        const isAdActive = playerRef.current?.isShowingAds() || videoRef.current?.dataset?.stormcloudAdPlaying === "true";
        if (playerRef.current && !isAdActive) {
          setShowCenterPlay(true);
        } else {
          setShowCenterPlay(false);
        }
      };
      const handleEnded = () => {
        setShowCenterPlay(true);
      };
      const video = videoRef.current;
      video.addEventListener("loadstart", handleLoadStart);
      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("loadeddata", handleLoadedData);
      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("canplaythrough", handleCanPlayThrough);
      video.addEventListener("waiting", handleWaiting);
      video.addEventListener("playing", handlePlaying);
      video.addEventListener("pause", handlePause);
      video.addEventListener("ended", handleEnded);
      if (video.paused) {
        setShowCenterPlay(true);
      }
      return () => {
        if (bufferingTimeoutRef.current) {
          clearTimeout(bufferingTimeoutRef.current);
          bufferingTimeoutRef.current = null;
        }
        video.removeEventListener("loadstart", handleLoadStart);
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("loadeddata", handleLoadedData);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("canplaythrough", handleCanPlayThrough);
        video.removeEventListener("waiting", handleWaiting);
        video.removeEventListener("playing", handlePlaying);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("ended", handleEnded);
      };
    }, [debugAdTiming]);
    useEffect(() => {
      setPlayerAspectRatio(DEFAULT_PLAYER_ASPECT_RATIO);
    }, [src]);
    useEffect(() => {
      return () => {
        if (controlsTimerRef.current) {
          clearTimeout(controlsTimerRef.current);
        }
      };
    }, []);
    const progressPercent = duration > 0 ? currentTime / duration * 100 : 0;
    const VolumeIcon = isMuted2 || volume === 0 ? FaVolumeMute : volume < 0.5 ? FaVolumeDown : FaVolumeUp;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("style", { children: `
              @keyframes sc-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              @keyframes sc-loading-glow {
                0%, 100% { opacity: 0.85; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.05); }
              }
              @keyframes sc-pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.6; }
              }
              @keyframes sc-fade-in {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .sc-wrapper:fullscreen,
              .sc-wrapper:has(*:fullscreen) {
                border-radius: 0 !important;
                box-shadow: none !important;
                width: 100vw !important;
                height: 100vh !important;
                max-width: 100vw !important;
                max-height: 100vh !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                z-index: 999999 !important;
                background: #000 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              .sc-ctrl-btn {
                background: none;
                border: none;
                color: #fff;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                padding: 8px;
                transition: background 0.15s ease, opacity 0.15s ease;
                opacity: 0.9;
              }
              .sc-ctrl-btn:hover {
                opacity: 1;
                background: rgba(255, 255, 255, 0.1);
              }
              .sc-ctrl-btn:active {
                opacity: 0.7;
              }
              .sc-controls-bar {
                transition: opacity 0.35s ease, transform 0.35s ease;
              }
              .sc-progress-track:hover .sc-progress-thumb {
                transform: translate(-50%, -50%) scale(1) !important;
              }
              .sc-loading-hidden .sc-loading-indicator {
                display: none !important;
              }
            ` }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          ref: wrapperRef,
          className: `sc-wrapper ${wrapperClassName || ""}`,
          onMouseMove: handleWrapperMouseMove,
          onMouseLeave: handleWrapperMouseLeave,
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: isFullscreen2 ? "fixed" : "relative",
            top: isFullscreen2 ? 0 : void 0,
            left: isFullscreen2 ? 0 : void 0,
            overflow: "hidden",
            width: isFullscreen2 ? "100vw" : "100%",
            height: isFullscreen2 ? "100vh" : "auto",
            minHeight: isFullscreen2 ? "100vh" : "auto",
            aspectRatio: isFullscreen2 ? void 0 : playerAspectRatio,
            maxWidth: isFullscreen2 ? "100vw" : "100%",
            maxHeight: isFullscreen2 ? "100vh" : "none",
            zIndex: isFullscreen2 ? 999999 : void 0,
            backgroundColor: "#000",
            borderRadius: isFullscreen2 ? 0 : void 0,
            boxShadow: isFullscreen2 ? "none" : void 0,
            ...wrapperStyle
          },
          children: [
            /* @__PURE__ */ jsx(
              "video",
              {
                ref: videoRef,
                className,
                style: {
                  display: "block",
                  width: "100%",
                  height: "100%",
                  maxWidth: "100%",
                  maxHeight: isFullscreen2 ? "100%" : "none",
                  objectFit: isFullscreen2 ? "cover" : "contain",
                  backgroundColor: "#000",
                  aspectRatio: isFullscreen2 ? "unset" : void 0,
                  ...style
                },
                controls: shouldShowNativeControls && controls && !showCustomControls,
                playsInline,
                preload,
                poster,
                ...restVideoAttrs,
                children
              }
            ),
            (isLoading || isBuffering) && !hideLoadingIndicator && /* @__PURE__ */ jsxs(
              "div",
              {
                className: "sc-loading-indicator",
                style: {
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 20,
                  width: `${Math.max(34, 38 * responsiveScale)}px`,
                  height: `${Math.max(34, 38 * responsiveScale)}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "sc-loading-glow 1.4s ease-in-out infinite",
                  filter: "drop-shadow(0 6px 14px rgba(0, 0, 0, 0.55))"
                },
                children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        border: "3px solid rgba(255, 255, 255, 0.25)",
                        borderTopColor: "#ff0000",
                        borderRightColor: "rgba(255, 255, 255, 0.85)",
                        animation: "sc-spin 0.8s linear infinite"
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        background: "#ff0000",
                        boxShadow: "0 0 10px rgba(255, 0, 0, 0.65)"
                      }
                    }
                  )
                ]
              }
            ),
            showLicenseWarning && /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 25,
                  background: "rgba(220, 38, 38, 0.92)",
                  backdropFilter: "blur(24px)",
                  color: "white",
                  padding: "28px 36px",
                  borderRadius: "14px",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.5)",
                  textAlign: "center",
                  maxWidth: "400px",
                  margin: "0 16px"
                },
                children: [
                  /* @__PURE__ */ jsx("div", { style: { fontSize: "18px", fontWeight: "700", marginBottom: "10px", letterSpacing: "-0.02em" }, children: "License Key Required" }),
                  /* @__PURE__ */ jsxs("div", { style: { fontSize: "13px", lineHeight: "1.6", color: "rgba(255, 255, 255, 0.85)" }, children: [
                    "Please provide a valid license key to use the Stormcloud Video Player.",
                    /* @__PURE__ */ jsx("br", {}),
                    "Contact your administrator for licensing information."
                  ] })
                ]
              }
            ),
            adStatus.showAds && /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  position: "absolute",
                  top: `${12 * responsiveScale}px`,
                  left: `${12 * responsiveScale}px`,
                  zIndex: 50,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  pointerEvents: "none",
                  animation: "sc-fade-in 0.3s ease"
                },
                children: /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      background: "rgba(234, 179, 8, 0.95)",
                      color: "#111",
                      padding: `${4 * responsiveScale}px ${12 * responsiveScale}px`,
                      borderRadius: "6px",
                      fontSize: `${11 * responsiveScale}px`,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      boxShadow: "0 2px 12px rgba(234, 179, 8, 0.35)"
                    },
                    children: `Ad ${Math.max(1, adStatus.currentIndex)}`
                  }
                )
              }
            ),
            showCenterPlay && !isLoading && !isBuffering && !showLicenseWarning && !adStatus.showAds && /* @__PURE__ */ jsx(
              "div",
              {
                onClick: handleCenterPlayClick,
                style: {
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 15,
                  cursor: "pointer",
                  background: "rgba(0, 0, 0, 0.55)",
                  backdropFilter: "blur(8px)",
                  borderRadius: "50%",
                  width: `${92 * responsiveScale}px`,
                  height: `${92 * responsiveScale}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s ease, transform 0.2s ease",
                  animation: "sc-fade-in 0.2s ease"
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.72)";
                  e.currentTarget.style.transform = "translate(-50%, -50%) scale(1.06)";
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.background = "rgba(0, 0, 0, 0.55)";
                  e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)";
                },
                title: "Play",
                children: /* @__PURE__ */ jsx(
                  FaPlay,
                  {
                    size: Math.max(24, 32 * responsiveScale),
                    color: "white",
                    style: { marginLeft: "4px" }
                  }
                )
              }
            ),
            shouldShowEnhancedControls && !showLicenseWarning ? /* @__PURE__ */ jsxs(
              "div",
              {
                className: "sc-controls-bar",
                style: {
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.35) 60%, transparent 100%)",
                  padding: `${20 * responsiveScale}px ${12 * responsiveScale}px ${8 * responsiveScale}px`,
                  zIndex: 10,
                  opacity: controlsVisible || adStatus.showAds ? 1 : 0,
                  transform: controlsVisible || adStatus.showAds ? "translateY(0)" : "translateY(6px)",
                  pointerEvents: controlsVisible || adStatus.showAds ? "auto" : "none"
                },
                children: [
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: "sc-progress-track",
                      style: {
                        width: "100%",
                        height: "3px",
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "1.5px",
                        marginBottom: `${8 * responsiveScale}px`,
                        cursor: "pointer",
                        position: "relative",
                        transition: "height 0.1s ease"
                      },
                      onClick: handleTimelineSeek,
                      onMouseEnter: (e) => {
                        e.currentTarget.style.height = "5px";
                      },
                      onMouseLeave: (e) => {
                        e.currentTarget.style.height = "3px";
                      },
                      children: [
                        /* @__PURE__ */ jsx(
                          "div",
                          {
                            style: {
                              position: "absolute",
                              top: 0,
                              left: 0,
                              bottom: 0,
                              background: adStatus.showAds ? "#f5c518" : "#ff0000",
                              borderRadius: "1.5px",
                              width: `${progressPercent}%`,
                              transition: "width 0.15s linear"
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "sc-progress-thumb",
                            style: {
                              position: "absolute",
                              top: "50%",
                              left: `${progressPercent}%`,
                              transform: "translate(-50%, -50%) scale(0)",
                              width: "13px",
                              height: "13px",
                              background: adStatus.showAds ? "#f5c518" : "#ff0000",
                              borderRadius: "50%",
                              boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
                              transition: "transform 0.1s ease, left 0.15s linear"
                            }
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        color: "white",
                        gap: `${8 * responsiveScale}px`
                      },
                      children: [
                        /* @__PURE__ */ jsxs(
                          "div",
                          {
                            style: {
                              display: "flex",
                              alignItems: "center",
                              gap: `${8 * responsiveScale}px`
                            },
                            children: [
                              /* @__PURE__ */ jsx(
                                "button",
                                {
                                  className: "sc-ctrl-btn",
                                  onClick: handlePlayPause,
                                  style: {
                                    padding: `${8 * responsiveScale}px`,
                                    borderRadius: "50%",
                                    minWidth: `${36 * responsiveScale}px`,
                                    minHeight: `${36 * responsiveScale}px`
                                  },
                                  title: isPlaying ? "Pause" : "Play",
                                  children: isPlaying ? /* @__PURE__ */ jsx(FaPause, { size: Math.max(14, 18 * responsiveScale) }) : /* @__PURE__ */ jsx(FaPlay, { size: Math.max(14, 18 * responsiveScale), style: { marginLeft: "2px" } })
                                }
                              ),
                              /* @__PURE__ */ jsxs(
                                "div",
                                {
                                  style: {
                                    display: "flex",
                                    alignItems: "center",
                                    paddingRight: `${6 * responsiveScale}px`
                                  },
                                  onMouseEnter: () => setShowVolumeSlider(true),
                                  onMouseLeave: () => setShowVolumeSlider(false),
                                  children: [
                                    /* @__PURE__ */ jsx(
                                      "button",
                                      {
                                        className: "sc-ctrl-btn",
                                        onClick: () => {
                                          if (playerRef.current) playerRef.current.toggleMute();
                                          onVolumeToggle?.();
                                          resetControlsTimer();
                                        },
                                        style: {
                                          padding: `${8 * responsiveScale}px`,
                                          borderRadius: "50%",
                                          minWidth: `${36 * responsiveScale}px`,
                                          minHeight: `${36 * responsiveScale}px`
                                        },
                                        title: isMuted2 ? "Unmute" : "Mute",
                                        children: /* @__PURE__ */ jsx(VolumeIcon, { size: Math.max(14, 18 * responsiveScale) })
                                      }
                                    ),
                                    /* @__PURE__ */ jsx(
                                      "div",
                                      {
                                        style: {
                                          width: showVolumeSlider ? `${68 * responsiveScale}px` : "0px",
                                          overflow: "hidden",
                                          transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                          display: "flex",
                                          alignItems: "center",
                                          paddingLeft: showVolumeSlider ? `${3 * responsiveScale}px` : "0",
                                          paddingRight: showVolumeSlider ? `${8 * responsiveScale}px` : "0"
                                        },
                                        children: /* @__PURE__ */ jsxs(
                                          "div",
                                          {
                                            style: {
                                              position: "relative",
                                              width: `${56 * responsiveScale}px`,
                                              height: "3px",
                                              cursor: "pointer",
                                              borderRadius: "1.5px"
                                            },
                                            onMouseDown: (e) => {
                                              e.preventDefault();
                                              const el = e.currentTarget;
                                              const move = (ev) => {
                                                const r2 = el.getBoundingClientRect();
                                                handleVolumeChange(Math.max(0, Math.min(1, (ev.clientX - r2.left) / r2.width)));
                                              };
                                              const up = () => {
                                                document.removeEventListener("mousemove", move);
                                                document.removeEventListener("mouseup", up);
                                              };
                                              document.addEventListener("mousemove", move);
                                              document.addEventListener("mouseup", up);
                                              const r = el.getBoundingClientRect();
                                              handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                                            },
                                            onClick: (e) => {
                                              e.stopPropagation();
                                              const r = e.currentTarget.getBoundingClientRect();
                                              handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                                            },
                                            children: [
                                              /* @__PURE__ */ jsx("div", { style: { position: "absolute", inset: 0, background: "rgba(255, 255, 255, 0.2)", borderRadius: "1.5px" } }),
                                              /* @__PURE__ */ jsx(
                                                "div",
                                                {
                                                  style: {
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    width: `${(isMuted2 ? 0 : volume) * 100}%`,
                                                    background: "#fff",
                                                    borderRadius: "1.5px",
                                                    transition: "width 0.1s ease-out"
                                                  }
                                                }
                                              ),
                                              /* @__PURE__ */ jsx(
                                                "div",
                                                {
                                                  style: {
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: `${(isMuted2 ? 0 : volume) * 100}%`,
                                                    transform: "translate(-50%, -50%)",
                                                    width: "12px",
                                                    height: "12px",
                                                    background: "#fff",
                                                    borderRadius: "50%",
                                                    boxShadow: "0 0 3px rgba(0, 0, 0, 0.3)",
                                                    transition: "left 0.1s ease-out"
                                                  }
                                                }
                                              )
                                            ]
                                          }
                                        )
                                      }
                                    )
                                  ]
                                }
                              ),
                              /* @__PURE__ */ jsxs(
                                "div",
                                {
                                  style: {
                                    fontSize: `${13 * responsiveScale}px`,
                                    fontFamily: "Roboto, 'Segoe UI', Arial, sans-serif",
                                    color: "rgba(255, 255, 255, 0.9)",
                                    display: viewportWidth < 480 ? "none" : "block",
                                    fontVariantNumeric: "tabular-nums",
                                    userSelect: "none",
                                    marginLeft: `${4 * responsiveScale}px`
                                  },
                                  children: [
                                    formatTime(currentTime),
                                    " ",
                                    /* @__PURE__ */ jsx("span", { style: { color: "rgba(255,255,255,0.5)" }, children: "/" }),
                                    " ",
                                    formatTime(duration)
                                  ]
                                }
                              )
                            ]
                          }
                        ),
                        /* @__PURE__ */ jsxs(
                          "div",
                          {
                            style: {
                              display: "flex",
                              alignItems: "center",
                              gap: `${8 * responsiveScale}px`
                            },
                            children: [
                              /* @__PURE__ */ jsxs("div", { style: { position: "relative", display: viewportWidth < 600 ? "none" : "block" }, children: [
                                /* @__PURE__ */ jsxs(
                                  "button",
                                  {
                                    className: "sc-ctrl-btn",
                                    onClick: () => {
                                      setShowSpeedMenu(!showSpeedMenu);
                                      resetControlsTimer();
                                    },
                                    style: {
                                      padding: `${6 * responsiveScale}px ${10 * responsiveScale}px`,
                                      borderRadius: `${6 * responsiveScale}px`,
                                      fontSize: `${13 * responsiveScale}px`,
                                      fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                                      fontWeight: "600",
                                      minHeight: `${32 * responsiveScale}px`
                                    },
                                    title: "Playback Speed",
                                    children: [
                                      playbackRate,
                                      "x"
                                    ]
                                  }
                                ),
                                showSpeedMenu && /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    style: {
                                      position: "absolute",
                                      bottom: "100%",
                                      right: 0,
                                      marginBottom: "8px",
                                      background: "rgba(0, 0, 0, 0.8)",
                                      backdropFilter: "blur(24px)",
                                      WebkitBackdropFilter: "blur(24px)",
                                      borderRadius: "10px",
                                      border: "1px solid rgba(255, 255, 255, 0.1)",
                                      overflow: "hidden",
                                      minWidth: "80px",
                                      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5)",
                                      animation: "sc-fade-in 0.15s ease"
                                    },
                                    children: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => /* @__PURE__ */ jsxs(
                                      "button",
                                      {
                                        onClick: () => handlePlaybackRateChange(speed),
                                        style: {
                                          display: "block",
                                          width: "100%",
                                          padding: `${8 * responsiveScale}px ${14 * responsiveScale}px`,
                                          background: playbackRate === speed ? "rgba(255, 255, 255, 0.12)" : "transparent",
                                          border: "none",
                                          color: playbackRate === speed ? "#fff" : "rgba(255, 255, 255, 0.7)",
                                          cursor: "pointer",
                                          fontSize: `${12 * responsiveScale}px`,
                                          fontFamily: "'SF Mono', monospace",
                                          fontWeight: playbackRate === speed ? "700" : "500",
                                          textAlign: "center",
                                          transition: "background 0.15s ease, color 0.15s ease"
                                        },
                                        onMouseEnter: (e) => {
                                          if (playbackRate !== speed) {
                                            e.target.style.background = "rgba(255, 255, 255, 0.08)";
                                            e.target.style.color = "#fff";
                                          }
                                        },
                                        onMouseLeave: (e) => {
                                          if (playbackRate !== speed) {
                                            e.target.style.background = "transparent";
                                            e.target.style.color = "rgba(255, 255, 255, 0.7)";
                                          }
                                        },
                                        children: [
                                          speed,
                                          "x"
                                        ]
                                      },
                                      speed
                                    ))
                                  }
                                )
                              ] }),
                              /* @__PURE__ */ jsx(
                                "button",
                                {
                                  className: "sc-ctrl-btn",
                                  onClick: () => {
                                    if (onFullscreenToggle) {
                                      onFullscreenToggle();
                                    } else if (wrapperRef.current) {
                                      if (!document.fullscreenElement) {
                                        wrapperRef.current.requestFullscreen().catch(() => {
                                        });
                                      } else {
                                        document.exitFullscreen().catch(() => {
                                        });
                                      }
                                    }
                                    resetControlsTimer();
                                  },
                                  style: {
                                    padding: `${8 * responsiveScale}px`,
                                    borderRadius: "50%",
                                    minWidth: `${36 * responsiveScale}px`,
                                    minHeight: `${36 * responsiveScale}px`
                                  },
                                  title: isFullscreen2 ? "Exit Fullscreen" : "Enter Fullscreen",
                                  children: isFullscreen2 ? /* @__PURE__ */ jsx(FaCompress, { size: Math.max(14, 18 * responsiveScale) }) : /* @__PURE__ */ jsx(FaExpand, { size: Math.max(14, 18 * responsiveScale) })
                                }
                              )
                            ]
                          }
                        )
                      ]
                    }
                  )
                ]
              }
            ) : showCustomControls && !showLicenseWarning && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    position: "absolute",
                    top: `${10 * responsiveScale}px`,
                    left: `${10 * responsiveScale}px`,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    zIndex: 10,
                    opacity: controlsVisible ? 1 : 0,
                    transition: "opacity 0.35s ease"
                  },
                  children: [
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        style: {
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "#ff3b30",
                          animation: "sc-pulse 1.5s ease-in-out infinite",
                          flexShrink: 0
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        style: {
                          fontSize: `${12 * responsiveScale}px`,
                          fontFamily: "'SF Pro Display', 'Segoe UI', Arial, sans-serif",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          color: "#fff",
                          textShadow: "0 1px 3px rgba(0,0,0,0.6)",
                          userSelect: "none"
                        },
                        children: "LIVE"
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "sc-controls-bar",
                  style: {
                    position: "absolute",
                    bottom: `${10 * responsiveScale}px`,
                    right: `${10 * responsiveScale}px`,
                    display: "flex",
                    flexDirection: isPortrait ? "column" : "row",
                    alignItems: isPortrait ? "flex-end" : "center",
                    gap: `${8 * responsiveScale}px`,
                    zIndex: 10,
                    opacity: controlsVisible ? 1 : 0,
                    transform: controlsVisible ? "translateY(0)" : "translateY(4px)",
                    pointerEvents: controlsVisible ? "auto" : "none"
                  },
                  children: [
                    /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          display: "flex",
                          alignItems: "center",
                          background: "rgba(0, 0, 0, 0.6)",
                          borderRadius: `${18 * responsiveScale}px`,
                          padding: "2px",
                          paddingRight: `${8 * responsiveScale}px`
                        },
                        onMouseEnter: () => setShowVolumeSlider(true),
                        onMouseLeave: () => setShowVolumeSlider(false),
                        children: [
                          /* @__PURE__ */ jsx(
                            "button",
                            {
                              className: "sc-ctrl-btn",
                              onClick: () => {
                                if (playerRef.current) playerRef.current.toggleMute();
                                onVolumeToggle?.();
                                resetControlsTimer();
                              },
                              style: {
                                padding: `${8 * responsiveScale}px`,
                                borderRadius: "50%",
                                minWidth: `${36 * responsiveScale}px`,
                                minHeight: `${36 * responsiveScale}px`
                              },
                              title: isMuted2 ? "Unmute" : "Mute",
                              children: /* @__PURE__ */ jsx(VolumeIcon, { size: Math.max(14, 18 * responsiveScale) })
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "div",
                            {
                              style: {
                                width: showVolumeSlider ? `${68 * responsiveScale}px` : "0px",
                                overflow: "hidden",
                                transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: showVolumeSlider ? `${3 * responsiveScale}px` : "0",
                                paddingRight: showVolumeSlider ? `${10 * responsiveScale}px` : "0"
                              },
                              children: /* @__PURE__ */ jsxs(
                                "div",
                                {
                                  style: {
                                    position: "relative",
                                    width: `${56 * responsiveScale}px`,
                                    height: "3px",
                                    cursor: "pointer",
                                    borderRadius: "1.5px"
                                  },
                                  onMouseDown: (e) => {
                                    e.preventDefault();
                                    const el = e.currentTarget;
                                    const move = (ev) => {
                                      const r2 = el.getBoundingClientRect();
                                      handleVolumeChange(Math.max(0, Math.min(1, (ev.clientX - r2.left) / r2.width)));
                                    };
                                    const up = () => {
                                      document.removeEventListener("mousemove", move);
                                      document.removeEventListener("mouseup", up);
                                    };
                                    document.addEventListener("mousemove", move);
                                    document.addEventListener("mouseup", up);
                                    const r = el.getBoundingClientRect();
                                    handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                                  },
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    const r = e.currentTarget.getBoundingClientRect();
                                    handleVolumeChange(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)));
                                  },
                                  children: [
                                    /* @__PURE__ */ jsx("div", { style: { position: "absolute", inset: 0, background: "rgba(255, 255, 255, 0.2)", borderRadius: "1.5px" } }),
                                    /* @__PURE__ */ jsx(
                                      "div",
                                      {
                                        style: {
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          bottom: 0,
                                          width: `${(isMuted2 ? 0 : volume) * 100}%`,
                                          background: "#fff",
                                          borderRadius: "1.5px",
                                          transition: "width 0.1s ease-out"
                                        }
                                      }
                                    ),
                                    /* @__PURE__ */ jsx(
                                      "div",
                                      {
                                        style: {
                                          position: "absolute",
                                          top: "50%",
                                          left: `${(isMuted2 ? 0 : volume) * 100}%`,
                                          transform: "translate(-50%, -50%)",
                                          width: "12px",
                                          height: "12px",
                                          background: "#fff",
                                          borderRadius: "50%",
                                          boxShadow: "0 0 3px rgba(0, 0, 0, 0.3)",
                                          transition: "left 0.1s ease-out"
                                        }
                                      }
                                    )
                                  ]
                                }
                              )
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        className: "sc-ctrl-btn",
                        onClick: () => {
                          if (onFullscreenToggle) {
                            onFullscreenToggle();
                          } else if (wrapperRef.current) {
                            if (!document.fullscreenElement) {
                              wrapperRef.current.requestFullscreen().catch(() => {
                              });
                            } else {
                              document.exitFullscreen().catch(() => {
                              });
                            }
                          }
                          resetControlsTimer();
                        },
                        style: {
                          padding: `${8 * responsiveScale}px`,
                          borderRadius: "50%",
                          minWidth: `${36 * responsiveScale}px`,
                          minHeight: `${36 * responsiveScale}px`,
                          background: "rgba(0, 0, 0, 0.6)"
                        },
                        title: isFullscreen2 ? "Exit Fullscreen" : "Enter Fullscreen",
                        children: isFullscreen2 ? /* @__PURE__ */ jsx(FaCompress, { size: Math.max(14, 18 * responsiveScale) }) : /* @__PURE__ */ jsx(FaExpand, { size: Math.max(14, 18 * responsiveScale) })
                      }
                    )
                  ]
                }
              )
            ] }),
            onControlClick && /* @__PURE__ */ jsx(
              "div",
              {
                onClick: onControlClick,
                style: {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                  cursor: "pointer"
                }
              }
            )
          ]
        }
      )
    ] });
  },
  (prevProps, nextProps) => {
    for (const prop of CRITICAL_PROPS) {
      if (prevProps[prop] !== nextProps[prop]) {
        return false;
      }
    }
    const uiProps = [
      "autoplay",
      "muted",
      "controls",
      "showCustomControls",
      "className",
      "style",
      "wrapperClassName",
      "wrapperStyle",
      "playsInline",
      "preload",
      "poster",
      "children"
    ];
    for (const prop of uiProps) {
      if (prevProps[prop] !== nextProps[prop]) {
        return false;
      }
    }
    const callbackProps = [
      "onReady",
      "onVolumeToggle",
      "onFullscreenToggle",
      "onControlClick"
    ];
    for (const prop of callbackProps) {
      if (prevProps[prop] !== nextProps[prop]) {
        return false;
      }
    }
    return true;
  }
);

// src/StormcloudPlayer.tsx
import React3, { Component as Component4, Suspense } from "react";

// src/props.ts
var noop = () => {
};
var defaultProps = {
  playing: false,
  loop: false,
  controls: true,
  volume: 1,
  muted: false,
  playbackRate: 1,
  width: "100%",
  height: "auto",
  style: {},
  progressInterval: 1e3,
  playsInline: false,
  autoplay: false,
  preload: "metadata",
  poster: "",
  className: "",
  wrapperClassName: "",
  wrapperStyle: {},
  allowNativeHls: false,
  lowLatencyMode: false,
  driftToleranceMs: 1e3,
  immediateManifestAds: true,
  debugAdTiming: false,
  showCustomControls: false,
  hideLoadingIndicator: false,
  licenseKey: "",
  isVmap: false,
  vmapUrl: "",
  adFailsafeTimeoutMs: 1e4,
  minSegmentsBeforePlay: 2,
  onStart: noop,
  onPlay: noop,
  onPause: noop,
  onBuffer: noop,
  onBufferEnd: noop,
  onEnded: noop,
  onError: noop,
  onDuration: noop,
  onSeek: noop,
  onProgress: noop,
  onVolumeToggle: noop,
  onFullscreenToggle: noop,
  onControlClick: noop
};

// src/utils.ts
import { lazy as reactLazy } from "react";
var lazy = reactLazy;
var omit = (object, keys) => {
  const result = { ...object };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
};
var isMediaStream = (url) => {
  return typeof window !== "undefined" && window.MediaStream && url instanceof window.MediaStream;
};
var supportsWebKitPresentationMode = () => {
  if (typeof window === "undefined") return false;
  const video = document.createElement("video");
  return "webkitSupportsPresentationMode" in video;
};
var randomString = () => {
  return Math.random().toString(36).substr(2, 9);
};
var parseQuery = (url) => {
  const query = {};
  const queryString = url.split("?")[1] || "";
  if (!queryString) return query;
  const manualParse = (qs) => {
    qs.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      if (key) {
        try {
          query[decodeURIComponent(key)] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
        } catch (e) {
          query[key] = value || "";
        }
      }
    });
  };
  if (typeof URLSearchParams !== "undefined") {
    try {
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        query[key] = value;
      });
      return query;
    } catch (e) {
      manualParse(queryString);
    }
  } else {
    manualParse(queryString);
  }
  return query;
};
var merge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        merge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }
  return merge(target, ...sources);
};
var isObject = (item) => {
  return item && typeof item === "object" && !Array.isArray(item);
};
var IS_BROWSER = typeof window !== "undefined" && window.document;
var IS_GLOBAL = typeof globalThis !== "undefined" && globalThis.window && globalThis.window.document;
var IS_IOS = IS_BROWSER && /iPad|iPhone|iPod/.test(navigator.userAgent);
var IS_SAFARI = IS_BROWSER && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
var SUPPORTS_HLS = () => {
  if (!IS_BROWSER) return false;
  const video = document.createElement("video");
  return Boolean(video.canPlayType("application/vnd.apple.mpegurl"));
};
var SUPPORTS_DASH = () => {
  if (!IS_BROWSER) return false;
  const video = document.createElement("video");
  return Boolean(video.canPlayType("application/dash+xml"));
};

// src/patterns.ts
var HLS_EXTENSIONS = /\.(m3u8)($|\?)/i;
var HLS_PATHS = /\/hls\//i;
var DASH_EXTENSIONS = /\.(mpd)($|\?)/i;
var VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)($|\?)/i;
var AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|aac|wma|flac|m4a)($|\?)/i;
var canPlay = {
  hls: (url) => {
    if (!url || typeof url !== "string") return false;
    return HLS_EXTENSIONS.test(url) || HLS_PATHS.test(url);
  },
  dash: (url) => {
    if (!url || typeof url !== "string") return false;
    return DASH_EXTENSIONS.test(url);
  },
  video: (url) => {
    if (!url || typeof url !== "string") return false;
    return VIDEO_EXTENSIONS.test(url);
  },
  audio: (url) => {
    if (!url || typeof url !== "string") return false;
    return AUDIO_EXTENSIONS.test(url);
  },
  file: (url) => {
    if (!url || typeof url !== "string") return false;
    return VIDEO_EXTENSIONS.test(url) || AUDIO_EXTENSIONS.test(url);
  }
};

// src/players/HlsPlayer.tsx
import { Component } from "react";
var HlsPlayer = class extends Component {
  constructor() {
    super(...arguments);
    this.player = null;
    this.mounted = false;
    this.load = async () => {
      if (!this.props.videoElement || !this.props.src) return;
      try {
        if (this.player) {
          this.player.destroy();
          this.player = null;
        }
        const config = {
          src: this.props.src,
          videoElement: this.props.videoElement
        };
        if (this.props.autoplay !== void 0)
          config.autoplay = this.props.autoplay;
        if (this.props.muted !== void 0) config.muted = this.props.muted;
        if (this.props.lowLatencyMode !== void 0)
          config.lowLatencyMode = this.props.lowLatencyMode;
        if (this.props.allowNativeHls !== void 0)
          config.allowNativeHls = this.props.allowNativeHls;
        if (this.props.isLiveStream !== void 0)
          config.isLiveStream = this.props.isLiveStream;
        if (this.props.driftToleranceMs !== void 0)
          config.driftToleranceMs = this.props.driftToleranceMs;
        if (this.props.immediateManifestAds !== void 0)
          config.immediateManifestAds = this.props.immediateManifestAds;
        if (this.props.debugAdTiming !== void 0)
          config.debugAdTiming = this.props.debugAdTiming;
        if (this.props.showCustomControls !== void 0)
          config.showCustomControls = this.props.showCustomControls;
        if (this.props.onVolumeToggle !== void 0)
          config.onVolumeToggle = this.props.onVolumeToggle;
        if (this.props.onFullscreenToggle !== void 0)
          config.onFullscreenToggle = this.props.onFullscreenToggle;
        if (this.props.onControlClick !== void 0)
          config.onControlClick = this.props.onControlClick;
        if (this.props.licenseKey !== void 0)
          config.licenseKey = this.props.licenseKey;
        if (this.props.adFailsafeTimeoutMs !== void 0)
          config.adFailsafeTimeoutMs = this.props.adFailsafeTimeoutMs;
        if (this.props.minSegmentsBeforePlay !== void 0)
          config.minSegmentsBeforePlay = this.props.minSegmentsBeforePlay;
        if (this.props.ctvAdRequest !== void 0)
          config.ctvAdRequest = this.props.ctvAdRequest;
        this.player = new StormcloudVideoPlayer(config);
        this.props.onMount?.(this);
        await this.player.load();
        if (this.mounted) {
          this.props.onReady?.();
        }
      } catch (error) {
        if (this.mounted) {
          this.props.onError?.(error);
        }
      }
    };
    this.play = () => {
      if (this.props.videoElement) {
        const video = this.props.videoElement;
        const hasValidSource = video.src || video.currentSrc && video.currentSrc !== "" || video.readyState >= 1;
        if (hasValidSource) {
          video.play()?.catch((error) => {
            console.error("[HlsPlayer] Failed to play:", error);
            this.props.onError?.(error);
          });
          this.props.onPlay?.();
        } else {
          console.warn("[HlsPlayer] Cannot play: video has no valid source");
        }
      }
    };
    this.pause = () => {
      if (this.props.videoElement) {
        this.props.videoElement.pause();
        this.props.onPause?.();
      }
    };
    this.stop = () => {
      this.pause();
      if (this.props.videoElement) {
        this.props.videoElement.currentTime = 0;
      }
    };
    this.seekTo = (seconds, keepPlaying) => {
      if (this.props.videoElement) {
        this.props.videoElement.currentTime = seconds;
        if (!keepPlaying) {
          this.pause();
        }
      }
    };
    this.setVolume = (volume) => {
      if (this.props.videoElement) {
        this.props.videoElement.volume = Math.max(0, Math.min(1, volume));
      }
    };
    this.mute = () => {
      if (this.props.videoElement) {
        this.props.videoElement.muted = true;
      }
    };
    this.unmute = () => {
      if (this.props.videoElement) {
        this.props.videoElement.muted = false;
      }
    };
    this.setPlaybackRate = (rate) => {
      if (this.props.videoElement && rate > 0) {
        this.props.videoElement.playbackRate = rate;
      }
    };
    this.getDuration = () => {
      if (this.props.videoElement && isFinite(this.props.videoElement.duration)) {
        return this.props.videoElement.duration;
      }
      return null;
    };
    this.getCurrentTime = () => {
      if (this.props.videoElement && isFinite(this.props.videoElement.currentTime)) {
        return this.props.videoElement.currentTime;
      }
      return null;
    };
    this.getSecondsLoaded = () => {
      if (this.props.videoElement && this.props.videoElement.buffered.length > 0) {
        return this.props.videoElement.buffered.end(
          this.props.videoElement.buffered.length - 1
        );
      }
      return null;
    };
    this.getInternalPlayer = (key = "player") => {
      if (key === "player") return this.player;
      if (key === "video") return this.props.videoElement;
      if (key === "hls" && this.player) return this.player.hls;
      return null;
    };
  }
  componentDidMount() {
    this.mounted = true;
    this.load();
  }
  componentWillUnmount() {
    this.mounted = false;
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.load();
    }
  }
  render() {
    return null;
  }
};
HlsPlayer.displayName = "HlsPlayer";
HlsPlayer.canPlay = canPlay.hls;

// src/players/FilePlayer.tsx
import { Component as Component2 } from "react";
var FilePlayer = class extends Component2 {
  constructor() {
    super(...arguments);
    this.mounted = false;
    this.ready = false;
    this.load = () => {
      if (!this.props.videoElement || !this.props.src) return;
      const video = this.props.videoElement;
      const handleLoadedMetadata = () => {
        if (this.mounted && !this.ready) {
          this.ready = true;
          this.props.onReady?.();
        }
      };
      const handlePlay = () => {
        if (this.mounted) {
          this.props.onPlay?.();
        }
      };
      const handlePause = () => {
        if (this.mounted) {
          this.props.onPause?.();
        }
      };
      const handleEnded = () => {
        if (this.mounted) {
          this.props.onEnded?.();
        }
      };
      const handleError = (error) => {
        if (this.mounted) {
          this.props.onError?.(error);
        }
      };
      const handleLoadedData = () => {
        if (this.mounted) {
          this.props.onLoaded?.();
        }
      };
      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("ended", handleEnded);
      video.addEventListener("error", handleError);
      video.addEventListener("loadeddata", handleLoadedData);
      video.src = this.props.src;
      if (this.props.autoplay !== void 0) video.autoplay = this.props.autoplay;
      if (this.props.muted !== void 0) video.muted = this.props.muted;
      if (this.props.loop !== void 0) video.loop = this.props.loop;
      if (this.props.controls !== void 0) video.controls = this.props.controls;
      if (this.props.playsInline !== void 0)
        video.playsInline = this.props.playsInline;
      if (this.props.preload !== void 0)
        video.preload = this.props.preload;
      if (this.props.poster !== void 0) video.poster = this.props.poster;
      this.props.onMount?.(this);
      return () => {
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("ended", handleEnded);
        video.removeEventListener("error", handleError);
        video.removeEventListener("loadeddata", handleLoadedData);
      };
    };
    this.play = () => {
      if (this.props.videoElement) {
        const video = this.props.videoElement;
        const hasValidSource = video.src || video.currentSrc && video.currentSrc !== "" || video.readyState >= 1;
        if (hasValidSource) {
          video.play()?.catch((error) => {
            console.error("[FilePlayer] Failed to play:", error);
            this.props.onError?.(error);
          });
        } else {
          console.warn("[FilePlayer] Cannot play: video has no valid source");
        }
      }
    };
    this.pause = () => {
      if (this.props.videoElement) {
        this.props.videoElement.pause();
      }
    };
    this.stop = () => {
      this.pause();
      if (this.props.videoElement) {
        this.props.videoElement.currentTime = 0;
      }
    };
    this.seekTo = (seconds, keepPlaying) => {
      if (this.props.videoElement) {
        this.props.videoElement.currentTime = seconds;
        if (!keepPlaying) {
          this.pause();
        }
      }
    };
    this.setVolume = (volume) => {
      if (this.props.videoElement) {
        this.props.videoElement.volume = Math.max(0, Math.min(1, volume));
      }
    };
    this.mute = () => {
      if (this.props.videoElement) {
        this.props.videoElement.muted = true;
      }
    };
    this.unmute = () => {
      if (this.props.videoElement) {
        this.props.videoElement.muted = false;
      }
    };
    this.setPlaybackRate = (rate) => {
      if (this.props.videoElement && rate > 0) {
        this.props.videoElement.playbackRate = rate;
      }
    };
    this.setLoop = (loop) => {
      if (this.props.videoElement) {
        this.props.videoElement.loop = loop;
      }
    };
    this.getDuration = () => {
      if (this.props.videoElement && isFinite(this.props.videoElement.duration)) {
        return this.props.videoElement.duration;
      }
      return null;
    };
    this.getCurrentTime = () => {
      if (this.props.videoElement && isFinite(this.props.videoElement.currentTime)) {
        return this.props.videoElement.currentTime;
      }
      return null;
    };
    this.getSecondsLoaded = () => {
      if (this.props.videoElement && this.props.videoElement.buffered.length > 0) {
        return this.props.videoElement.buffered.end(
          this.props.videoElement.buffered.length - 1
        );
      }
      return null;
    };
    this.getInternalPlayer = (key = "player") => {
      if (key === "video") return this.props.videoElement;
      return null;
    };
    this.enablePIP = async () => {
      if (this.props.videoElement && "requestPictureInPicture" in this.props.videoElement) {
        try {
          await this.props.videoElement.requestPictureInPicture();
        } catch (error) {
          console.warn("Picture-in-Picture failed:", error);
        }
      }
    };
    this.disablePIP = async () => {
      if (document.pictureInPictureElement) {
        try {
          await document.exitPictureInPicture();
        } catch (error) {
          console.warn("Exit Picture-in-Picture failed:", error);
        }
      }
    };
  }
  componentDidMount() {
    this.mounted = true;
    this.load();
  }
  componentWillUnmount() {
    this.mounted = false;
  }
  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      this.load();
    }
  }
  render() {
    return null;
  }
};
FilePlayer.displayName = "FilePlayer";
FilePlayer.canPlay = canPlay.file;

// src/players/index.ts
var players = [
  {
    key: "hls",
    name: "HLS Player",
    canPlay: canPlay.hls,
    lazyPlayer: lazy(() => Promise.resolve({ default: HlsPlayer }))
  },
  {
    key: "file",
    name: "File Player",
    canPlay: canPlay.file,
    canEnablePIP: (url) => {
      return canPlay.file(url) && (document.pictureInPictureEnabled || typeof document.webkitSupportsPresentationMode === "function");
    },
    lazyPlayer: lazy(() => Promise.resolve({ default: FilePlayer }))
  }
];
var players_default = players;

// src/Player.tsx
import React2, { Component as Component3 } from "react";
var SEEK_ON_PLAY_EXPIRY = 5e3;
var Player = class extends Component3 {
  constructor() {
    super(...arguments);
    this.mounted = false;
    this.isReady = false;
    this.isPlaying = false;
    this.isLoading = true;
    this.loadOnReady = null;
    this.startOnPlay = true;
    this.seekOnPlay = null;
    this.onDurationCalled = false;
    this.handlePlayerMount = (player) => {
      if (this.player) {
        this.progress();
        return;
      }
      this.player = player;
      this.player.load(this.props.src);
      this.progress();
    };
    this.getInternalPlayer = (key) => {
      if (!this.player) return null;
      return this.player.getInternalPlayer(key);
    };
    this.progress = () => {
      if (this.props.src && this.player && this.isReady) {
        const playedSeconds = this.getCurrentTime() || 0;
        const loadedSeconds = this.getSecondsLoaded();
        const duration = this.getDuration();
        if (duration) {
          const progress = {
            playedSeconds,
            played: playedSeconds / duration,
            loaded: 0,
            loadedSeconds: 0
          };
          if (loadedSeconds !== null) {
            progress.loadedSeconds = loadedSeconds;
            progress.loaded = loadedSeconds / duration;
          }
          if (progress.playedSeconds !== this.prevPlayed || progress.loadedSeconds !== this.prevLoaded) {
            this.props.onProgress?.(progress);
          }
          this.prevPlayed = progress.playedSeconds;
          this.prevLoaded = progress.loadedSeconds;
        }
      }
      this.progressTimeout = window.setTimeout(
        this.progress,
        this.props.progressInterval || 1e3
      );
    };
    this.handleReady = () => {
      if (!this.mounted) return;
      this.isReady = true;
      this.isLoading = false;
      const { onReady, playing, volume, muted } = this.props;
      onReady();
      if (!muted && volume !== null) {
        this.player.setVolume(volume);
      }
      if (this.loadOnReady) {
        this.player.load(this.loadOnReady, true);
        this.loadOnReady = null;
      } else if (playing) {
        this.player.play();
      }
      this.handleDurationCheck();
    };
    this.handlePlay = () => {
      this.isPlaying = true;
      this.isLoading = false;
      const { onStart, onPlay, playbackRate } = this.props;
      if (this.startOnPlay) {
        if (this.player.setPlaybackRate && playbackRate !== 1) {
          this.player.setPlaybackRate(playbackRate);
        }
        onStart?.();
        this.startOnPlay = false;
      }
      onPlay?.();
      if (this.seekOnPlay) {
        this.seekTo(this.seekOnPlay);
        this.seekOnPlay = null;
      }
      this.handleDurationCheck();
    };
    this.handlePause = (e) => {
      this.isPlaying = false;
      if (!this.isLoading) {
        this.props.onPause?.(e);
      }
    };
    this.handleEnded = () => {
      const { activePlayer, loop, onEnded } = this.props;
      if (activePlayer.loopOnEnded && loop) {
        this.seekTo(0);
      }
      if (!loop) {
        this.isPlaying = false;
        onEnded?.();
      }
    };
    this.handleError = (...args) => {
      this.isLoading = false;
      this.props.onError?.(args[0], args[1], args[2], args[3]);
    };
    this.handleDurationCheck = () => {
      clearTimeout(this.durationCheckTimeout);
      const duration = this.getDuration();
      if (duration) {
        if (!this.onDurationCalled) {
          this.props.onDuration?.(duration);
          this.onDurationCalled = true;
        }
      } else {
        this.durationCheckTimeout = window.setTimeout(
          this.handleDurationCheck,
          100
        );
      }
    };
    this.handleLoaded = () => {
      this.isLoading = false;
    };
  }
  componentDidMount() {
    this.mounted = true;
  }
  componentWillUnmount() {
    clearTimeout(this.progressTimeout);
    clearTimeout(this.durationCheckTimeout);
    this.mounted = false;
  }
  componentDidUpdate(prevProps) {
    if (!this.player) return;
    const { src, playing, volume, muted, playbackRate, loop, activePlayer } = this.props;
    if (prevProps.src !== src) {
      if (this.isLoading && !activePlayer.forceLoad && !isMediaStream(src)) {
        console.warn(
          `StormcloudPlayer: the attempt to load ${src} is being deferred until the player has loaded`
        );
        this.loadOnReady = src || null;
        return;
      }
      this.isLoading = true;
      this.startOnPlay = true;
      this.onDurationCalled = false;
      this.player.load(src, this.isReady);
    }
    if (!prevProps.playing && playing && !this.isPlaying) {
      this.player.play();
    }
    if (prevProps.playing && !playing && this.isPlaying) {
      this.player.pause();
    }
    if (prevProps.volume !== volume && volume !== null) {
      this.player.setVolume(volume);
    }
    if (prevProps.muted !== muted) {
      if (muted) {
        this.player.mute();
      } else {
        this.player.unmute();
        if (volume !== null) {
          setTimeout(() => this.player.setVolume(volume));
        }
      }
    }
    if (prevProps.playbackRate !== playbackRate && this.player.setPlaybackRate) {
      this.player.setPlaybackRate(playbackRate);
    }
    if (prevProps.loop !== loop && this.player.setLoop) {
      this.player.setLoop(loop);
    }
  }
  getDuration() {
    if (!this.isReady) return null;
    return this.player.getDuration();
  }
  getCurrentTime() {
    if (!this.isReady) return null;
    return this.player.getCurrentTime();
  }
  getSecondsLoaded() {
    if (!this.isReady) return null;
    return this.player.getSecondsLoaded();
  }
  seekTo(amount, type, keepPlaying) {
    if (!this.isReady) {
      if (amount !== 0) {
        this.seekOnPlay = amount;
        setTimeout(() => {
          this.seekOnPlay = null;
        }, SEEK_ON_PLAY_EXPIRY);
      }
      return;
    }
    const isFraction = !type ? amount > 0 && amount < 1 : type === "fraction";
    if (isFraction) {
      const duration = this.player.getDuration();
      if (!duration) {
        console.warn(
          "StormcloudPlayer: could not seek using fraction \u2013 duration not yet available"
        );
        return;
      }
      this.player.seekTo(duration * amount, keepPlaying);
      return;
    }
    this.player.seekTo(amount, keepPlaying);
  }
  render() {
    const Player2 = this.props.activePlayer;
    if (!Player2) {
      return null;
    }
    return React2.createElement(Player2, {
      ...this.props,
      onMount: this.handlePlayerMount,
      onReady: this.handleReady,
      onPlay: this.handlePlay,
      onPause: this.handlePause,
      onEnded: this.handleEnded,
      onLoaded: this.handleLoaded,
      onError: this.handleError
    });
  }
};
Player.displayName = "Player";
Player.defaultProps = defaultProps;

// src/StormcloudPlayer.tsx
var IS_BROWSER2 = typeof window !== "undefined" && window.document;
var IS_GLOBAL2 = typeof globalThis !== "undefined" && globalThis.window && globalThis.window.document;
var UniversalSuspense = IS_BROWSER2 || IS_GLOBAL2 ? Suspense : () => null;
var SUPPORTED_PROPS = [
  "src",
  "playing",
  "loop",
  "controls",
  "volume",
  "muted",
  "playbackRate",
  "width",
  "height",
  "style",
  "progressInterval",
  "playsInline",
  "autoplay",
  "preload",
  "poster",
  "className",
  "wrapperClassName",
  "wrapperStyle",
  "allowNativeHls",
  "lowLatencyMode",
  "isLiveStream",
  "driftToleranceMs",
  "immediateManifestAds",
  "debugAdTiming",
  "showCustomControls",
  "licenseKey",
  "isVmap",
  "vmapUrl",
  "adFailsafeTimeoutMs",
  "minSegmentsBeforePlay",
  "ctvAdRequest",
  "onReady",
  "onStart",
  "onPlay",
  "onPause",
  "onBuffer",
  "onBufferEnd",
  "onEnded",
  "onError",
  "onDuration",
  "onSeek",
  "onProgress",
  "onVolumeToggle",
  "onFullscreenToggle",
  "onControlClick"
];
var customPlayers = [];
var createStormcloudPlayer = (playerList, fallback) => {
  var _a;
  return _a = class extends Component4 {
    constructor() {
      super(...arguments);
      this.state = {
        showPreview: false
      };
      this.references = {
        wrapper: (wrapper) => {
          this.wrapper = wrapper;
        },
        player: (player) => {
          this.player = player;
        }
      };
      this.getActivePlayer = (src) => {
        if (!src) return null;
        for (const player of [...customPlayers, ...playerList]) {
          if (player.canPlay(src)) {
            return player;
          }
        }
        if (fallback) {
          return fallback;
        }
        return null;
      };
      this.getAttributes = (src) => {
        return omit(this.props, SUPPORTED_PROPS);
      };
      this.handleReady = () => {
        this.props.onReady?.(this);
      };
      this.seekTo = (fraction, type, keepPlaying) => {
        if (!this.player) return null;
        this.player.seekTo(fraction, type, keepPlaying);
      };
      this.getCurrentTime = () => {
        if (!this.player) return null;
        return this.player.getCurrentTime();
      };
      this.getSecondsLoaded = () => {
        if (!this.player) return null;
        return this.player.getSecondsLoaded();
      };
      this.getDuration = () => {
        if (!this.player) return null;
        return this.player.getDuration();
      };
      this.getInternalPlayer = (key = "player") => {
        if (!this.player) return null;
        return this.player.getInternalPlayer(key);
      };
      this.renderActivePlayer = (src) => {
        if (!src) return null;
        const activePlayer = this.getActivePlayer(src);
        if (!activePlayer) return null;
        return React3.createElement(Player, {
          ...this.props,
          key: activePlayer.key,
          ref: this.references.player,
          activePlayer: activePlayer.lazyPlayer || activePlayer,
          onReady: this.handleReady
        });
      };
    }
    render() {
      const {
        src,
        style,
        width,
        height,
        fallback: fallbackElement,
        wrapper: Wrapper
      } = this.props;
      const attributes = this.getAttributes(src);
      const wrapperRef = typeof Wrapper === "string" ? this.references.wrapper : void 0;
      return React3.createElement(
        Wrapper,
        {
          ref: wrapperRef,
          style: { ...style, width, height },
          ...attributes
        },
        React3.createElement(
          UniversalSuspense,
          { fallback: fallbackElement },
          this.renderActivePlayer(src)
        )
      );
    }
  }, _a.displayName = "StormcloudPlayer", _a.defaultProps = {
    ...defaultProps,
    fallback: null,
    wrapper: "div"
  }, _a.addCustomPlayer = (player) => {
    customPlayers.push(player);
  }, _a.removeCustomPlayers = () => {
    customPlayers.length = 0;
  }, _a.canPlay = (src) => {
    for (const Player2 of [...customPlayers, ...playerList]) {
      if (Player2.canPlay(src)) {
        return true;
      }
    }
    return false;
  }, _a.canEnablePIP = (src) => {
    for (const Player2 of [...customPlayers, ...playerList]) {
      if (Player2.canEnablePIP && Player2.canEnablePIP(src)) {
        return true;
      }
    }
    return false;
  }, _a;
};
var StormcloudPlayer = createStormcloudPlayer(
  players_default,
  players_default[players_default.length - 1]
);
var StormcloudPlayer_default = StormcloudPlayer;
export {
  DEFAULT_MQTT_CONFIG,
  IS_BROWSER,
  IS_GLOBAL,
  IS_IOS,
  IS_SAFARI,
  MQTT_CA_CERT_FILE,
  SUPPORTS_DASH,
  SUPPORTS_HLS,
  StormcloudPlayer_default as StormcloudPlayer,
  StormcloudVideoPlayer,
  StormcloudVideoPlayerComponent,
  applyMQTTConfig,
  buildAnalyticsContext,
  buildMQTTBrokerUrl,
  buildPlayerTopic,
  canPlay,
  configureMQTT,
  createHlsAdPlayer,
  createPalNonceManager,
  createStormcloudPlayer,
  StormcloudVideoPlayerComponent as default,
  detectBrowser,
  disconnectMQTT,
  ensureMQTTClient,
  getBrowserConfigOverrides,
  getBrowserID,
  getClientInfo,
  getMQTTStatus,
  initMQTTClient,
  initializePolyfills,
  isMQTTConfigured,
  isMQTTConnected,
  isMQTTEnabled,
  isMediaStream,
  lazy,
  logBrowserInfo,
  merge,
  mqttConfig,
  omit,
  parseQuery,
  players_default as players,
  publishMQTT,
  randomString,
  sendAdDetectTracking,
  sendAdImpressionTracking,
  sendAdLoadedTracking,
  sendHeartbeat,
  sendInitialTracking,
  supportsFeature,
  supportsModernJS,
  supportsWebKitPresentationMode
};
